import { useState, useMemo, useEffect, useRef, useCallback, Fragment } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  useGetBookingDetail,
  useGetBookingActivity,
  useConfirmBooking,
  useAddBookingProducts,
  useUpdateBookingProduct,
  useDeleteBookingProduct,
  useDeleteBookingAssignment,
  useCreateBookingAssignments,
  useUpdateBookingAssignment,
  useAddBookingTravelers,
  useDeleteBookingTraveler,
  useEditBookingTraveler,
  useCreateTravelerModification,
  useAddBookingSSRs,
  useDeleteBookingSSR,
  useUpdateSSRStatus,
  useUpdateSSRDetails,
  useSetFlightPricing,
  useSetHotelPricing,
  useSetTransferPricing,
  useSetGenericPricing,
  useSetSSRPricing,
  useSetAdjustments,
  useCalculateVAT,
  useGetProductModifications,
  useDeleteProductModification,
  useDeleteTravelerModification,
  mapUiProductToApi,
  isSupportedProductType,
  useSyncCustomerSnapshot,
  useChangeBookingCustomer,
  useGetAssignmentLockStatus,
  useSubmitBooking,
  useGetCancellationCases,
} from "../../../api/booking/useBookingApi";
import { useGetSSRCategories, useGetSSRItems } from "../../../api/products/useSSRApi";
import { SSR_CATEGORIES } from "../../../components/Booking/StepSSR";
import { SSR_STATUS_VALUES, ssrStatusColor } from "../../../constants/documentConstants";
import { useUpdatePrivateCustomer, useTravelersList } from "../../../api/people/usePeopleApi";
import { useUpdateProfessionalCustomer } from "../../../api/people/usePeopleApi";
import { classifyTraveler, generateAdjustmentId } from "../../../components/Booking/bookingConstants";
import BookingDetailPricing from "../../../components/Booking/BookingDetailPricing";
import FlightRoute from "../../../components/Booking/FlightRoute";
import { PriceBreakdownAfterCancellation } from "../../../components/Booking/CancellationPricing";
import AddPrivateCustomerModal from "../../../components/Customers/AddPrivateCustomerModal";
import AddProfessionalCustomerModal from "../../../components/Customers/AddProfessionalCustomerModal";
import {
  ArrowLeft,
  ArrowRight,
  FolderOpen,
  Map as MapIcon,
  User,
  Euro,
  CheckCircle,
  CreditCard,
  FileText,
  ClipboardList,
  StickyNote,
  Link2,
  Plane,
  Hotel,
  Bus,
  Shield,
  Package,
  Car,
  Ship,
  Check,
  Copy,
  Send,
  Download,
  Ban,
  FilePlus,
  Mail,
  Plus,
  Pencil,
  Trash2,
  Lock,
  UserCircle,
  Phone,
  Tag,
  Percent,
  X,
  MoreVertical,
  Eye,
  UserX,
  UserPlus,
  TrendingUp,
  TrendingDown,
  Save,
  ChevronDown,
  ChevronRight,
  Clock,
  Globe,
  Monitor,
  Info,
  AlertTriangle,
  Loader2,
  Search,
  RefreshCw,
  DollarSign,
  ShoppingCart,
  Landmark,
  AlertCircle,
  Calendar,
  Users,
  Briefcase,
  ClipboardCheck,
  Sparkles,
  Wrench,
  ChevronsDown,
  ChevronsUp,
  XCircle,
} from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/selectors/authSelectors";
import { useToast } from "../../../context/ToastContext";
import AddComponentModal from "../../../components/AddComponentModal/AddComponentModal";
import ProductFormScreen from "../../../components/Booking/ProductFormScreen";
import BaseModal from "../../../components/BaseComponents/BaseModal/BaseModal";
import ItineraryTab from "./ItineraryTab";
import useScrollTabAdvance from "./useScrollTabAdvance";
import PaymentsTab from "./PaymentsTab";
import DocumentsTab from "./DocumentsTab";
import EmailsTab from "./emails/EmailsTab";
import NotesTab from "./NotesTab";
import CustomerRequestsPanel from "./CustomerRequestsPanel";
import UrgentBanner from "./UrgentBanner";
import { useGenerateVouchers } from "../../../api/document/useDocumentsApi";
import { useGetBookingPayments } from "../../../api/finance/usePaymentsApi";
import LoadingButton from "../../../components/BaseComponents/LoadingButton/LoadingButton";
import styles from "./BookingDetail.module.css";

/* ── Map raw API product back to flat form format for ProductFormScreen ── */
const mapApiProductToForm = (rawProduct) => {
  if (!rawProduct) return null;
  let pd = rawProduct.productDetails || {};
  if (typeof pd === "string") try { pd = JSON.parse(pd); } catch { pd = {}; }
  const d = pd;
  const today = new Date().toISOString().slice(0, 10);
  const base = {
    _serverId: rawProduct.id,
    type: rawProduct.productType,
    status: rawProduct.productStatus || "Pending",
    supplierId: rawProduct.supplierId || null,
    supplier: rawProduct.supplierName || (rawProduct.supplierId ? `Supplier #${rawProduct.supplierId}` : ""),
    supplierRef: rawProduct.supplierReference || "",
    supplierCommercialRole: rawProduct.supplierCommercialRole || "MERCHANT",
    creationDate: rawProduct.creationDate || today,
    serviceDate: rawProduct.serviceDate || d.serviceDate || "",
    serviceDateEnd: rawProduct.serviceDateEnd || d.serviceDateEnd || "",
    notes: rawProduct.internalNotes || "",
    supplierNote: rawProduct.supplierNote || d.supplierNote || "",
    customerRemark: rawProduct.customerRemark || d.customerRemark || "",
  };

  if (rawProduct.productType === "Flight") {
    const ctx = rawProduct.booking_context || rawProduct.bookingContext || "";
    return {
      ...base,
      flightBookingMode: ctx === "Flight Only" ? "flightonly" : "flightwithin",
      flightType: d.flightType || "RT",
      flightPricingMode: d.flightPricingMode || "FULL",
      airlinePnr: d.airlinePnr || "",
      ticketNumber: d.ticketNumber || "",
      // SPLIT pricing mode: direction-level PNR & ticket (RT / Multi)
      outboundPnr: d.outboundPnr || "",
      outboundTicket: d.outboundTicket || "",
      inboundPnr: d.inboundPnr || "",
      inboundTicket: d.inboundTicket || "",
      legs: (d.legs || []).map((leg) => ({
        id: leg.id || `leg-${Date.now()}-${Math.random()}`,
        direction: leg.direction || "Outbound",
        // Support both form-style keys (departure/arrival/date/time) and
        // API-normalised keys (departureAirport/arrivalAirport/departureDate/…)
        departure: leg.departure || leg.departureAirport || "",
        departureName: leg.departureName || "",
        arrival: leg.arrival || leg.arrivalAirport || "",
        arrivalName: leg.arrivalName || "",
        date: leg.date || leg.departureDate || "",
        time: leg.time || leg.departureTime || "",
        arrivalDate: leg.arrivalDate || "",
        arrivalTime: leg.arrivalTime || "",
        airlineCode: leg.airlineCode || "",
        airlineName: leg.airlineName || "",
        flightNumber: leg.flightNumber || "",
        cabinClass: leg.cabinClass || "",
        fareClass: leg.fareClass || "",
        fareClassName: leg.fareClassName || "",
        fareClassId: leg.fareClassId || null,
        // Keep whatever the leg actually carries. This used to default to
        // "Scheduled", which is not one of the values the form offers, so the
        // status dropdown rendered blank on every leg that had no status —
        // and the invented value would then be written back on save.
        flightStatus: leg.flightStatus || "",
        pnr: leg.pnr || "",
        ticketNumber: leg.ticketNumber || "",
        supplierRef: leg.supplierRef || "",
        // Baggage: support both flat keys (form-style) and nested baggage obj (API-style)
        baggageHandbag: !!(leg.baggage?.handbag || leg.baggageHandbag),
        baggageHandbagKg: leg.baggage?.handbag?.kg ?? leg.baggageHandbagKg ?? "",
        baggageHandbagPc: leg.baggage?.handbag?.pc ?? leg.baggageHandbagPc ?? "",
        baggageCabin: !!(leg.baggage?.cabin || leg.baggageCabin),
        baggageCabinKg: leg.baggage?.cabin?.kg ?? leg.baggageCabinKg ?? "",
        baggageCabinPc: leg.baggage?.cabin?.pc ?? leg.baggageCabinPc ?? "",
        baggageChecked: !!(leg.baggage?.checked || leg.baggageChecked),
        baggageCheckedKg: leg.baggage?.checked?.kg ?? leg.baggageCheckedKg ?? "",
        baggageCheckedPc: leg.baggage?.checked?.pc ?? leg.baggageCheckedPc ?? "",
        baggageCheckedInfant: !!(leg.baggage?.checkedInfant || leg.baggageCheckedInfant),
        baggageCheckedInfantKg: leg.baggage?.checkedInfant?.kg ?? leg.baggageCheckedInfantKg ?? "",
        baggageCheckedInfantPc: leg.baggage?.checkedInfant?.pc ?? leg.baggageCheckedInfantPc ?? "",
      })),
    };
  }

  if (rawProduct.productType === "Hotel") {
    let rooms = (d.rooms || []).map((r) => ({
      ...r,
      id: r.id || `room-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    }));
    if (rooms.length === 0 && (d.roomName || d.boardCode || d.roomType || d.roomStatus)) {
      rooms = [{
        id: `room-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        roomRate: d.roomRate || "Refundable",
        roomName: d.roomName || "",
        roomCharacteristic: d.roomCharacteristic || "",
        roomCode: d.roomCode || "",
        roomType: d.roomType || "",
        numberOfRooms: d.numberOfRooms || 1,
        roomStatus: d.roomStatus || "",
        boardCode: d.boardCode || "",
        boardName: d.boardName || "",
      }];
    }
    return {
      ...base,
      // Absent on legacy hotels → treated as "contains transfer" (allow).
      hotelContainsTransfer: d.hotelContainsTransfer !== false,
      hotelCode: d.hotelCode || "",
      hotelName: d.hotelName || "",
      hotelId: d.hotelId || null,
      hotelAddress: d.hotelAddress || "",
      // Hotel metadata — needed so HotelForm's selectedHotel displays correctly
      hotelStars: d.hotelStars || 0,
      hotelCity: d.hotelCity || "",
      hotelCountry: d.hotelCountry || "",
      checkIn: d.checkIn || "",
      checkOut: d.checkOut || "",
      nights: d.nights || 1,
      days: d.days || 1,
      // Payment deadlines — form saves as lastPaymentDate / lastPaymentTime
      lastPaymentDate: d.lastPaymentDate || d.lastPaymentDeadlineDate || "",
      lastPaymentTime: d.lastPaymentTime || d.lastPaymentDeadlineTime || "",
      lastFreeCancellationDate: d.lastFreeCancellationDate || "",
      lastFreeCancellationTime: d.lastFreeCancellationTime || "",
      // Early-bird — form saves as ebFirst_date / ebFirst_value etc.
      ebFirst_date: d.ebFirst_date || d.lastDateFor1stEBPayment || "",
      ebFirst_unit: d.ebFirst_unit || "%",
      ebFirst_value: d.ebFirst_value || d.firstEBPaymentPercentage || "",
      ebSecond_date: d.ebSecond_date || d.lastDateFor2ndEBPayment || "",
      ebSecond_unit: d.ebSecond_unit || "%",
      ebSecond_value: d.ebSecond_value || d.secondEBPaymentPercentage || "",
      ebThird_date: d.ebThird_date || d.lastDateFor3rdEBPayment || "",
      ebThird_unit: d.ebThird_unit || "%",
      ebThird_value: d.ebThird_value || d.thirdEBPaymentPercentage || "",
      roomRate: d.roomRate || "Refundable",
      roomName: d.roomName || "",
      roomCharacteristic: d.roomCharacteristic || "",
      roomCode: d.roomCode || "",
      roomType: d.roomType || "",
      numberOfRooms: d.numberOfRooms || 1,
      roomStatus: d.roomStatus || "",
      boardCode: d.boardCode || "",
      boardName: d.boardName || "",
      rooms,
      clientRemarks: d.clientRemarks || "",
      supplierRemarks: d.supplierRemarks || "",
      officeNotes: d.officeNotes || "",
    };
  }

  if (rawProduct.productType === "Transfer") {
    const ctx = rawProduct.booking_context || rawProduct.bookingContext || "";
    const pax = d.passengers || {};

    const unmapLeg = (legData) => {
      if (!legData) return {};
      // Support both flat form-style keys and nested API-style (schedule / flightLink)
      const ls = legData.schedule || legData;   // flat-style falls back to legData itself
      const lf = legData.flightLink || legData; // flat-style falls back to legData itself
      return {
        pickupLocationType: ls.pickupLocationType || "",
        dropoffLocationType: ls.dropoffLocationType || "",
        pickupLocation: ls.pickupLocation || "",
        pickupLocationLabel: ls.pickupLocationLabel || ls.pickupLocation || "",
        dropoffLocation: ls.dropoffLocation || "",
        dropoffLocationLabel: ls.dropoffLocationLabel || ls.dropoffLocation || "",
        pickupDate: ls.pickupDate || "",
        pickupTimeType: ls.pickupTimeType || "exact",
        pickupTime: ls.pickupTime || "",
        flightAirlineCode: legData.flightAirlineCode || lf.airlineCode || "",
        flightFlightNumber: legData.flightFlightNumber || lf.flightNumber || "",
        flightDate: legData.flightDate || lf.date || "",
        flightTime: legData.flightTime || lf.time || "",
        _linkedFlightId: lf.linkedFlightId ? String(lf.linkedFlightId) : "",
        _linkedLegId: lf.linkedLegId || "",
        pickupInstructions: legData.pickupInstructions || "",
        cantFindInstructions: legData.cantFindInstructions || "",
        travelDisruptionInfo: legData.travelDisruptionInfo || "",
        contactInfoSupplier: legData.contactInfoSupplier || "",
      };
    };

    if (d.transferMoment === "RoundTrip") {
      return {
        ...base,
        transferContext: ctx === "Standalone Transfer" ? "standalone" : (d.transferContext || "within_travel"),
        transferMoment: "RoundTrip",
        transferType: d.transferType || "",
        vehicleType: d.vehicleType || "",
        numberOfAdults: pax.adults || d.numberOfAdults || 1,
        numberOfChildren: pax.children ?? d.numberOfChildren ?? 0,
        leadTravelerName: pax.leadTravelerName || d.leadTravelerName || "",
        // Fall back to the legacy shared (product-level) values so existing
        // round-trip transfers keep showing their notes after the per-leg move.
        arrivalLeg: { ...unmapLeg(d.arrivalLeg), travelDisruptionInfo: d.arrivalLeg?.travelDisruptionInfo || d.travelDisruptionInfo || "", contactInfoSupplier: d.arrivalLeg?.contactInfoSupplier || d.contactInfoSupplier || "" },
        departureLeg: { ...unmapLeg(d.departureLeg), travelDisruptionInfo: d.departureLeg?.travelDisruptionInfo || d.travelDisruptionInfo || "", contactInfoSupplier: d.departureLeg?.contactInfoSupplier || d.contactInfoSupplier || "" },
        travelDisruptionInfo: d.travelDisruptionInfo || "",
        contactInfoSupplier: d.contactInfoSupplier || "",
        emergencyAssistance: d.emergencyAssistance || "",
        clientRemarks: d.clientRemarks || "",
        supplierRemarks: d.supplierRemarks || "",
        officeNotes: d.officeNotes || "",
      };
    }

    // Support both flat form-style keys and nested API-style (schedule / flightLink)
    const sched = d.schedule || d;   // flat-style: schedule fields sit directly on d
    const fl = d.flightLink || d;   // flat-style: flightLink fields sit directly on d
    return {
      ...base,
      transferContext: ctx === "Standalone Transfer" ? "standalone" : (d.transferContext || "within_travel"),
      transferMoment: d.transferMoment || "",
      transferType: d.transferType || "",
      vehicleType: d.vehicleType || "",
      pickupLocationType: sched.pickupLocationType || "",
      dropoffLocationType: sched.dropoffLocationType || "",
      pickupLocation: sched.pickupLocation || "",
      pickupLocationLabel: d.pickupLocationLabel || sched.pickupLocation || "",
      dropoffLocation: sched.dropoffLocation || "",
      dropoffLocationLabel: d.dropoffLocationLabel || sched.dropoffLocation || "",
      pickupDate: sched.pickupDate || "",
      pickupTimeType: sched.pickupTimeType || "exact",
      pickupTime: sched.pickupTime || "",
      numberOfAdults: pax.adults || d.numberOfAdults || 1,
      numberOfChildren: pax.children ?? d.numberOfChildren ?? 0,
      leadTravelerName: pax.leadTravelerName || d.leadTravelerName || "",
      flightAirlineCode: d.flightAirlineCode || fl.airlineCode || "",
      flightFlightNumber: d.flightFlightNumber || fl.flightNumber || "",
      flightDate: d.flightDate || fl.date || "",
      flightTime: d.flightTime || fl.time || "",
      _linkedFlightId: fl.linkedFlightId ? String(fl.linkedFlightId) : "",
      _linkedLegId: fl.linkedLegId || "",
      pickupInstructions: d.pickupInstructions || "",
      cantFindInstructions: d.cantFindInstructions || "",
      travelDisruptionInfo: d.travelDisruptionInfo || "",
      contactInfoSupplier: d.contactInfoSupplier || "",
      emergencyAssistance: d.emergencyAssistance || "",
      clientRemarks: d.clientRemarks || "",
      supplierRemarks: d.supplierRemarks || "",
      officeNotes: d.officeNotes || "",
    };
  }

  if (rawProduct.productType === "Insurance") {
    return {
      ...base,
      insuranceContext: d.insuranceContext || "Standalone",
      insuranceType: d.insuranceType || "",
      insuranceName: d.insuranceName || "",
      insuranceIdNumber: d.insuranceIdNumber || "",
      coverageArea: d.coverageArea || "",
      insuranceStartDate: d.insuranceStartDate || "",
      holidayStartDate: d.holidayStartDate || "",
      insuranceExpireDate: d.insuranceExpireDate || "",
      numberOfPersons: d.numberOfPersons || 1,
      bookingDate: d.bookingDate || "",
      invoiceNotes: d.invoiceNotes || "",
      officeNotes: d.officeNotes || "",
    };
  }

  if (rawProduct.productType === "Package") {
    return {
      ...base,
      packageType: d.packageType || "",
      supplierReservationNumber: d.supplierReservationNumber || "",
      supplierInvoiceNumber: d.supplierInvoiceNumber || "",
      bookingDate: d.bookingDate || "",
      packageName: d.packageName || "",
      destinationCity: d.destinationCity || "",
      destinationCountry: d.destinationCountry || "",
      departureDate: d.departureDate || "",
      returnDate: d.returnDate || "",
      numberOfNights: d.numberOfNights || 0,
      pkgHotelName: d.pkgHotelName || "",
      pkgHotelStars: d.pkgHotelStars || "",
      pkgHotelCity: d.pkgHotelCity || "",
      pkgHotelCountry: d.pkgHotelCountry || "",
      pkgNumberOfAdults: d.pkgNumberOfAdults || 2,
      pkgNumberOfChildren: d.pkgNumberOfChildren || 0,
      pkgNumberOfRooms: d.pkgNumberOfRooms || 1,
      pkgRoomType: d.pkgRoomType || "",
      pkgRoomName: d.pkgRoomName || "",
      pkgRoomCharacteristics: d.pkgRoomCharacteristics || "",
      pkgBoardType: d.pkgBoardType || "",
      pkgDepartureAirport: d.pkgDepartureAirport || "",
      pkgArrivalAirport: d.pkgArrivalAirport || "",
      pkgAirlineName: d.pkgAirlineName || "",
      pkgTransferIncluded: d.pkgTransferIncluded || false,
      pkgTransferPriceIncluded: d.pkgTransferPriceIncluded || false,
      pkgInsuranceIncluded: d.pkgInsuranceIncluded || false,
      pkgInsurancePriceIncluded: d.pkgInsurancePriceIncluded || false,
      clientRemarks: d.clientRemarks || "",
      supplierRemarks: d.supplierRemarks || "",
      officeNotes: d.officeNotes || "",
    };
  }

  if (rawProduct.productType === "CarRental") {
    return {
      ...base,
      carRentalContext: d.carRentalContext || "Standalone",
      supplierReservationNumber: d.supplierReservationNumber || "",
      bookingDate: d.bookingDate || "",
      carCategory: d.carCategory || "",
      vehicleMakeModel: d.vehicleMakeModel || "",
      orSimilar: d.orSimilar || false,
      creditCardRequired: d.creditCardRequired || false,
      depositRequired: d.depositRequired || false,
      insuranceIncluded: d.insuranceIncluded || false,
      pickupDate: d.pickupDate || "",
      pickupTime: d.pickupTime || "",
      pickupLocation: d.pickupLocation || "",
      dropoffDate: d.dropoffDate || "",
      dropoffTime: d.dropoffTime || "",
      dropoffLocation: d.dropoffLocation || "",
      rentalDays: d.rentalDays || 0,
      leadDriverName: d.leadDriverName || "",
      leadDriverAge: d.leadDriverAge || "",
      additionalDrivers: d.additionalDrivers || [],
      pickupInstructions: d.pickupInstructions || "",
      supplierContactInfo: d.supplierContactInfo || "",
      emergencyAssistance: d.emergencyAssistance || "",
      invoiceNotes: d.invoiceNotes || "",
      officeNotes: d.officeNotes || "",
    };
  }

  if (rawProduct.productType === "Visa") {
    return {
      ...base,
      visaContext: d.visaContext || "Standalone",
      supplierRefNumber: d.supplierRefNumber || "",
      bookingDate: d.bookingDate || "",
      visaType: d.visaType || "",
      visaCountry: d.visaCountry || "",
      visaEntryType: d.visaEntryType || "",
      visaDecisionStatus: d.visaDecisionStatus || "Pending",
      visaIssueDate: d.visaIssueDate || "",
      visaExpiryDate: d.visaExpiryDate || "",
      visaDurationOfStay: d.visaDurationOfStay || "",
      officeNotes: d.officeNotes || "",
    };
  }

  if (rawProduct.productType === "Cruise") {
    return {
      ...base,
      cruiseContext: d.cruiseContext || "Standalone",
      bookingDate: d.bookingDate || "",
      cruiseLine: d.cruiseLine || "",
      shipName: d.shipName || "",
      cruiseNameItinerary: d.cruiseNameItinerary || "",
      departurePort: d.departurePort || "",
      arrivalPort: d.arrivalPort || "",
      departureDate: d.departureDate || "",
      returnDate: d.returnDate || "",
      duration: d.duration || 0,
      cabins: d.cabins || [],
      numberOfAdults: d.numberOfAdults || 2,
      numberOfChildren: d.numberOfChildren || 0,
      leadTravelerName: d.leadTravelerName || "",
      portsOfCall: d.portsOfCall || [],
      clientRemarks: d.clientRemarks || "",
      supplierRemarks: d.supplierRemarks || "",
      officeNotes: d.officeNotes || "",
    };
  }

  return base;
};

const BOOKING = {
  ref: "SNK-2025-00124",
  createdAt: "12 Jan 2025",
  createdBy: "Sarah Admin",
  invoiceNumber: "INV-2025-00841",
  origin: "Online",
  bookingStatus: "Pending",
  paymentStatus: "Partial",
  saleType: "PACKAGE_TRAVEL",
  totalPrice: 3840.0,
  customer: {
    name: "Sarah Johnson",
    firstName: "Sarah",
    lastName: "Johnson",
    email: "sarah.j@email.com",
    phone: "+32 477 123 456",
    type: "Private",
    dateOfBirth: "1985-04-12",
    gender: "Female",
    nationality: "Belgian",
    language: "English",
    street: "Kerkstraat",
    houseNumber: "45",
    boxNumber: "3A",
    city: "Brussels",
    postalCode: "1000",
    country: "Belgium",
  },
  components: [
    {
      id: 1, type: "Flight", iconKey: "flight", status: "Confirmed", supplier: "Airtuerk",
      supplierName: "Airtuerk GMBH", travelersCount: 3, costPrice: 980.0, sellingPrice: 1240.0,
      fields: [
        { label: "From", value: "Brussels (BRU)" }, { label: "To", value: "Antalya (AYT)" },
        { label: "Departure", value: "14 Mar 2025 06:45" }, { label: "Return", value: "21 Mar 2025 14:30" },
        { label: "Passengers", value: "2 Adults, 1 Child" }, { label: "Class", value: "Economy" },
        { label: "PNR", value: "TK-7X9K2M" }, { label: "Selling Price", value: "€ 1,240.00" },
      ],
      _raw: {
        id: 1, productType: "Flight", productStatus: "Confirmed", supplierId: null,
        supplierReference: "", bookingContext: "Flight Only",
        productDetails: {
          flightType: "RT", flightPricingMode: "FULL", airlinePnr: "TK-7X9K2M", ticketNumber: "",
          legs: [
            { id: "demo-leg-1", direction: "Outbound", departureAirport: "BRU", arrivalAirport: "AYT", departureDate: "2025-03-14", departureTime: "06:45", arrivalDate: "2025-03-14", arrivalTime: "11:30", airlineCode: "TK", flightNumber: "1940", cabinClass: "Economy", fareClass: "Y", fareClassName: "Flex", flightStatus: "Confirmed", pnr: "TK-7X9K2M", ticketNumber: "", baggage: { handbag: { kg: "8", pc: "1" }, checked: { kg: "23", pc: "1" } } },
            { id: "demo-leg-2", direction: "Inbound", departureAirport: "AYT", arrivalAirport: "BRU", departureDate: "2025-03-21", departureTime: "14:30", arrivalDate: "2025-03-21", arrivalTime: "17:45", airlineCode: "TK", flightNumber: "1941", cabinClass: "Economy", fareClass: "Y", fareClassName: "Flex", flightStatus: "Confirmed", pnr: "TK-7X9K2M", ticketNumber: "", baggage: { handbag: { kg: "8", pc: "1" }, checked: { kg: "23", pc: "1" } } },
          ],
          pricing: {
            travelerPricing: [
              { travelerId: 1, netPrice: 245, sellingPrice: 320, ticketNumber: "TK-2351940" },
              { travelerId: 2, netPrice: 245, sellingPrice: 320, ticketNumber: "TK-2351941" },
              { travelerId: 3, netPrice: 180, sellingPrice: 240, ticketNumber: "TK-2351942" },
            ],
          },
          scheduleChanges: [
            {
              title: "Schedule Change – 10 Mar 2026",
              prev: { from: "BRU", to: "AYT", date: "14 Mar 2025", depTime: "06:45", arrTime: "11:30", flightCode: "TK1940" },
              updated: { from: "BRU", to: "AYT", date: "14 Mar 2025", depTime: "07:15", arrTime: "12:00", flightCode: "TK1940" },
              detectedVia: "API",
              detectedAt: "12 May 2025, 14:20",
              updatedBy: "System",
              customerNotified: false,
              docsRegenerated: false,
              impact: "Minor",
              resolved: false,
            },
          ],
        },
      },
    },
    {
      id: 2, type: "Hotel", iconKey: "hotel", status: "Confirmed", supplier: "Hotelbeds",
      supplierName: "Hotelbeds", travelersCount: 3, costPrice: 1620.0, sellingPrice: 2200.0,
      fields: [
        { label: "Hotel Name", value: "Rixos Premium Belek" }, { label: "Location", value: "Belek, Turkey" },
        { label: "Check-in", value: "14 Mar 2025" }, { label: "Check-out", value: "21 Mar 2025" },
        { label: "Room Type & Board", value: "Family Suite — All Inclusive" }, { label: "Nights", value: "7 nights" },
        { label: "Supplier Ref", value: "HB-99234112" }, { label: "Selling Price", value: "€ 2,200.00" },
      ],
      _raw: {
        id: 2, productType: "Hotel", productStatus: "Confirmed", supplierId: null,
        supplierReference: "HB-99234112", bookingContext: "Hotel",
        productDetails: {
          hotelCode: "RIXPREM", hotelName: "Rixos Premium Belek", hotelId: null, hotelAddress: "Belek, Turkey",
          checkIn: "2025-03-14", checkOut: "2025-03-21", nights: 7, days: 8,
          roomRate: "Non-Refundable", roomName: "Family Suite", roomCharacteristic: "Sea View",
          roomCode: "FSUI", roomType: "Suite", numberOfRooms: 1, roomStatus: "Confirmed",
          boardCode: "AI", boardName: "All Inclusive",
          lastPaymentDeadlineDate: "", lastPaymentDeadlineTime: "",
          lastFreeCancellationDate: "", lastFreeCancellationTime: "",
          clientRemarks: "", supplierRemarks: "", officeNotes: "",
          rooms: [],
        },
      },
    },
    {
      id: 3, type: "Transfer", iconKey: "transfer", status: "Confirmed", supplier: "Hotelbeds",
      supplierName: "Hotelbeds", travelersCount: 2, costPrice: 210.0, sellingPrice: 280.0,
      fields: [
        { label: "Transfer Type", value: "Private Transfer" }, { label: "Route (Outbound)", value: "AYT → Rixos Belek" },
        { label: "Route (Return)", value: "Rixos Belek → AYT" }, { label: "Passengers", value: "3 pax" },
        { label: "Supplier Ref", value: "TR-00193842" }, { label: "Selling Price", value: "€ 280.00" },
      ],
      _raw: {
        id: 3, productType: "Transfer", productStatus: "Confirmed", supplierId: null,
        supplierReference: "TR-00193842", bookingContext: "Transfer within Travel Booking",
        productDetails: {
          transferMoment: "Arrival", transferType: "Private", vehicleType: "Minibus",
          schedule: { pickupLocationType: "Airport", dropoffLocationType: "Hotel", pickupLocation: "Antalya Airport (AYT)", dropoffLocation: "Rixos Premium Belek", pickupDate: "2025-03-14", pickupTime: "12:00" },
          passengers: { adults: 2, children: 1, leadTravelerName: "Sarah Johnson" },
          flightLink: { airlineCode: "TK", flightNumber: "1940", date: "2025-03-14", time: "11:30" },
          pickupInstructions: "", cantFindInstructions: "", travelDisruptionInfo: "",
          contactInfoSupplier: "", emergencyAssistance: "",
          clientRemarks: "", supplierRemarks: "", officeNotes: "",
        },
      },
    },
    {
      id: 4, type: "Insurance", iconKey: "insurance", status: "Pending", supplier: "Manual",
      supplierName: "Manual", travelersCount: 2, costPrice: 80.0, sellingPrice: 120.0,
      fields: [
        { label: "Policy Type", value: "All-risk Travel Insurance" }, { label: "Coverage", value: "3 travelers" },
        { label: "Valid From", value: "14 Mar 2025" }, { label: "Valid To", value: "21 Mar 2025" },
        { label: "Policy Ref", value: "Pending" }, { label: "Selling Price", value: "€ 120.00" },
      ],
    },
  ],
  travelers: [
    { id: 1, name: "Sarah Johnson", dob: "12 Apr 1985", type: "Adult", passport: "BE123456", status: "Confirmed", docs: true },
    { id: 2, name: "Mark Johnson", dob: "05 Sep 1983", type: "Adult", passport: "BE789012", status: "Confirmed", docs: true },
    { id: 3, name: "Lena Johnson", dob: "22 Jul 2015", type: "Child", passport: "BE345678", status: "Confirmed", docs: false },
  ],
  assignments: [
    { travelerId: 1, productId: 1, hasPricing: true },
    { travelerId: 2, productId: 1, hasPricing: true },
    { travelerId: 3, productId: 1, hasPricing: true },
    { travelerId: 1, productId: 2, hasPricing: true },
    { travelerId: 2, productId: 2, hasPricing: true },
    { travelerId: 3, productId: 2, hasPricing: false },
    { travelerId: 1, productId: 3, hasPricing: true },
    { travelerId: 2, productId: 3, hasPricing: true },
    { travelerId: 3, productId: 3, hasPricing: false },
    { travelerId: 1, productId: 4, hasPricing: false },
    { travelerId: 2, productId: 4, hasPricing: false },
  ],
  pricing: {
    sellingPrice: 3840.0, costPrice: 2890.0, margin: 950.0, marginPct: 24.7,
    vatRegime: "TOMS", vatCountry: "Turkey",
    breakdown: [
      { label: "Flight", cost: 980.0, sell: 1240.0 },
      { label: "Hotel", cost: 1620.0, sell: 2200.0 },
      { label: "Transfer", cost: 210.0, sell: 280.0 },
      { label: "Insurance", cost: 80.0, sell: 120.0 },
    ],
    vat: [
      { component: "Hotel (Turkey)", regime: "TOMS", rate: "Margin only", amount: "€ 76.00" },
      { component: "Flight", regime: "TOMS", rate: "Exempt", amount: "€ 0.00" },
      { component: "Transfer", regime: "TOMS", rate: "Margin only", amount: "€ 18.40" },
      { component: "Insurance", regime: "N/A", rate: "Exempt", amount: "€ 0.00" },
    ],
    tomsMargin: 950.0, tomsVat: 94.4,
  },
  confirmations: [
    { id: 1, component: "Flight", iconKey: "flight", supplier: "Airtuerk", ref: "TK-7X9K2M", confirmedAt: "12 Jan 2025 09:14", confirmedBy: "System (API)" },
    { id: 2, component: "Hotel", iconKey: "hotel", supplier: "Hotelbeds", ref: "HB-99234112", confirmedAt: "12 Jan 2025 09:16", confirmedBy: "System (API)" },
    { id: 3, component: "Transfer", iconKey: "transfer", supplier: "Hotelbeds", ref: "TR-00193842", confirmedAt: "12 Jan 2025 09:16", confirmedBy: "System (API)" },
    { id: 4, component: "Insurance", iconKey: "insurance", supplier: "Manual", ref: "Pending", confirmedAt: "—", confirmedBy: "Awaiting manual" },
  ],
  payments: {
    client: [
      { date: "12 Jan 2025", method: "Credit Card", amount: 1920.0, status: "Paid", ref: "PAY-CC-002841" },
      { date: "28 Feb 2025", method: "Bank Transfer", amount: 1920.0, status: "Pending", ref: "PAY-BT-002842" },
    ],
    supplier: [
      { supplier: "Airtuerk", amount: 980.0, status: "Paid", dueDate: "12 Jan 2025", ref: "SUP-TK-00481" },
      { supplier: "Hotelbeds", amount: 1830.0, status: "Paid", dueDate: "14 Jan 2025", ref: "SUP-HB-00129" },
      { supplier: "Insurance", amount: 80.0, status: "Unpaid", dueDate: "01 Mar 2025", ref: "—" },
    ],
  },
  documents: [
    { name: "Booking Confirmation", type: "PDF", size: "124 KB", generatedAt: "12 Jan 2025 09:20", iconKey: "file" },
    { name: "Travel Voucher — Flight", type: "PDF", size: "88 KB", generatedAt: "12 Jan 2025 09:21", iconKey: "flight" },
    { name: "Travel Voucher — Hotel", type: "PDF", size: "95 KB", generatedAt: "12 Jan 2025 09:21", iconKey: "hotel" },
    { name: "Invoice INV-2025-00841", type: "PDF", size: "142 KB", generatedAt: "12 Jan 2025 09:22", iconKey: "file" },
  ],
  auditLog: [
    { id: 1, action: "Booking Created", user: "Sarah Admin", timestamp: "2025-01-12T09:12:34.000Z", note: "Booking originated from online checkout. Customer: Sarah Johnson.", isSystem: false, productType: null, ipAddress: "192.168.1.45", userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0" },
    { id: 2, action: "Traveler Added", user: "Sarah Admin", timestamp: "2025-01-12T09:12:35.000Z", note: "Added traveler: Sarah Johnson (ADT)", isSystem: false, productType: null, ipAddress: "192.168.1.45", userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0" },
    { id: 3, action: "Traveler Added", user: "Sarah Admin", timestamp: "2025-01-12T09:12:36.000Z", note: "Added traveler: Mark Johnson (ADT)", isSystem: false, productType: null, ipAddress: "192.168.1.45", userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0" },
    { id: 4, action: "Product Added", user: "Sarah Admin", timestamp: "2025-01-12T09:13:02.000Z", note: "Flight BRU → AYT added via Airtuerk API", isSystem: false, productType: "Flight", ipAddress: "192.168.1.45", userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0", newValue: { supplier: "Airtuerk", route: "BRU → AYT", airline: "Turkish Airlines", departureDate: "2025-03-15" } },
    { id: 5, action: "Product Added", user: "Sarah Admin", timestamp: "2025-01-12T09:13:28.000Z", note: "Hotel Rixos Sungate added via Hotelbeds", isSystem: false, productType: "Hotel", ipAddress: "192.168.1.45", userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0", newValue: { supplier: "Hotelbeds", hotel: "Rixos Sungate", checkIn: "2025-03-15", checkOut: "2025-03-22", rooms: 1 } },
    { id: 6, action: "Product Added", user: "Sarah Admin", timestamp: "2025-01-12T09:13:45.000Z", note: "Airport transfer AYT → Hotel added", isSystem: false, productType: "Transfer", ipAddress: "192.168.1.45", userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0", newValue: { supplier: "Hotelbeds", type: "Airport Transfer", route: "AYT Airport → Rixos Sungate" } },
    { id: 7, action: "Flight Confirmed", user: "System (Airtuerk API)", timestamp: "2025-01-12T09:14:12.000Z", note: "PNR TK-7X9K2M confirmed automatically via API.", isSystem: true, productType: "Flight" },
    { id: 8, action: "Hotel Confirmed", user: "System (Hotelbeds API)", timestamp: "2025-01-12T09:16:08.000Z", note: "Hotel booking HB-99234112 confirmed.", isSystem: true, productType: "Hotel" },
    { id: 9, action: "Transfer Confirmed", user: "System (Hotelbeds API)", timestamp: "2025-01-12T09:16:14.000Z", note: "Transfer TR-00193842 confirmed.", isSystem: true, productType: "Transfer" },
    { id: 10, action: "Pricing Set", user: "Sarah Admin", timestamp: "2025-01-12T09:17:05.000Z", note: "Flight pricing set for 2 travelers", isSystem: false, productType: "Flight", financialImpact: "Selling: €1,240.00 | Cost: €980.00", newValue: { sellingPrice: "€1,240.00", costPrice: "€980.00", markup: "€260.00" } },
    { id: 11, action: "Pricing Set", user: "Sarah Admin", timestamp: "2025-01-12T09:17:22.000Z", note: "Hotel pricing set for Room 1", isSystem: false, productType: "Hotel", financialImpact: "Selling: €2,100.00 | Cost: €1,680.00", newValue: { sellingPrice: "€2,100.00", costPrice: "€1,680.00", markup: "€420.00" } },
    { id: 12, action: "Payment Received", user: "System (Payment Gateway)", timestamp: "2025-01-12T09:18:44.000Z", note: "Deposit of €1,920.00 received via Credit Card. Ref: PAY-CC-002841.", isSystem: true, financialImpact: "€1,920.00 received" },
    { id: 13, action: "Assignment Created", user: "Sarah Admin", timestamp: "2025-01-12T09:19:10.000Z", note: "Sarah Johnson assigned to Flight BRU → AYT", isSystem: false, productType: "Flight" },
    { id: 14, action: "Assignment Created", user: "Sarah Admin", timestamp: "2025-01-12T09:19:11.000Z", note: "Mark Johnson assigned to Flight BRU → AYT", isSystem: false, productType: "Flight" },
    { id: 15, action: "Documents Generated", user: "Sarah Admin", timestamp: "2025-01-12T09:22:30.000Z", note: "Confirmation, vouchers, and invoice generated and emailed to sarah.j@email.com.", isSystem: false, ipAddress: "192.168.1.45", userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0" },
    { id: 16, action: "Booking Confirmed", user: "System", timestamp: "2025-01-12T09:22:31.000Z", note: "All required components confirmed. Booking auto-confirmed.", isSystem: true },
    { id: 17, action: "Schedule Change", user: "System (Airtuerk API)", timestamp: "2025-02-05T14:33:00.000Z", note: "Flight schedule updated by airline", isSystem: true, productType: "Flight", oldValue: { departureTime: "08:30", arrivalTime: "12:45" }, newValue: { departureTime: "09:15", arrivalTime: "13:30" } },
    { id: 18, action: "Product Updated", user: "Ahmed Ops", timestamp: "2025-02-05T14:45:00.000Z", note: "Updated flight segment after schedule change notification", isSystem: false, productType: "Flight", ipAddress: "10.0.0.22", userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15", oldValue: { departureTime: "08:30" }, newValue: { departureTime: "09:15" }, reason: "Airline schedule change" },
    { id: 19, action: "Pricing Updated", user: "Ahmed Ops", timestamp: "2025-02-28T11:05:00.000Z", note: "Hotel pricing adjusted after rate change", isSystem: false, productType: "Hotel", ipAddress: "10.0.0.22", userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15", oldValue: { sellingPrice: "€2,100.00" }, newValue: { sellingPrice: "€2,200.00" }, financialImpact: "+€100.00 selling price increase", reason: "Rate adjustment" },
  ],
};

const TABS = [
  { key: "customer", label: "Customer", Icon: UserCircle },
  { key: "itinerary", label: "Itinerary", Icon: MapIcon },
  { key: "travelers", label: "Travelers", Icon: User },
  { key: "assignments", label: "Assignments", Icon: Link2 },
  { key: "pricing", label: "Pricing & VAT", Icon: Euro },
  { key: "overview", label: "Booking Overview", Icon: ClipboardCheck },
  { key: "confirmations", label: "Confirmations", Icon: CheckCircle },
  { key: "payments", label: "Payments", Icon: CreditCard },
  { key: "documents", label: "Documents", Icon: FileText },
  { key: "emails", label: "Emails", Icon: Mail },
  { key: "notes", label: "Notes", Icon: StickyNote },
  { key: "audit", label: "Audit Log", Icon: ClipboardList },
];

const ICON_MAP = {
  flight: Plane, hotel: Hotel, transfer: Bus, insurance: Shield,
  package: Package, carrental: Car, visa: FileText, cruise: Ship, file: FileText,
};

const PRODUCT_STYLE = {
  Flight: { bg: "#eef9f3", color: "#0d7a4f", border: "#c3e8d4" },
  Hotel: { bg: "#f5eeff", color: "#7c3aed", border: "#ddd0f7" },
  Transfer: { bg: "#eef3ff", color: "#3b5bdb", border: "#d0dafb" },
  Insurance: { bg: "#fef3e2", color: "#b06e0a", border: "#fde4b0" },
  Package: { bg: "#fff0f0", color: "#c42b2b", border: "#f5c2c2" },
  CarRental: { bg: "#e8f5ee", color: "#0d7a4f", border: "#c3e8d4" },
  Visa: { bg: "#f5f6f8", color: "#64748b", border: "#e2e5ea" },
  Cruise: { bg: "#e8f5fb", color: "#0e7490", border: "#bae6fd" },
};

const PRODUCT_ICON_MAP = {
  Flight: Plane, Hotel: Hotel, Transfer: Bus, Insurance: Shield,
  Package: Package, CarRental: Car, Visa: FileText, Cruise: Ship,
};

const statusClass = (s) => {
  if (s === "Confirmed") return styles.badgeConfirmed;
  if (s === "Pending") return styles.badgePending;
  if (s === "Cancelled") return styles.badgeCancelled;
  return "";
};

const payClass = (s) => {
  if (s === "Paid") return styles.payPaid;
  if (s === "Partial") return styles.payPartial;
  if (s === "Refunded") return styles.payRefunded;
  if (s === "Waiting Supplier Refund") return styles.payWaiting;
  return styles.payUnpaid;
};

// Color-only variant for the large payment status shown beside the price panel.
const payTextClass = (s) => {
  if (s === "Paid") return styles.payTextPaid;
  if (s === "Partial") return styles.payTextPartial;
  if (s === "Refunded") return styles.payTextRefunded;
  if (s === "Waiting Supplier Refund") return styles.payTextWaiting;
  return styles.payTextUnpaid;
};

const fmt = (n) => `€ ${Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
};

const productIconKey = (type) => ({
  Flight: "flight", Hotel: "hotel", Transfer: "transfer", Insurance: "insurance",
  Package: "package", CarRental: "carrental", Visa: "visa", Cruise: "cruise",
}[type] || "file");

const buildProductFields = (p) => {
  const d = typeof p.productDetails === 'string' ? JSON.parse(p.productDetails) : (p.productDetails || {});
  if (p.productType === "Flight") {
    // The full per-leg itinerary (every leg — inbound, outbound and each segment) is
    // rendered in the always-visible legs strip on the Overview row, so the expandable
    // detail only carries the shared summary fields here (avoids duplicating the legs).
    const legs = Array.isArray(d.legs) ? d.legs : [];
    const fields = [];
    const cabin = (legs[0]?.cabinClass || "").toUpperCase();
    if (cabin) fields.push({ label: "Class", value: cabin });
    // Keep a plain "PNR" field so the collapsed-row PNR lookup still resolves.
    const outPnr = (d.outboundPnr || d.airlinePnr || legs[0]?.pnr || "").toUpperCase();
    const inPnr = (d.inboundPnr || "").toUpperCase();
    fields.push({ label: "PNR", value: outPnr || "—" });
    if (inPnr && inPnr !== outPnr) fields.push({ label: "Inbound PNR", value: inPnr });
    const ticket = (d.ticketNumber || legs[0]?.ticketNumber || "").toUpperCase();
    fields.push({ label: "Ticket", value: ticket || "—" });
    return fields;
  }
  if (p.productType === "Hotel") {
    return [
      { label: "Hotel Name", value: d.hotelName || "—" },
      { label: "Address", value: d.hotelAddress || "—" },
      { label: "Check-in", value: formatDate(d.checkIn) },
      { label: "Check-out", value: formatDate(d.checkOut) },
      { label: "Nights", value: d.nights ? `${d.nights} nights` : "—" },
      { label: "Supplier Ref", value: p.supplierReference || "—" },
    ];
  }
  if (p.productType === "Transfer") {
    if (d.transferMoment === "RoundTrip") {
      const aLeg = d.arrivalLeg?.schedule || {};
      const dLeg = d.departureLeg?.schedule || {};
      return [
        { label: "Transfer Moment", value: "Round Trip" },
        { label: "Transfer Type", value: d.transferType || "—" },
        { label: "Vehicle", value: d.vehicleType || "—" },
        { label: "Arrival: Pickup", value: aLeg.pickupLocation || "—" },
        { label: "Arrival: Dropoff", value: aLeg.dropoffLocation || "—" },
        { label: "Arrival Date", value: aLeg.pickupDate ? `${formatDate(aLeg.pickupDate)} ${aLeg.pickupTime || ""}`.trim() : "—" },
        { label: "Departure: Pickup", value: dLeg.pickupLocation || "—" },
        { label: "Departure: Dropoff", value: dLeg.dropoffLocation || "—" },
        { label: "Departure Date", value: dLeg.pickupDate ? `${formatDate(dLeg.pickupDate)} ${dLeg.pickupTime || ""}`.trim() : "—" },
        { label: "Supplier Ref", value: p.supplierReference || "—" },
      ];
    }
    const s = d.schedule || {};
    return [
      { label: "Transfer Type", value: d.transferType || "—" },
      { label: "Vehicle", value: d.vehicleType || "—" },
      { label: "Pickup", value: s.pickupLocation || "—" },
      { label: "Dropoff", value: s.dropoffLocation || "—" },
      { label: "Pickup Date", value: s.pickupDate ? `${formatDate(s.pickupDate)} ${s.pickupTime || ""}`.trim() : "—" },
      { label: "Supplier Ref", value: p.supplierReference || "—" },
    ];
  }
  if (p.productType === "Insurance") {
    return [
      { label: "Insurance Type", value: d.insuranceType || d.policyType || "—" },
      { label: "Insurance Name", value: d.insuranceName || "—" },
      { label: "Coverage Area", value: d.coverageArea || d.coverage || "—" },
      { label: "Start Date", value: formatDate(d.insuranceStartDate || d.validFrom) },
      { label: "Expiry Date", value: formatDate(d.insuranceExpireDate || d.validTo) },
      { label: "Insurance ID", value: d.insuranceIdNumber || d.policyRef || p.supplierReference || "—" },
    ];
  }
  if (p.productType === "Package") {
    return [
      { label: "Package Name", value: d.packageName || "—" },
      { label: "Destination", value: d.destinationCity || d.destination || "—" },
      { label: "Departure Date", value: formatDate(d.departureDate || d.departure) },
      { label: "Return Date", value: formatDate(d.returnDate) },
      { label: "Passengers", value: d.passengers || "—" },
      { label: "Board Type", value: d.pkgBoardType || d.boardType || "—" },
      { label: "Supplier Ref", value: p.supplierReference || d.supplierReservationNumber || "—" },
    ];
  }
  if (p.productType === "CarRental") {
    const veh = [d.carCategory, d.vehicleMakeModel].filter(Boolean).join(" — ");
    return [
      { label: "Vehicle Type", value: veh || "—" },
      { label: "Pick-up Location", value: d.pickupLocation || "—" },
      { label: "Drop-off Location", value: d.dropoffLocation || "—" },
      { label: "Pick-up Date", value: formatDate(d.pickupDate) },
      { label: "Drop-off Date", value: formatDate(d.dropoffDate) },
      { label: "Driver Name", value: d.driver || d.leadDriverName || "—" },
      { label: "Supplier Ref", value: p.supplierReference || d.supplierReservationNumber || "—" },
    ];
  }
  if (p.productType === "Visa") {
    return [
      { label: "Visa Type", value: d.visaType || "—" },
      { label: "Destination Country", value: d.visaCountry || d.destinationCountry || "—" },
      { label: "Entry Type", value: d.visaEntryType || "—" },
      { label: "Issue Date", value: formatDate(d.visaIssueDate || d.applicationDate) },
      { label: "Expiry Date", value: formatDate(d.visaExpiryDate) },
      { label: "Status", value: d.visaDecisionStatus || d.visaStatus || "—" },
      { label: "Supplier Ref", value: p.supplierReference || d.supplierRefNumber || "—" },
    ];
  }
  if (p.productType === "Cruise") {
    const ports = Array.isArray(d.portsOfCall) ? d.portsOfCall.length : 0;
    return [
      { label: "Cruise", value: d.cruiseNameItinerary || d.shipName || d.cruiseLine || "—" },
      { label: "Ship", value: d.shipName || "—" },
      { label: "Departure Port", value: d.departurePort || "—" },
      { label: "Departure Date", value: formatDate(d.departureDate) },
      { label: "Return Date", value: formatDate(d.returnDate) },
      { label: "Duration", value: d.duration ? `${d.duration} night${d.duration !== 1 ? "s" : ""}` : "—" },
      { label: "Ports of Call", value: ports ? `${ports} ports` : "—" },
      { label: "Supplier Ref", value: p.supplierReference || "—" },
    ];
  }
  return [];
};

const sumProductPricing = (p, cancelledTravelerIds) => {
  const pd = typeof p.productDetails === 'string' ? JSON.parse(p.productDetails) : (p.productDetails || {});
  const pricing = pd.pricing || {};
  // When a set of cancelled traveler ids is supplied, per-traveler pricing rows for
  // those travelers are skipped — mirrors the backend recalc so the frontend totals
  // are correct and self-consistent regardless of the backend's recalc state.
  const skip = (tid) => cancelledTravelerIds instanceof Set && cancelledTravelerIds.has(String(tid));
  let cost = 0, sell = 0;
  if (p.productType === "Flight") {
    (pricing.travelerPricing || []).forEach((tp) => {
      if (skip(tp.travelerId)) return;
      cost += parseFloat(tp.netPrice || 0) + parseFloat(tp.tax || 0);
      sell += parseFloat(tp.sellingPrice || tp.clientPrice || 0);
    });
  } else if (p.productType === "Hotel") {
    (pricing.roomPricing || []).forEach((rp) => {
      cost += parseFloat(rp.netPrice || 0);
      // sellingPrice = netPrice + markup (stored WITHOUT vat); add vat separately
      sell += parseFloat(rp.sellingPrice || 0) + parseFloat(rp.vatTax || rp.vat || 0);
    });
  } else if (p.productType === "Transfer") {
    cost += parseFloat(pricing.costPrice || 0);
    // sellingPrice = costPrice + markup (stored WITHOUT vat); add vat separately
    sell += parseFloat(pricing.sellingPrice || 0) + parseFloat(pricing.vatTax || pricing.vat || 0);
  } else {
    // Generic (Insurance, Package, CarRental, Visa, Cruise, Incoming) — pricing is
    // stored per-traveler in pricing.travelerPricing[] (sellingPrice already includes
    // VAT, computed server-side as cost + markup + vat). Mirror the backend
    // recalculateBookingTotals so the breakdown, KPIs and supplier payables agree
    // with the booking grandTotal. Fall back to flat pricing for legacy records.
    const tps = pricing.travelerPricing || [];
    if (tps.length > 0) {
      tps.forEach((tp) => {
        if (skip(tp.travelerId)) return;
        cost += parseFloat(tp.costPrice || 0);
        // sellingPrice is stored VAT-inclusive (cost + markup + vat); derive it if a
        // legacy row is missing it so the total never silently drops to 0.
        sell += tp.sellingPrice != null
          ? parseFloat(tp.sellingPrice)
          : parseFloat(tp.costPrice || 0) + parseFloat(tp.markup || 0) + parseFloat(tp.vatTax || 0);
      });
    } else {
      cost += parseFloat(pricing.costPrice || 0);
      sell += parseFloat(pricing.sellingPrice || 0) + parseFloat(pricing.vatTax || pricing.vat || 0);
    }
  }
  return { cost, sell };
};

const mapApiBookingDetail = (b) => {
  if (!b) return null;
  const products = b.products || [];
  const travelers = b.travelers || [];
  const payments = b.payments || [];

  const assignments = b.assignments || [];
  // Normalize productDetails to always be an object (Sequelize may return JSON columns as strings)
  const normalizedProducts = products.map((p) => ({
    ...p,
    productDetails: typeof p.productDetails === 'string' ? JSON.parse(p.productDetails) : (p.productDetails || {}),
  }));
  const cancelledTravelerIds = new Set((b.travelers || []).filter((t) => t.status === "Cancelled").map((t) => String(t.id)));
  const components = normalizedProducts.map((p) => {
    const { cost, sell } = sumProductPricing(p, cancelledTravelerIds);
    const travelersCount = assignments.filter((a) => a.productId === p.id && !cancelledTravelerIds.has(String(a.travelerId))).length;
    return {
      id: p.id,
      type: p.productType,
      iconKey: productIconKey(p.productType),
      status: p.productStatus || "Pending",
      supplier: p.supplierName || (p.supplierId ? `Supplier #${p.supplierId}` : "—"),
      supplierName: p.supplierName || "",
      costPrice: cost,
      sellingPrice: sell,
      travelersCount,
      fields: buildProductFields(p),
      _raw: p,
    };
  });

  // Build breakdown from products + SSRs. Cancelled products / cancelled-traveler
  // rows / cancelled SSRs are EXCLUDED from the live booking value — their original
  // price no longer counts (the backend recalculates the same way); the cancellation
  // impact is shown in the Cancellation Effects panel instead.
  const ssrs = (b.ssrs || b.bookingSSRs || []).filter((s) => !s.deletedAt && !s.deleted_at);
  const activeComponents = components.filter((c) => c.status !== "Cancelled");
  const cancelledComponents = components.filter((c) => c.status === "Cancelled");
  // Cancellation economics, folded into the headline totals to mirror the backend
  // recalc: the Sunsky fee (VAT-inclusive) is retained revenue → adds to selling;
  // the supplier cancellation cost is a real booking cost → adds to cost; the VAT
  // inside the fee flows via b.totalVatTax. Whole-product cancels store a top-level
  // {cost,fee,vat}; partial flight cancels accumulate directionFees[].
  const cancEffect = (raw) => {
    const c = raw?.productDetails?.cancellation || {};
    let fee = parseFloat(c.fee || 0), vat = parseFloat(c.vat || 0), cost = parseFloat(c.cost || 0);
    for (const df of c.directionFees || []) {
      fee += parseFloat(df.fee || 0); vat += parseFloat(df.vat || 0); cost += parseFloat(df.cost || 0);
    }
    return { fee, vat, cost };
  };
  // Cancelled products keep their ORIGINAL contracted value in Total Selling Price; the
  // customer refund (credit note) nets grandTotal / margin down to what the client bears
  // = supplier cost + Sunsky cancellation fee. The fee is VAT-INCLUSIVE (VAT sits inside
  // it), so `vat` is NOT added to the penalty — that would double-count it. Net effect:
  // Gross Margin contribution = the Sunsky CANC. FEE (our earnings), the supplier cost is
  // passed through, and the fee's VAT still reports separately in VAT Liability.
  // Mirrors recalculateBookingTotals so the frontend figures match the stored totals.
  // Product-level cancellation fold: represent the cancelled product by its RETAINED
  // value (cost + fee we keep), NOT the original gross. This makes computedSelling
  // equal the customer's post-cancellation obligation, so a full-cancel booking with
  // e.g. €165 original → €124.39 retained shows Total Selling Price €124.39 and
  // Outstanding = paid − 124.39 (negative when paid > retained ⇒ refund due).
  let prodCancSell = 0, prodCancCost = 0, cancellationCredit = 0;
  cancelledComponents.forEach((c) => {
    const e = cancEffect(c._raw);
    prodCancSell += (e.cost || 0) + (e.fee || 0);
    prodCancCost += e.cost;
  });
  // Still-active flight with cancelled direction(s): the cancelled leg's own price is
  // already out of sumProductPricing (cancelFlightLegs moved the row to
  // cancellation.removedPricing). What the client still bears is the retained penalty
  // = Sunsky fee + supplier cancellation cost — same as the cancelled-product fold
  // above. Adding only the fee left the supplier cancellation cost out of the client's
  // obligation, overstating the refund shown as Outstanding.
  activeComponents.forEach((c) => {
    if (c._raw?.productType === "Flight") {
      for (const df of c._raw?.productDetails?.cancellation?.directionFees || []) {
        prodCancSell += parseFloat(df.fee || 0) + parseFloat(df.cost || 0);
        prodCancCost += parseFloat(df.cost || 0);
      }
    }
  });
  // Traveler-level cancellation fold: the cancelled traveler's pricing share was
  // already dropped by sumProductPricing; fold back the retained fee + supplier cost.
  let travCancSell = 0, travCancCost = 0;
  (b.travelerModifications || []).forEach((m) => {
    if (m.actionType === 'CANCEL_TRAVELER' && cancelledTravelerIds.has(String(m.travelerId))) {
      travCancSell += parseFloat(m.agencyFee || 0) + parseFloat(m.supplierCost || 0);
      travCancCost += parseFloat(m.supplierCost || 0);
      cancellationCredit += parseFloat(m.refundAmount || 0);
    }
  });
  const activeSsrs = ssrs.filter((s) => s.status !== "Cancelled" && !cancelledTravelerIds.has(String(s.travelerId)) && !cancelledComponents.some((c) => c.id === s.productId));
  const hasCancellations = cancelledComponents.length > 0 || cancelledTravelerIds.size > 0 || activeSsrs.length !== ssrs.length;
  const ssrBreakdown = activeSsrs.length > 0
    ? [{
      label: "SSR",
      cost: activeSsrs.reduce((sum, s) => sum + parseFloat(s.costPrice || 0) * parseInt(s.quantity || 1), 0),
      sell: activeSsrs.reduce((sum, s) => {
        const qty = parseInt(s.quantity || 1);
        // SSR Fee is VAT-inclusive: sellingPrice (cost + fee) already contains the VAT.
        return sum + parseFloat(s.sellingPrice || 0) * qty;
      }, 0),
    }]
    : [];
  // Breakdown rows are recomputed cancellation-aware: cancelled products are
  // dropped (activeComponents) and, for still-active products, a cancelled
  // traveler's per-traveler row is excluded. The rows therefore SUM EXACTLY to the
  // Subtotal/Total below — the breakdown always reconciles to the headline figures.
  const breakdown = [
    ...activeComponents.map((c) => {
      const { cost, sell } = sumProductPricing(c._raw, cancelledTravelerIds);
      return { label: c.type, cost, sell };
    }),
    ...ssrBreakdown,
    ...((prodCancSell || prodCancCost) ? [{ label: "Cancelled services (retained)", cost: prodCancCost, sell: prodCancSell }] : []),
    ...((travCancSell || travCancCost) ? [{ label: "Cancellation fees (retained)", cost: travCancCost, sell: travCancSell }] : []),
  ];
  const computedCost = breakdown.reduce((sum, r) => sum + (r.cost || 0), 0);
  const computedSelling = breakdown.reduce((sum, r) => sum + (r.sell || 0), 0);
  // When a cancellation exists, use the self-consistent computed totals (which
  // exclude cancelled products + cancelled-traveler rows) so the figures are correct
  // regardless of whether the backend has re-costed yet; for live bookings keep the
  // existing computed-first behaviour.
  const totalCost = hasCancellations ? computedCost : (computedCost || parseFloat(b.totalCost || 0));
  const totalSelling = hasCancellations ? computedSelling : (computedSelling || parseFloat(b.totalAmount || 0));
  // netSelling nets out the cancellation credit (customer refund). Total Selling Price
  // shows the gross; margin & net payable use net. cancellationCredit is 0 without
  // cancellations, so margin/marginPct are unchanged for live bookings.
  const netSelling = totalSelling - cancellationCredit;
  const margin = netSelling - totalCost;
  const marginPct = netSelling > 0 ? (margin / netSelling) * 100 : 0;

  const confirmations = normalizedProducts.map((p) => {
    const pd = typeof p.productDetails === 'string' ? JSON.parse(p.productDetails) : (p.productDetails || {});
    return {
      id: p.id,
      component: p.productType,
      iconKey: productIconKey(p.productType),
      supplier: p.supplierName || (p.supplierId ? `Supplier #${p.supplierId}` : "—"),
      ref: p.supplierReference || pd.supplierReservationNumber || pd.supplierRefNumber || "—",
      confirmedAt: p.productStatus === "Confirmed" ? formatDate(p.updatedAt) : "—",
      confirmedBy: p.productStatus === "Confirmed" ? "System" : "Pending",
    };
  });

  const clientPayments = payments
    .filter((p) => p.paymentDirection !== "Outgoing" && p.direction !== "Outgoing")
    .map((p) => ({
      date: formatDate(p.paymentDate || p.createdAt),
      method: p.paymentMethod || p.method || "—",
      amount: parseFloat(p.amount || 0),
      status: p.paymentStatus === "Completed" ? "Paid" : (p.paymentStatus || "Pending"),
      ref: p.transactionReference || p.referenceNumber || "—",
    }));

  return {
    ref: b.bookingReference,
    id: b.id,
    createdAt: formatDate(b.bookingDate || b.createdAt),
    createdBy: b.createdBy ? `User #${b.createdBy}` : "—",
    invoiceNumber: `INV-${b.bookingReference || b.id}`,
    origin: b.bookingType || "Offline",
    bookingStatus: b.status || "Pending",
    paymentStatus: b.paymentStatus || "Unpaid",
    saleType: "PACKAGE_TRAVEL",
    totalPrice: parseFloat(b.grandTotal || b.totalAmount || 0),
    // Customer refund (credit note) from cancellations — nets Total Selling Price down
    // to what the client actually owes when computing outstanding. 0 for live bookings.
    cancellationCredit,
    customer: b.customerType === "Company"
      ? {
        type: "Company",
        name: b.customerName || "—",
        tradingName: b.customerProfile?.tradingName || b.customerName || "—",
        legalName: b.customerProfile?.legalName || "—",
        vatNumber: b.customerProfile?.vatNumber || "—",
        industry: b.customerProfile?.industry || "—",
        website: b.customerProfile?.website || "—",
        email: b.customerEmail || b.customerProfile?.invoiceEmail || "—",
        phone: b.customerPhone || b.customerProfile?.primaryContactPhone || "—",
        street: b.customerProfile?.street || "—",
        houseNumber: b.customerProfile?.houseNumber || "—",
        boxNumber: b.customerProfile?.boxNumber || "—",
        city: b.customerProfile?.city || "—",
        postalCode: b.customerProfile?.postalCode || "—",
        country: b.customerProfile?.country || "—",
        invoiceEmail: b.customerProfile?.invoiceEmail || "—",
        paymentTerms: b.customerProfile?.paymentTerms || "—",
        invoicingAddress: b.customerProfile?.invoicingAddress || "—",
        contactFirstName: b.customerProfile?.primaryContactFirstName || "—",
        contactLastName: b.customerProfile?.primaryContactLastName || "—",
        contactEmail: b.customerProfile?.primaryContactEmail || "—",
        contactPhone: b.customerProfile?.primaryContactPhone || "—",
        emergencyPhone: b.emergencyContact?.phone || "—",
        contactRole: b.customerProfile?.primaryContactRole || "—",
        contactLanguage: b.customerProfile?.preferredLanguage || "—",
      }
      : {
        type: b.customerType || "Private",
        name: b.customerName || "—",
        firstName: b.customerProfile?.firstName || (b.customerName || "").split(" ")[0] || "",
        lastName: b.customerProfile?.lastName || (b.customerName || "").split(" ").slice(1).join(" ") || "",
        email: b.customerEmail || b.customerProfile?.email || "—",
        phone: b.customerPhone || b.customerProfile?.phone || "—",
        // Who to ring when the customer cannot be reached. The booking carries the number
        // given for THIS trip and the profile carries the current one; the booking wins,
        // because during a trip the number that was valid for it is the one that matters.
        emergencyPhone: b.emergencyContact?.phone || b.customerProfile?.emergencyPhone || "—",
        dateOfBirth: b.customerProfile?.dateOfBirth ? formatDate(b.customerProfile.dateOfBirth) : "—",
        gender: b.customerProfile?.gender
          ? b.customerProfile.gender.charAt(0).toUpperCase() + b.customerProfile.gender.slice(1).toLowerCase().replace(/_/g, " ")
          : "—",
        nationality: b.customerProfile?.nationality || "—",
        language: b.customerProfile?.preferredLanguage || "—",
        street: b.customerProfile?.street || "—",
        houseNumber: b.customerProfile?.houseNumber || "—",
        boxNumber: b.customerProfile?.boxNumber || "—",
        city: b.customerProfile?.city || "—",
        postalCode: b.customerProfile?.postalCode || "—",
        country: b.customerProfile?.country || "—",
      },
    components,
    travelers: travelers.map((t) => ({
      id: t.id,
      firstName: t.firstName || "",
      lastName: t.lastName || "",
      name: `${t.firstName || ""} ${(t.lastName || "").toUpperCase()}`.trim() || "—",
      dob: formatDate(t.dateOfBirth),
      rawDob: t.dateOfBirth || "",
      type: t.type === "ADT" ? "Adult" : t.type === "CHD" ? "Child" : t.type === "INF" ? "Infant" : (t.type || "—"),
      gender: t.gender ? t.gender.charAt(0).toUpperCase() + t.gender.slice(1).toLowerCase() : "—",
      passport: t.passportNumber || "—",
      status: t.status || "Confirmed",
      docs: !!t.passportNumber,
    })),
    assignments: (b.assignments || []).map((a) => {
      const product = normalizedProducts.find((p) => p.id === a.productId);
      let hasPricing = false;
      if (product) {
        const pricing = product.productDetails?.pricing || {};
        if (product.productType === "Flight") {
          // Flight pricing is per-traveler — check if THIS traveler has a pricing entry
          hasPricing = (pricing.travelerPricing || []).some(
            (tp) => tp.travelerId === a.travelerId && (parseFloat(tp.sellingPrice || tp.clientPrice || 0) > 0 || parseFloat(tp.netPrice || 0) > 0)
          );
        }
        // Hotel and Transfer pricing is per-product, not per-traveler, so
        // the traveler-product combination never has its own pricing entry.
        // Assignment removal is governed by invoice/document impact, not product pricing.
      }
      return { id: a.id, travelerId: a.travelerId, productId: a.productId, assignmentType: a.assignmentType || null, roomNumber: a.roomNumber ?? null, seatNumber: a.seatNumber ?? null, segmentId: a.segmentId ?? null, hasPricing };
    }),
    pricing: {
      sellingPrice: totalSelling,
      costPrice: totalCost,
      margin,
      marginPct: parseFloat(marginPct.toFixed(2)),
      vatRegime: "TOMS",
      vatCountry: "—",
      breakdown,
      vat: [],
      tomsMargin: margin,
      tomsVat: parseFloat(b.totalVatTax || 0),
    },
    // Drop soft-deleted SSRs defensively so a removed SSR can never resurface in
    // pricing/itinerary if a stale (cached) booking payload slips one through.
    ssrs: (b.ssrs || []).filter((s) => !s.deletedAt && !s.deleted_at).map((s) => ({
      id: s.id,
      bookingId: s.bookingId,
      travelerId: s.travelerId,
      productId: s.productId,
      segmentId: s.segmentId,
      ssrCode: s.ssrCode,
      ssrName: s.ssrName,
      ssrCategory: s.ssrCategory,
      ssrDescription: s.ssrDescription,
      quantity: s.quantity || 1,
      costPrice: parseFloat(s.costPrice || 0),
      sellingPrice: parseFloat(s.sellingPrice || 0),
      markup: parseFloat(s.markup || 0),
      tax: parseFloat(s.tax || 0),
      vatTax: parseFloat(s.vatTax || 0),
      status: s.status || "Requested",
    })),
    confirmations,
    payments: { client: clientPayments, supplier: [] },
    documents: [],
    auditLog: [],
    adjustments: Array.isArray(b.adjustments) ? b.adjustments : [],
    // Cancellation/modification effects surfaced from the booking GET so the
    // Travelers, Assignments and Pricing tabs can reflect what was cancelled.
    travelerModifications: Array.isArray(b.travelerModifications) ? b.travelerModifications : [],
    productModifications: Array.isArray(b.productModifications) ? b.productModifications : [],
  };
};

// ── Type Picker Modal ─────────────────────────────────────────────────────
const ADD_COMPONENT_TYPES = [
  { key: "Flight", label: "Flight", desc: "Outbound, return, or multi-leg segments", Icon: Plane, bg: "#eef9f3", color: "#0d7a4f", border: "#c3e8d4" },
  { key: "Hotel", label: "Hotel / Accommodation", desc: "Hotel stay with check-in/out dates", Icon: Hotel, bg: "#f5eeff", color: "#7c3aed", border: "#ddd0f7" },
  { key: "Transfer", label: "Transfer", desc: "Ground transport between locations", Icon: Bus, bg: "#eef3ff", color: "#3b5bdb", border: "#d0dafb" },
  { key: "Insurance", label: "Travel Insurance", desc: "Travel and cancellation insurance", Icon: Shield, bg: "#fef3e2", color: "#b06e0a", border: "#fde4b0" },
  { key: "Package", label: "Package Holiday", desc: "Pre-packaged offline holiday product", Icon: Package, bg: "#fff0f0", color: "#c42b2b", border: "#f5c2c2" },
  { key: "CarRental", label: "Car Rental", desc: "Rental vehicle for the travel period", Icon: Car, bg: "#e8f5ee", color: "#0d7a4f", border: "#c3e8d4" },
  { key: "Visa", label: "Visa Service", desc: "Visa application and processing", Icon: FileText, bg: "#f5f6f8", color: "#64748b", border: "#e2e5ea" },
  { key: "Cruise", label: "Cruise", desc: "Cruise line booking with cabin allocation", Icon: Ship, bg: "#eef6ff", color: "#2563eb", border: "#bfdbfe" },
];

const TypePickerModal = ({ open, onClose, onSelect }) => (
  <BaseModal open={open} onClose={onClose} title="Add Component — Select Type" width="640px">
    <div className={styles.typePickerGrid}>
      {ADD_COMPONENT_TYPES.map((ct) => {
        const TIcon = ct.Icon;
        return (
          <div key={ct.key} className={styles.typePickerCard} onClick={() => onSelect(ct.key)}>
            <div className={styles.typePickerIcon} style={{ background: ct.bg, color: ct.color, border: `1.5px solid ${ct.border}` }}>
              <TIcon size={18} />
            </div>
            <div className={styles.typePickerText}>
              <div className={styles.typePickerLabel}>{ct.label}</div>
              <div className={styles.typePickerDesc}>{ct.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  </BaseModal>
);

// ── Product Chips ──────────────────────────────────────────────────────────
/* Soft per-product accents (matches itinerary + assignment palette) */
const PRODUCT_CHIP_STYLE = {
  Flight:    { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  Hotel:     { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
  Transfer:  { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
  CarRental: { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
  Visa:      { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  Package:   { bg: "#eef2ff", color: "#4f46e5", border: "#c7d2fe" },
  Insurance: { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  Cruise:    { bg: "#f0f9ff", color: "#0284c7", border: "#bae6fd" },
};
const ProductChips = ({ types }) => {
  const unique = [...new Set(types)];
  return (
    <div className={styles.productChips}>
      {unique.map((key) => {
        const Icon = PRODUCT_ICON_MAP[key] || FileText;
        const cs = PRODUCT_CHIP_STYLE[key] || { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };
        return (
          <span key={key} className={styles.productChip} style={{ background: cs.bg, color: cs.color, borderColor: cs.border }}>
            <Icon size={10} />
            {key}
          </span>
        );
      })}
    </div>
  );
};

const BookingDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("customer");

  const navState = location.state?.booking;
  const bookingId = navState?.id ?? params.ref;

  const { data: apiBooking, loading, error, refetch } = useGetBookingDetail(bookingId);
  const { data: activityLog, refetch: refetchActivity } = useGetBookingActivity(bookingId);
  const { execute: confirmBookingApi, loading: confirmingBooking } = useConfirmBooking();
  const { execute: submitBookingApi, loading: submittingBooking } = useSubmitBooking();
  const { execute: addProductsApi } = useAddBookingProducts();
  const { execute: updateProductApi } = useUpdateBookingProduct();
  const { execute: deleteProductApi, loading: deletingProduct } = useDeleteBookingProduct();
  const { execute: deleteAssignmentApi, loading: deletingAssignment } = useDeleteBookingAssignment();
  const { execute: createAssignmentsApi, loading: creatingAssignment } = useCreateBookingAssignments();
  const { execute: updateAssignmentApi } = useUpdateBookingAssignment();
  const { execute: fetchAssignmentLockStatus, data: assignmentLockData } = useGetAssignmentLockStatus();
  const { execute: updatePrivateCustomerApi, loading: savingCustomer } = useUpdatePrivateCustomer();
  const { execute: updateProfessionalCustomerApi, loading: savingProfCustomer } = useUpdateProfessionalCustomer();
  const { execute: syncCustomerSnapshotApi } = useSyncCustomerSnapshot();
  const { execute: changeBookingCustomerApi, loading: changingCustomer } = useChangeBookingCustomer();
  const { execute: setFlightPricingApi } = useSetFlightPricing();
  const { execute: setHotelPricingApi } = useSetHotelPricing();
  const { execute: setTransferPricingApi } = useSetTransferPricing();
  const { execute: setGenericPricingApi } = useSetGenericPricing();
  const { execute: setSSRPricingApi } = useSetSSRPricing();
  const { execute: setAdjustmentsApi, loading: savingAdjustments } = useSetAdjustments();
  const { execute: generateVouchersApi, loading: generatingDocs } = useGenerateVouchers();
  const { execute: calculateVATApi } = useCalculateVAT();
  // Post-booking product modifications are fetched here (not only inside the Pricing
  // tab) so the header strip + Overview tab report the same total & outstanding as the
  // Pricing & VAT tab. Without this, those tabs ignored post-booking modifications and
  // the outstanding balance only ever looked correct on the Pricing tab.
  const { execute: fetchProductModificationsTotals } = useGetProductModifications();
  const [productModifications, setProductModifications] = useState([]);

  const mapped = useMemo(() => mapApiBookingDetail(apiBooking), [apiBooking]);

  // Finance payments — the source of truth for what the client has actually paid
  // (separate from the old BookingPayment model embedded in the booking detail)
  const bookingRefForPayments = mapped?.ref || "";
  const { data: financePayments, refetch: refetchFinancePayments } = useGetBookingPayments(bookingRefForPayments);

  const b = useMemo(() => {
    if (mapped) {
      return {
        ...mapped,
        auditLog: Array.isArray(activityLog?.logs) ? activityLog.logs.map((a) => {
          let parsedPayload = null;
          if (a.description) {
            try { parsedPayload = JSON.parse(a.description); } catch (_) { /* plain text */ }
          }
          return {
            id: a.id,
            action: a.action,
            user: a.performedBy ? `User #${a.performedBy}` : "System",
            timestamp: a.performedAt || a.createdAt || null,
            note: parsedPayload ? (parsedPayload.description || "") : (a.description || ""),
            isSystem: !a.performedBy,
            productType: a.productType || null,
            oldValue: parsedPayload?.oldValue ?? null,
            newValue: parsedPayload?.newValue ?? null,
            financialImpact: parsedPayload?.financialImpact || null,
            vatImpact: parsedPayload?.vatImpact || null,
            documentImpact: parsedPayload?.documentImpact || null,
            reason: parsedPayload?.reason || null,
            meta: parsedPayload?.meta || null,
          };
        }) : [],
      };
    }
    return {
      ...BOOKING,
      ...(navState ?? {}),
      customer: navState?.customer ?? BOOKING.customer,
      travelers: navState?.travelers ?? BOOKING.travelers,
      assignments: navState?.assignments ?? BOOKING.assignments,
      pricing: navState?.pricing ?? BOOKING.pricing,
      confirmations: navState?.confirmations ?? BOOKING.confirmations,
      payments: navState?.payments ?? BOOKING.payments,
      documents: navState?.documents ?? BOOKING.documents,
      auditLog: navState?.auditLog ?? BOOKING.auditLog,
      invoiceNumber: navState?.invoiceNumber ?? `INV-${(navState?.ref ?? BOOKING.ref).split("-").slice(-1)[0]}`,
      createdBy: navState?.createdBy ?? BOOKING.createdBy,
      saleType: navState?.saleType ?? BOOKING.saleType,
      origin: navState?.origin ?? BOOKING.origin,
    };
  }, [mapped, activityLog, navState]);

  const [components, setComponents] = useState([]);
  const [bookingStatus, setBookingStatus] = useState("Pending");
  const [assignments, setAssignments] = useState([]);

  const refetchAll = useCallback(() => {
    refetch?.();
    refetchActivity?.();
    if (bookingId) fetchAssignmentLockStatus(bookingId);
  }, [refetch, refetchActivity, bookingId, fetchAssignmentLockStatus]);

  useEffect(() => {
    setComponents(b.components || []);
    setBookingStatus(b.bookingStatus || "Pending");
    setAssignments(b.assignments || []);
  }, [b.components, b.bookingStatus, b.assignments]);

  // Keep the parent-level product modifications in sync. Re-runs whenever the booking
  // is refetched (apiBooking identity changes) — e.g. after a modification is added or
  // removed in the Pricing tab — so the header/Overview totals stay current.
  useEffect(() => {
    if (!bookingId) { setProductModifications([]); return; }
    fetchProductModificationsTotals(bookingId, null).then((res) => {
      if (res.success && Array.isArray(res.data)) setProductModifications(res.data);
    });
  }, [bookingId, apiBooking, fetchProductModificationsTotals]);

  // Re-fetch audit log every time the user switches to the Audit Log tab
  // so new activity entries created while on other tabs are always visible.
  useEffect(() => {
    if (activeTab === "audit") {
      refetchActivity();
    }
  }, [activeTab, refetchActivity]);

  // Fetch assignment lock status when switching to assignments tab
  useEffect(() => {
    if (activeTab === "assignments" && bookingId) {
      fetchAssignmentLockStatus(bookingId);
    }
  }, [activeTab, bookingId, fetchAssignmentLockStatus]);
  const [addTypePickerOpen, setAddTypePickerOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalType, setAddModalType] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [productEditScreen, setProductEditScreen] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingComponent, setDeletingComponent] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [adjustments, setAdjustmentsState] = useState([]);
  useEffect(() => {
    if (mapped?.adjustments !== undefined) setAdjustmentsState(mapped.adjustments);
  }, [mapped?.adjustments]);
  // Tracks whether local adjustments differ from what's saved on the server, so
  // the Save button stays available even when the list becomes empty (e.g. the
  // last adjustment was removed) — otherwise the removal can never be persisted.
  const adjustmentsDirty = useMemo(
    () => JSON.stringify(adjustments || []) !== JSON.stringify(mapped?.adjustments || []),
    [adjustments, mapped?.adjustments]
  );
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [editingAdjustmentIdx, setEditingAdjustmentIdx] = useState(null);
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [customerEditError, setCustomerEditError] = useState(null);
  const [changeCustomerOpen, setChangeCustomerOpen] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);

  const allComponentsConfirmed = components.length > 0 && components.every((c) => c.status === "Confirmed");
  const isBookingConfirmed = bookingStatus === "Confirmed";
  const canConfirm = allComponentsConfirmed && !isBookingConfirmed;
  const canLock = isBookingConfirmed && !isLocked;

  const RICH_FORM_TYPES = ["Flight", "Hotel", "Transfer", "Insurance", "Package", "CarRental", "Visa", "Cruise"];

  const handleAddComponent = async (component) => {
    if (bookingId) {
      // Build a product object the API mapper understands
      const product = {
        type: component.type,
        status: component.status || 'Pending',
        supplierRef: component.supplier,
        costPrice: component.costPrice,
        sellingPrice: component.sellingPrice,
      };
      // Flatten AddComponentModal field values onto the product
      if (component.fields) {
        const FIELD_DEFS_MAP = {
          Insurance: ['policyType', 'coverage', 'validFrom', 'validTo', 'policyRef'],
          Package: ['packageName', 'destination', 'departure', 'return', 'passengers', 'boardType', 'supplierRef'],
          CarRental: ['vehicleType', 'pickupLocation', 'dropoffLocation', 'pickupDate', 'dropoffDate', 'driver', 'supplierRef'],
          Visa: ['visaType', 'destinationCountry', 'travelers', 'applicationDate', 'visaStatus', 'supplierRef'],
        };
        const keys = FIELD_DEFS_MAP[component.type] || [];
        component.fields.forEach((f) => {
          const key = keys.find((k) => {
            const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
            return f.label === label || k === f.label;
          });
          if (key && f.value && f.value !== '—') product[key] = f.value;
        });
        // Also map by label directly for simpler matching
        const labelToKey = {
          'PNR': 'airlinePnr', 'Ticket Number': 'ticketNumber',
          'Policy Type': 'policyType', 'Coverage': 'coverage', 'Valid From': 'validFrom',
          'Valid To': 'validTo', 'Policy Ref': 'policyRef', 'Package Name': 'packageName',
          'Destination': 'destination', 'Departure Date': 'departure', 'Return Date': 'return',
          'Passengers': 'passengers', 'Board Type': 'boardType', 'Supplier Ref': 'supplierRef',
          'Vehicle Type': 'vehicleType', 'Pick-up Location': 'pickupLocation',
          'Drop-off Location': 'dropoffLocation', 'Pick-up Date': 'pickupDate',
          'Drop-off Date': 'dropoffDate', 'Driver Name': 'driver',
          'Visa Type': 'visaType', 'Destination Country': 'destinationCountry',
          'Travelers': 'travelers', 'Application Date': 'applicationDate', 'Status': 'visaStatus',
        };
        component.fields.forEach((f) => {
          const key = labelToKey[f.label];
          if (key && f.value && f.value !== '—') product[key] = f.value;
        });
      }
      // Ensure Flight products have required defaults for mapUiProductToApi
      if (component.type === 'Flight') {
        product.flightType = product.flightType || 'OW';
        product.flightPricingMode = product.flightPricingMode || 'FULL';
        product.legs = product.legs || [];
      }
      const res = await addProductsApi(bookingId, [product]);
      if (!res.success) {
        showToast(res.message || "Failed to add component", "error");
        return;
      }
      showToast(`${component.type} added successfully`, "success");
      refetchAll();
    } else {
      setComponents((prev) => [...prev, component]);
    }
  };

  const handleEditComponent = async (component) => {
    if (bookingId && component._raw?.id) {
      // Build product for API
      const product = {
        type: component.type,
        status: component.status || 'Pending',
        supplierRef: component.supplier,
        costPrice: component.costPrice,
        sellingPrice: component.sellingPrice,
      };
      const labelToKey = {
        'PNR': 'airlinePnr', 'Ticket Number': 'ticketNumber',
        'Policy Type': 'policyType', 'Coverage': 'coverage', 'Valid From': 'validFrom',
        'Valid To': 'validTo', 'Policy Ref': 'policyRef', 'Package Name': 'packageName',
        'Destination': 'destination', 'Departure Date': 'departure', 'Return Date': 'return',
        'Passengers': 'passengers', 'Board Type': 'boardType', 'Supplier Ref': 'supplierRef',
        'Vehicle Type': 'vehicleType', 'Pick-up Location': 'pickupLocation',
        'Drop-off Location': 'dropoffLocation', 'Pick-up Date': 'pickupDate',
        'Drop-off Date': 'dropoffDate', 'Driver Name': 'driver',
        'Visa Type': 'visaType', 'Destination Country': 'destinationCountry',
        'Travelers': 'travelers', 'Application Date': 'applicationDate', 'Status': 'visaStatus',
      };
      if (component.fields) {
        component.fields.forEach((f) => {
          const key = labelToKey[f.label];
          if (key && f.value && f.value !== '—') product[key] = f.value;
        });
      }
      if (component.type === 'Flight') {
        product.flightType = product.flightType || 'OW';
        product.flightPricingMode = product.flightPricingMode || 'FULL';
        product.legs = product.legs || [];
      }
      const res = await updateProductApi(bookingId, component._raw.id, product);
      if (!res.success) {
        showToast(res.message || "Failed to update component", "error");
        return;
      }
      if (res.bookingAutoConfirmed) {
        setBookingStatus("Confirmed");
        showToast(`${component.type} updated — booking auto-confirmed (all products confirmed)`, "success");
      } else {
        showToast(`${component.type} updated successfully`, "success");
      }
      refetchAll();
    } else {
      setComponents((prev) => prev.map((c) => (c.id === component.id ? component : c)));
    }
  };

  const handleDeleteComponent = async () => {
    if (!deletingComponent) return;
    if (bookingId && deletingComponent._raw?.id) {
      const res = await deleteProductApi(bookingId, deletingComponent._raw.id);
      if (!res.success) {
        showToast(res.message || "Failed to delete component", "error");
        setDeleteModalOpen(false);
        setDeletingComponent(null);
        return;
      }
      showToast("Component removed successfully", "success");
      refetchAll();
    }
    setComponents((prev) => prev.filter((c) => c.id !== deletingComponent.id));
    setDeleteModalOpen(false);
    setDeletingComponent(null);
  };

  const handleTypePickerSelect = (typeKey) => {
    setAddTypePickerOpen(false);
    setProductEditScreen({ type: typeKey, data: null, componentId: null });
  };

  const openEdit = (component) => {
    const formData = component._raw ? mapApiProductToForm(component._raw) : null;
    setProductEditScreen({ type: component.type, data: formData, componentId: component.id });
  };

  const handleProductFormSave = async (updatedProduct) => {
    if (!bookingId) return;
    setSavingProduct(true);
    try {
      let res;
      if (productEditScreen?.componentId) {
        const serverId = components.find((c) => c.id === productEditScreen.componentId)?._raw?.id;
        if (!serverId) {
          // No server id → the PUT below would be skipped. Never fall through to a
          // "saved" toast when nothing was persisted (that reads as a silent data loss).
          showToast("Could not save — product reference was lost. Please reopen the product and try again.", "error");
          setSavingProduct(false);
          return;
        }
        res = await updateProductApi(bookingId, serverId, updatedProduct);
        if (!res.success) {
          showToast(res.message || "Failed to update product", "error");
          setSavingProduct(false);
          return;
        }
      } else {
        res = await addProductsApi(bookingId, [updatedProduct]);
        if (!res.success) {
          showToast(res.message || "Failed to add product", "error");
          setSavingProduct(false);
          return;
        }
      }
      if (res?.bookingAutoConfirmed) {
        setBookingStatus("Confirmed");
        showToast("Product updated — booking auto-confirmed (all products confirmed)", "success");
      } else {
        showToast(productEditScreen?.componentId ? "Product updated successfully" : "Product added successfully", "success");
      }
      setProductEditScreen(null);
      refetchAll();
    } finally {
      setSavingProduct(false);
    }
  };

  const openDelete = (component) => { setDeletingComponent(component); setDeleteModalOpen(true); };

  const customerProfileData = useMemo(() => {
    const cp = apiBooking?.customerProfile;
    if (!cp) return null;

    const normalizeGender = (g) => {
      if (!g) return "";
      const map = { MALE: "Male", FEMALE: "Female", OTHER: "Other", PREFER_NOT_TO_SAY: "Prefer not to say" };
      return map[g.toUpperCase()] || g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();
    };

    const normalizeStatus = (s) => {
      if (!s) return "Active";
      if (s.toUpperCase() === "ACTIVE") return "Active";
      if (s.toUpperCase() === "INACTIVE") return "Inactive";
      return s;
    };

    if (apiBooking?.customerType === "Company") {
      return {
        ...cp,
        companyName: cp.tradingName || cp.companyName || "",
        legalName: cp.legalName || "",
        vatNumber: cp.vatNumber || "",
        industry: cp.industry || "",
        website: cp.website || "",
        status: normalizeStatus(cp.status),
        address: {
          street: cp.street || "",
          houseNumber: cp.houseNumber || "",
          boxNumber: cp.boxNumber || "",
          city: cp.city || "",
          zip: cp.postalCode || cp.zip || "",
          country: cp.country || "",
        },
        primaryContact: {
          firstName: cp.primaryContactFirstName || "",
          lastName: cp.primaryContactLastName || "",
          email: cp.primaryContactEmail || "",
          phone: cp.primaryContactPhone || "",
          role: cp.primaryContactRole || "",
          language: cp.preferredLanguage || "",
        },
        invoicing: {
          email: cp.invoiceEmail || "",
          address: cp.invoicingAddress || "",
          paymentTerms: cp.paymentTerms || "30",
        },
      };
    }

    return {
      ...cp,
      firstName: cp.firstName || "",
      lastName: cp.lastName || "",
      email: cp.email || "",
      phone: cp.phone || "",
      dateOfBirth: cp.dateOfBirth || "",
      gender: normalizeGender(cp.gender),
      nationality: cp.nationality || "",
      language: cp.preferredLanguage || "",
      status: normalizeStatus(cp.status),
      address: {
        street: cp.street || "",
        houseNumber: cp.houseNumber || "",
        boxNumber: cp.boxNumber || "",
        city: cp.city || "",
        zip: cp.postalCode || cp.zip || "",
        country: cp.country || "",
      },
    };
  }, [apiBooking?.customerProfile, apiBooking?.customerType]);

  const handleCustomerSave = async (payload) => {
    setCustomerEditError(null);
    const cp = apiBooking?.customerProfile;
    const customerCode = cp?.customerCode || cp?.id || apiBooking?.customerId;
    const customerType = apiBooking?.customerType;
    if (!customerCode) return;

    // ── Build flat old/new diff snapshots before submitting ─────────────────
    // Flatten address fields with "address_" prefix so the diff table can show
    // them individually rather than as a nested object.
    const flattenProfile = (profile, isCompany) => {
      if (!profile) return {};
      if (isCompany) {
        const addr = profile.address || {};
        return {
          companyName:         profile.tradingName || profile.companyName || "",
          legalName:           profile.legalName || "",
          vatNumber:           profile.vatNumber || "",
          industry:            profile.industry || "",
          website:             profile.website || "",
          primaryFirstName:    profile.primaryContactFirstName || (profile.primaryContact?.firstName) || "",
          primaryLastName:     profile.primaryContactLastName  || (profile.primaryContact?.lastName)  || "",
          primaryEmail:        profile.primaryContactEmail     || (profile.primaryContact?.email)     || "",
          primaryPhone:        profile.primaryContactPhone     || (profile.primaryContact?.phone)     || "",
          primaryRole:         profile.primaryContactRole      || (profile.primaryContact?.role)      || "",
          language:            profile.preferredLanguage       || (profile.primaryContact?.language)  || "",
          invoiceEmail:        profile.invoiceEmail || (profile.invoicing?.email) || "",
          paymentTerms:        profile.paymentTerms || (profile.invoicing?.paymentTerms) || "",
          address_street:      addr.street      || profile.street      || "",
          address_houseNumber: addr.houseNumber || profile.houseNumber || "",
          address_boxNumber:   addr.boxNumber   || profile.boxNumber   || "",
          address_city:        addr.city        || profile.city        || "",
          address_zip:         addr.zip         || profile.postalCode  || "",
          address_country:     addr.country     || profile.country     || "",
        };
      }
      const addr = profile.address || {};
      return {
        firstName:           profile.firstName   || "",
        lastName:            profile.lastName    || "",
        email:               profile.email       || "",
        phone:               profile.phone       || "",
        dateOfBirth:         profile.dateOfBirth || "",
        gender:              profile.gender      || "",
        nationality:         profile.nationality || "",
        language:            profile.preferredLanguage || profile.language || "",
        address_street:      addr.street      || profile.street      || "",
        address_houseNumber: addr.houseNumber || profile.houseNumber || "",
        address_boxNumber:   addr.boxNumber   || profile.boxNumber   || "",
        address_city:        addr.city        || profile.city        || "",
        address_zip:         addr.zip         || profile.postalCode  || "",
        address_country:     addr.country     || profile.country     || "",
      };
    };

    const flattenPayload = (p, isCompany) => {
      if (!p) return {};
      if (isCompany) {
        const addr = p.address || {};
        const pc = p.primaryContact || {};
        return {
          companyName:         p.tradingName   || p.companyName || "",
          legalName:           p.legalName     || "",
          vatNumber:           p.vatNumber     || "",
          industry:            p.industry      || "",
          website:             p.website       || "",
          primaryFirstName:    p.primaryContactFirstName || pc.firstName || "",
          primaryLastName:     p.primaryContactLastName  || pc.lastName  || "",
          primaryEmail:        p.primaryContactEmail     || pc.email     || "",
          primaryPhone:        p.primaryContactPhone     || pc.phone     || "",
          primaryRole:         p.primaryContactRole      || pc.role      || "",
          language:            p.preferredLanguage       || pc.language  || "",
          invoiceEmail:        p.invoiceEmail || (p.invoicing?.email) || "",
          paymentTerms:        p.paymentTerms || (p.invoicing?.paymentTerms) || "",
          address_street:      addr.street      || "",
          address_houseNumber: addr.houseNumber || "",
          address_boxNumber:   addr.boxNumber   || "",
          address_city:        addr.city        || "",
          address_zip:         addr.zip         || "",
          address_country:     addr.country     || "",
        };
      }
      const addr = payload.address || {};
      return {
        firstName:           p.firstName   || "",
        lastName:            p.lastName    || "",
        email:               p.hasEmail === false ? "" : (p.email || ""),
        phone:               p.phone       || "",
        dateOfBirth:         p.dateOfBirth || "",
        gender:              p.gender      || "",
        nationality:         p.nationality || "",
        language:            p.language    || "",
        address_street:      addr.street      || "",
        address_houseNumber: addr.houseNumber || "",
        address_boxNumber:   addr.boxNumber   || "",
        address_city:        addr.city        || "",
        address_zip:         addr.zip         || "",
        address_country:     addr.country     || "",
      };
    };

    const isCompany = customerType === "Company";
    const oldFlat = flattenProfile(cp, isCompany);
    const newFlat = flattenPayload(payload, isCompany);

    // Detect which logical section was touched for the activity description
    const PERSONAL_KEYS = ["firstName", "lastName", "dateOfBirth", "gender", "nationality", "language", "companyName", "legalName", "vatNumber", "industry", "website"];
    const CONTACT_KEYS  = ["email", "phone", "invoiceEmail", "primaryEmail", "primaryPhone", "paymentTerms", "address_street", "address_houseNumber", "address_boxNumber", "address_city", "address_zip", "address_country"];
    const changedKeys = Object.keys(newFlat).filter((k) => (oldFlat[k] ?? "") !== (newFlat[k] ?? ""));
    const personalChanged = changedKeys.some((k) => PERSONAL_KEYS.includes(k));
    const contactChanged  = changedKeys.some((k) => CONTACT_KEYS.includes(k));
    const section = personalChanged && contactChanged
      ? "Customer Information"
      : personalChanged ? "Personal Information"
      : contactChanged  ? "Contact Information"
      : "Customer Information";

    try {
      // 1. Update the customer profile in the People system
      if (isCompany) {
        await updateProfessionalCustomerApi(customerCode, payload);
      } else {
        await updatePrivateCustomerApi(customerCode, payload);
      }

      // 2. Sync the booking's customerName/customerEmail/customerPhone snapshot
      //    and log a booking activity entry with full field-level diff
      if (bookingId) {
        await syncCustomerSnapshotApi(bookingId, {
          oldValue: oldFlat,
          newValue: newFlat,
          section,
        });
      }

      showToast("Customer updated successfully", "success");
      setCustomerEditOpen(false);
      refetchAll();
    } catch (err) {
      setCustomerEditError(err.message || "Failed to update customer");
      showToast(err.message || "Failed to update customer", "error");
    }
  };
  const handleChangeCustomer = async (customer) => {
    if (!bookingId || !customer) return;
    const newCustomerType = customer._type === "professional" ? "Company" : "Private";
    const customerId = customer.id;
    const oldCustomerType = apiBooking?.customerType;
    const result = await changeBookingCustomerApi(bookingId, customerId, newCustomerType);
    if (result.success) {
      showToast("Customer changed successfully", "success");
      if (result.warning) showToast(result.warning, "error");

      const customerTypeChanged = oldCustomerType && oldCustomerType !== newCustomerType;
      const hotelProducts = (apiBooking?.products || []).filter(
        (p) => p.productType?.toLowerCase() === "hotel"
      );

      if (customerTypeChanged && hotelProducts.length > 0) {
        showToast("Customer type changed — recalculating hotel VAT...", "success");
        for (const hp of hotelProducts) {
          try {
            const pricing = typeof hp.productDetails === "string"
              ? JSON.parse(hp.productDetails)?.pricing
              : hp.productDetails?.pricing;
            const rooms = pricing?.rooms || pricing?.roomPricing || [];
            const firstRoom = rooms[0] || {};
            await calculateVATApi({
              productId: hp.id,
              room_net_price: firstRoom.netPrice || firstRoom.costPrice || pricing?.costPrice || 0,
              markup: firstRoom.markup || (parseFloat(pricing?.sellingPrice || 0) - parseFloat(pricing?.costPrice || 0)) || 0,
            });
          } catch (err) {
            showToast(`Failed to recalculate VAT for hotel #${hp.id}`, "error");
          }
        }
        showToast("Hotel VAT recalculated for new customer type", "success");
      }

      setChangeCustomerOpen(false);
      refetchAll();
    } else {
      showToast(result.message || "Failed to change customer", "error");
    }
  };

  const handleConfirmBooking = async () => {
    if (bookingId) {
      const api = bookingStatus === "Draft" ? submitBookingApi : confirmBookingApi;
      const res = await api(bookingId);
      if (!res.success) {
        showToast(res.message || "Failed to confirm booking", "error");
        return;
      }
      refetchAll();
    }
    showToast("Booking confirmed successfully", "success");
    setBookingStatus("Confirmed");
    setConfirmModalOpen(false);
  };
  const handleLockBooking = () => { setIsLocked(true); setLockModalOpen(false); };
  const handleSaveAdjustment = (adjData) => {
    if (editingAdjustmentIdx !== null) {
      setAdjustmentsState((prev) => prev.map((a, i) => i === editingAdjustmentIdx ? { ...adjData, id: a.id } : a));
      showToast("Adjustment updated — click Save to apply", "success");
    } else {
      setAdjustmentsState((prev) => [...prev, { ...adjData, id: generateAdjustmentId() }]);
      showToast(`${adjData.type} "${adjData.title || ""}" added — click Save to apply`, "success");
    }
    setAdjustmentModalOpen(false);
    setEditingAdjustmentIdx(null);
  };
  const handleRemoveAdjustment = (idx) => {
    const removed = adjustments[idx];
    setAdjustmentsState((prev) => prev.filter((_, i) => i !== idx));
    showToast(`${removed?.type || "Adjustment"} "${removed?.title || ""}" removed — click Save to apply`, "info");
  };
  const handleSaveAdjustmentsToServer = async () => {
    if (!bookingId) return;
    const res = await setAdjustmentsApi(bookingId, adjustments);
    if (res.success) {
      showToast("Adjustments saved successfully", "success");
      refetchAll();
    } else {
      showToast(res.message || "Failed to save adjustments", "error");
    }
  };

  // Use raw totalAmount (no adjustments) as the subtotal baseline.
  // b.totalPrice maps to grandTotal which already includes saved adjustments,
  // so using it here would cause adjustments to be double-counted in the UI.
  const subtotal = b.pricing.sellingPrice; // = totalAmount from DB (raw product sum, no adjustments)
  const adjustmentsTotal = adjustments.reduce((sum, a) => {
    // Case-insensitive on mode/type: handles both manual ({mode:'Fixed',type:'Surcharge'})
    // and online ({mode:'fixed',type:'fee'}) shapes. Anything not 'percentage' is fixed;
    // anything not 'discount' (surcharge/fee) adds. Mirrors backend adjustmentAmount().
    const val = String(a.mode || "").toLowerCase() === "percentage" ? (subtotal * (a.value || 0)) / 100 : (a.value || 0);
    return sum + (String(a.type || "").toLowerCase() === "discount" ? -val : val);
  }, 0);
  const baseTotalPrice = subtotal + adjustmentsTotal;

  // Net financial impact of post-booking modifications on the customer total.
  // Hotel/Transfer product modifications are already baked into the product subtotal by
  // the backend, so only flight product mods + all traveler mods are added here. This
  // mirrors the Pricing tab's `modsNetForTotal` so every tab agrees on the total &
  // outstanding (totalModificationAmount is 0 for non-financial mods, so no filtering
  // is needed). Previously this delta lived only in the Pricing tab, which is why the
  // outstanding balance looked correct there but nowhere else.
  const modsNetForTotal =
    (productModifications || [])
      .filter((m) => {
        const parentType = (components || []).find((c) => String(c.id) === String(m.productId))?.type || "";
        return parentType !== "Hotel" && parentType !== "Transfer";
      })
      .reduce((s, m) => s + parseFloat(m.totalModificationAmount || 0), 0) +
    (b.travelerModifications || [])
      .filter((m) => m.actionType !== "CANCEL_TRAVELER")
      .reduce((s, m) => s + parseFloat(m.totalModificationAmount || 0), 0);

  // Customer-facing total = product subtotal + adjustments + post-booking modifications.
  // This is the GROSS Total Selling Price (a cancelled product keeps its original value).
  const totalPrice = baseTotalPrice + modsNetForTotal;

  // Use the finance Payment model (fetched separately) as the source of truth for
  // client paid amounts. DUAL-POOL rule: a client-covered transaction cost is part
  // of what the client owes, so outstanding + applied settle against the NET pool;
  // a Sunsky-borne cost is absorbed. Credit also uses the NET pool. We recompute
  // here against `netPayable` (which includes traveler mods the summary doesn't).
  const clientSummary = financePayments?.summary?.client;
  const netPool = clientSummary?.netPool;
  // Cancellation refund only nets down the payable when the client has actually paid.
  // If no payments exist yet, the full gross amount is owed (refund is not yet due).
  // Total Selling Price is always the GROSS customer obligation (active + retained
  // supplier cost). Refund due from cancellation surfaces naturally as customer credit
  // when payments exceed the booking value — never subtract it from the booking total.
  const netPayable = totalPrice;
  // Outstanding clamped at 0 (excess = Customer Credit, never a negative outstanding).
  const outstandingAmount = netPool != null
    ? Math.max(0, netPayable - netPool)
    : Math.max(0, netPayable - (clientSummary?.collected ?? 0));
  const customerCreditAmount = netPool != null
    ? Math.max(0, netPool - netPayable)
    : Math.max(0, (clientSummary?.collected ?? 0) - netPayable);
  // Signed balance shown as "Outstanding": + = client still owes us; − = we owe the
  // client a refund (e.g. a cancelled booking they already paid → refund due). It
  // returns to 0 once the refund is actually paid out (which lowers netPool).
  const signedOutstanding = outstandingAmount - customerCreditAmount;
  // "Payments Received" = amount applied to the booking (net, capped at net payable).
  const clientPaid = netPool != null
    ? Math.max(0, Math.min(netPool, netPayable))
    : (clientSummary?.collected ??
       b.payments.client.filter((p) => p.status === "Paid").reduce((s, p) => s + p.amount, 0));
  // ACTUAL net paid by the client (applied + any overpaid excess) = the real money in,
  // so "Payments Received"/"Paid Amount" reconcile with the signed Outstanding
  // (net payable − actual paid). For a normal booking this equals clientPaid.
  const clientPaidActual = clientPaid + customerCreditAmount;

  const confirmTooltip = () => {
    if (isBookingConfirmed) return "Booking is already confirmed";
    if (!allComponentsConfirmed) return "All components must be confirmed first";
    return "Confirm this booking";
  };

  const lockTooltip = () => {
    if (isLocked) return "Booking is already locked";
    if (!isBookingConfirmed) return "Booking must be confirmed before locking";
    return "Lock this booking";
  };

  const handleBack = () => {
    if (location.state?.fromCustomer) navigate(-1);
    else navigate("/bookings");
  };

  const productTypes = components.map((c) => c.type);

  /* Booking summary strip values */
  const travelerList = (b.travelers || []).filter((t) => t.status !== "Cancelled");
  const adultCount = travelerList.filter((t) => (t.type || "").toLowerCase() === "adult").length;
  const childCount = travelerList.length - adultCount;
  const travelerBreakdown = travelerList.length
    ? `${adultCount} Adult${adultCount !== 1 ? "s" : ""}${childCount ? `, ${childCount} Child${childCount !== 1 ? "ren" : ""}` : ""}`
    : "";
  const pctPaid = netPayable > 0 ? Math.min(100, Math.round((clientPaidActual / netPayable) * 100)) : 0;
  const pctRemaining = Math.max(0, 100 - pctPaid);

  /* Sticky "go to previous / next tab" navigation */
  const activeTabIndex = TABS.findIndex((t) => t.key === activeTab);
  const prevTab = activeTabIndex > 0 ? TABS[activeTabIndex - 1] : null;
  const PrevTabIcon = prevTab ? prevTab.Icon : null;
  const nextTab = activeTabIndex < TABS.length - 1 ? TABS[activeTabIndex + 1] : null;
  const NextTabIcon = nextTab ? nextTab.Icon : null;

  /* Scroll-to-advance / retreat: keep scrolling at the very bottom of a tab → glide
     to the next one; keep scrolling at the very top → glide back to the previous one.
     All trigger safety rules live in useScrollTabAdvance; here we only run the
     fade-out → switch → fade-in choreography. */
  const tabBarRef = useRef(null);
  const advPillRef = useRef(null);
  const advBarRef = useRef(null);
  const advPrevPillRef = useRef(null);
  const advPrevBarRef = useRef(null);
  const advScrollRef = useRef(null); // the hook writes its scroll container here
  const [tabLeaving, setTabLeaving] = useState(false);
  const autoAdvTimerRef = useRef(null);
  const autoDirTimerRef = useRef(null);
  // Direction of the in-flight scroll-driven switch ("next" | "prev" | null). Drives
  // the directional slide animations; null for ordinary tab-bar clicks. A ref (not
  // state): the keyed wrapper re-mounts on the swap render anyway, and the value is
  // cleared shortly after the entrance animation so manual clicks stay non-directional.
  const autoDirRef = useRef(null);
  const nextTabKeyRef = useRef(null);
  nextTabKeyRef.current = nextTab ? nextTab.key : null;
  const prevTabKeyRef = useRef(null);
  prevTabKeyRef.current = prevTab ? prevTab.key : null;

  const runAutoSwitch = useCallback((key, dir, positionAfterSwap) => {
    autoDirRef.current = dir;
    setTabLeaving(true); // fade the current panel out…
    window.clearTimeout(autoAdvTimerRef.current);
    window.clearTimeout(autoDirTimerRef.current);
    autoAdvTimerRef.current = window.setTimeout(() => {
      setActiveTab(key); // …swap while invisible…
      setTabLeaving(false); // …new panel mounts with the directional slide-in
      positionAfterSwap();
      // Clear after the 200ms entrance finishes so later manual clicks fade neutrally.
      autoDirTimerRef.current = window.setTimeout(() => { autoDirRef.current = null; }, 260);
    }, 120);
  }, []);

  const handleAutoAdvance = useCallback(() => {
    const key = nextTabKeyRef.current;
    if (!key) return;
    runAutoSwitch(key, "next", () => {
      const sc = advScrollRef.current;
      const bar = tabBarRef.current;
      if (sc && bar) {
        // Jump (while invisible) so the tab bar sits at the top of the new tab.
        const top = bar.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - 10;
        sc.scrollTo({ top: Math.max(0, top), behavior: "auto" });
      }
    });
  }, [runAutoSwitch]);

  const handleAutoRetreat = useCallback(() => {
    const key = prevTabKeyRef.current;
    if (!key) return;
    runAutoSwitch(key, "prev", () => {
      const sc = advScrollRef.current;
      if (sc) {
        // The previous panel's height only exists after React paints it — wait two
        // frames, then land near (not exactly at) its END so the reverse-reading flow
        // continues naturally and the "next tab" pill can't instantly re-arm.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const top = Math.max(0, sc.scrollHeight - sc.clientHeight - 24);
          sc.scrollTo({ top, behavior: "auto" });
        }));
      }
    });
  }, [runAutoSwitch]);
  useEffect(() => () => {
    window.clearTimeout(autoAdvTimerRef.current);
    window.clearTimeout(autoDirTimerRef.current);
  }, []);

  useScrollTabAdvance({
    enabled: (!!nextTab || !!prevTab) && !tabLeaving,
    resetKey: activeTab,
    anchorRef: tabBarRef,
    pillRef: advPillRef,
    barRef: advBarRef,
    prevPillRef: advPrevPillRef,
    prevBarRef: advPrevBarRef,
    containerOutRef: advScrollRef,
    hasNext: !!nextTab,
    hasPrev: !!prevTab,
    onAdvance: handleAutoAdvance,
    onRetreat: handleAutoRetreat,
  });

  return (
    <div className={styles.page}>
      <button className={styles.backLink} onClick={handleBack}>
        <ArrowLeft size={14} />
        Back to Bookings
      </button>

      {loading && <div style={{ padding: "8px 0", fontSize: 12.5, color: "#6b7280" }}>Loading booking…</div>}
      {error && <div style={{ padding: "8px 0", fontSize: 12.5, color: "#c42b2b" }}>{error}</div>}

      {/* UI-29: paid online booking not confirmed with the supplier. Same definition as the
          backend bell / action-required feed (urgentBooking.js); gone once Confirmed or Cancelled. */}
      <UrgentBanner booking={apiBooking} liveStatus={bookingStatus} activity={b.auditLog} />

      <div className={styles.headerCard}>
        <div className={styles.headerTop}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}><FolderOpen size={22} /></div>
            <div className={styles.headerMeta}>
              <div className={styles.headerTitleRow}>
                <h2>{b.ref}</h2>
                {isLocked && (
                  <span className={styles.lockedBadge}>
                    <Lock size={11} /> Locked
                  </span>
                )}
              </div>
              <div className={styles.headerTags}>
                <span className={`${styles.badge} ${statusClass(bookingStatus)}`}>
                  <span className={styles.dot} />{bookingStatus}
                </span>
                <ProductChips types={productTypes} />
                <span className={styles.badgeOrigin}>{b.origin}</span>
              </div>
            </div>
          </div>
          <div className={styles.headerRightWrap}>
            <div className={styles.headerStatusCard}>
              <div className={styles.totalPriceLabel}>Payment Status</div>
              <div className={`${styles.headerPayStatus} ${payTextClass(b.paymentStatus)}`}>{b.paymentStatus}</div>
            </div>
            <div className={styles.headerRight}>
              <div className={styles.totalPriceLabel}>Total Selling Price</div>
              <div className={styles.totalPrice}>{fmt(netPayable)}</div>
              {adjustments.length > 0 && (
                <div className={styles.discountIndicator}>
                  <Tag size={11} /> {adjustments.length} Adjustment{adjustments.length !== 1 ? "s" : ""}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerInfoRow}>
          {[
            { label: "Customer", value: b.customer.name },
            { label: "Email", value: b.customer.email },
            { label: "Invoice", value: b.invoiceNumber },
            { label: "Created", value: b.createdAt },
            { label: "Created By", value: b.createdBy },
            // { label: "Sale Type", value: b.saleType },
          ].map((cell) => (
            <div key={cell.label} className={styles.infoCell}>
              <span className={styles.infoCellLabel}>{cell.label}</span>
              <span className={styles.infoCellValue}>{cell.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.actionBar}>
        <span className={styles.actionBarLabel}>ACTIONS</span>
        {/* Confirm Booking lives in the Booking Overview tab (review-and-finalize step). */}
        {isBookingConfirmed && (
          <span className={styles.confirmedIndicator}>
            <CheckCircle size={13} /> Booking Confirmed
          </span>
        )}
        <button
          className={styles.btnSecondary}
          onClick={() => setActiveTab("overview")}
          title="Review all details and finalize this booking"
        >
          <ClipboardCheck size={13} /> Review &amp; Finalize
        </button>
        <LoadingButton
          className={styles.btnSecondary}
          disabled={isLocked}
          loading={generatingDocs}
          loadingText="Generating…"
          icon={<FilePlus size={13} />}
          onClick={async () => {
            const res = await generateVouchersApi(b.ref);
            if (res.success) { showToast(res.message || "Documents generated", "success"); setActiveTab("documents"); }
            else showToast(res.message || "Generation failed", "error");
          }}
        >
          Generate Documents
        </LoadingButton>
        <button className={styles.btnSecondary} disabled={isLocked}><Mail size={13} /> Send Documents</button>
        <div className={styles.actionDivider} />
        {!isLocked ? (
          <button
            className={`${styles.btnLock} ${!canLock ? styles.btnLockDisabled : ""}`}
            disabled={!canLock}
            onClick={() => setLockModalOpen(true)}
            title={lockTooltip()}
          >
            <Lock size={13} /> Lock Booking
          </button>
        ) : (
          <span className={styles.lockedIndicator}>
            <Lock size={13} /> Booking Locked
          </span>
        )}
        <div className={styles.actionDivider} />
        <button className={styles.btnDanger} disabled={isLocked} onClick={() => navigate("/bookings/cancellation", { state: { booking: { ...b, id: bookingId }, cancelApi: !!bookingId } })}>
          <Ban size={13} /> Cancel Booking
        </button>
      </div>

      <div className={styles.tabBar} ref={tabBarRef}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`${styles.tab} ${activeTab === t.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Booking summary strip — hidden on the Overview tab, which has its own KPI cards */}
      {activeTab !== "overview" && (
      <div className={styles.bookingStats}>
        <div className={styles.bStat}>
          <div className={styles.bStatIcon}><Package size={18} /></div>
          <div className={styles.bStatTxt}>
            <span className={styles.bStatLabel}>Total Products</span>
            <span className={styles.bStatValue}>{components.length}</span>
          </div>
        </div>
        <div className={styles.bStat}>
          <div className={styles.bStatIcon}><User size={18} /></div>
          <div className={styles.bStatTxt}>
            <span className={styles.bStatLabel}>Total Travelers</span>
            <span className={styles.bStatValue}>{travelerList.length}</span>
            {travelerBreakdown && <span className={styles.bStatSub}>{travelerBreakdown}</span>}
          </div>
        </div>
        <div className={styles.bStat}>
          <div className={styles.bStatIcon}><Tag size={18} /></div>
          <div className={styles.bStatTxt}>
            <span className={styles.bStatLabel}>Total Selling Price</span>
            <span className={styles.bStatValue}>{fmt(netPayable)}</span>
            <span className={styles.bStatSub}>Invoiced to customer</span>
          </div>
        </div>
        <div className={styles.bStat}>
          <div className={styles.bStatIcon}><CreditCard size={18} /></div>
          <div className={styles.bStatTxt}>
            <span className={styles.bStatLabel}>Payments Received</span>
            <span className={styles.bStatValue}>{fmt(clientPaidActual)}</span>
            <span className={styles.bStatSub}>{pctPaid}% of booking value</span>
          </div>
        </div>
        {/* Outstanding is shown as a dedicated card in the Pricing & VAT tab —
            hide the small header stat there to avoid duplicating it. */}
        {activeTab !== "pricing" && (
          <div className={styles.bStat}>
            <div className={styles.bStatIcon}><Euro size={18} /></div>
            <div className={styles.bStatTxt}>
              <span className={styles.bStatLabel}>Outstanding</span>
              <span className={`${styles.bStatValue} ${signedOutstanding > 0.005 ? styles.bStatDanger : signedOutstanding < -0.005 ? styles.bStatSubCredit : styles.bStatOk}`}>{fmt(signedOutstanding)}</span>
              <span className={`${styles.bStatSub} ${signedOutstanding < -0.005 ? styles.bStatSubCredit : ""}`}>
                {signedOutstanding < -0.005 ? "Refund due to customer" : signedOutstanding > 0.005 ? `${pctRemaining}% remaining` : "Fully settled"}
              </span>
            </div>
          </div>
        )}
      </div>
      )}

      {/* keyed wrapper: every tab change re-mounts it so the fade-in plays;
          tabPanelsLeaving fades the OLD panel out during scroll-driven switches.
          autoDirRef makes scroll switches directional (next = slide up, prev = slide
          down); manual clicks keep the neutral fade. */}
      <div
        key={activeTab}
        className={[
          styles.tabPanels,
          // Entrance direction — only on the freshly mounted panel (adding an
          // animation class to the still-mounted OLD panel would replay it mid-fade).
          !tabLeaving && autoDirRef.current === "next" ? styles.tabPanelsFromNext : "",
          !tabLeaving && autoDirRef.current === "prev" ? styles.tabPanelsFromPrev : "",
          tabLeaving ? styles.tabPanelsLeaving : "",
          tabLeaving && autoDirRef.current === "prev" ? styles.tabPanelsLeavingPrev : "",
        ].filter(Boolean).join(" ")}
      >
      {activeTab === "customer" && <CustomerOverviewTab customer={b.customer} onEdit={() => setCustomerEditOpen(true)} onChangeCustomer={() => setChangeCustomerOpen(true)} />}
      {activeTab === "itinerary" && (
        <ItineraryTab
          components={components}
          onAdd={() => setAddTypePickerOpen(true)}
          onEdit={openEdit}
          onDelete={openDelete}
          isLocked={isLocked}
          bookingId={bookingId}
          onRefresh={refetch}
          ssrs={b.ssrs || []}
          travelers={b.travelers || []}
          assignments={assignments}
          setSSRPricingApi={setSSRPricingApi}
        />
      )}
      {activeTab === "travelers" && (
        <TravelersTab
          travelers={b.travelers}
          travelerModifications={b.travelerModifications || []}
          bookingId={bookingId}
          isLocked={isLocked}
          onRefetch={refetch}
          ssrs={b.ssrs || []}
          components={components}
          onSwitchTab={setActiveTab}
          setSSRPricingApi={setSSRPricingApi}
        />
      )}
      {activeTab === "assignments" && (
        <AssignmentTab
          components={components}
          travelers={b.travelers}
          assignments={assignments}
          originalAssignments={b.assignments}
          ssrs={b.ssrs || []}
          isLocked={isLocked}
          assignmentLockData={assignmentLockData}
          bookingId={bookingId}
          showToast={showToast}
          onRemove={async (assignmentId, travelerId, productId, adminUnlockReason) => {
            if (assignmentId) {
              const res = await deleteAssignmentApi(bookingId, assignmentId, adminUnlockReason ? { data: { adminUnlockReason } } : undefined);
              if (!res.success) {
                showToast(res.message || "Failed to remove assignment", "error");
                return;
              }
              showToast("Assignment removed", "success");
              refetchAll();
            } else {
              setAssignments((prev) => prev.filter((a) => !(a.travelerId === travelerId && a.productId === productId)));
            }
          }}
          onAdd={async (travelerId, productId, assignmentType, adminUnlockReason) => {
            const payload = { assignments: [{ travelerId, productId, assignmentType }] };
            if (adminUnlockReason) payload.adminUnlockReason = adminUnlockReason;
            const res = await createAssignmentsApi(bookingId, [{ travelerId, productId, assignmentType }], adminUnlockReason ? { adminUnlockReason } : undefined);
            if (!res.success) {
              showToast(res.message || "Failed to add assignment", "error");
              return;
            }
            showToast("Traveler assigned successfully", "success");
            refetchAll();
          }}
          onUpdateAssignment={async (assignmentId, updates) => {
            if (!assignmentId) return;
            const res = await updateAssignmentApi(bookingId, assignmentId, updates);
            if (!res.success) {
              showToast(res.message || "Failed to update room assignment", "error");
              return;
            }
            showToast("Room assignment updated", "success");
            refetchAll();
          }}
        />
      )}
      {activeTab === "pricing" && (
        // PricingTab re-adds its own post-booking modifications delta, so it receives the
        // BASE total (subtotal + adjustments) — not the mods-inclusive parent total — to
        // avoid double-counting.
        <PricingTab
          booking={b}
          components={components}
          assignments={b.assignments}
          adjustments={adjustments}
          subtotal={subtotal}
          adjustmentsTotal={adjustmentsTotal}
          totalPrice={baseTotalPrice}
          outstandingAmount={outstandingAmount}
          customerCreditAmount={customerCreditAmount}
          clientPaid={clientPaid}
          isLocked={isLocked}
          bookingId={bookingId}
          ssrs={b.ssrs || []}
          onAddAdjustment={() => { setEditingAdjustmentIdx(null); setAdjustmentModalOpen(true); }}
          onEditAdjustment={(idx) => { setEditingAdjustmentIdx(idx); setAdjustmentModalOpen(true); }}
          onRemoveAdjustment={handleRemoveAdjustment}
          onSaveAdjustments={handleSaveAdjustmentsToServer}
          savingAdjustments={savingAdjustments}
          adjustmentsDirty={adjustmentsDirty}
          onCalculateVAT={calculateVATApi}
          setFlightPricingApi={setFlightPricingApi}
          setHotelPricingApi={setHotelPricingApi}
          setTransferPricingApi={setTransferPricingApi}
          setGenericPricingApi={setGenericPricingApi}
          setSSRPricingApi={setSSRPricingApi}
          refetch={refetchAll}
          showToast={showToast}
        />
      )}
      {activeTab === "overview" && (
        <OverviewTab
          booking={b}
          components={components}
          bookingStatus={bookingStatus}
          totalPrice={totalPrice}
          clientPaid={clientPaid}
          clientPaidActual={clientPaidActual}
          outstandingAmount={outstandingAmount}
          signedOutstanding={signedOutstanding}
          isLocked={isLocked}
          isBookingConfirmed={isBookingConfirmed}
          canConfirm={canConfirm}
          allComponentsConfirmed={allComponentsConfirmed}
          confirmTooltipText={confirmTooltip()}
          onConfirm={() => setConfirmModalOpen(true)}
          generatingDocs={generatingDocs}
          onGenerateDocuments={async () => {
            const res = await generateVouchersApi(b.ref);
            if (res.success) { showToast(res.message || "Documents generated", "success"); setActiveTab("documents"); }
            else showToast(res.message || "Generation failed", "error");
          }}
          onSwitchTab={setActiveTab}
        />
      )}
      {/* What the customer wrote at checkout, read from the raw booking rather than the
          mapped view model: these are records to act on, not figures to present, and the
          panel owns its own follow-up calls. */}
      {activeTab === "overview" && (
        <CustomerRequestsPanel
          bookingId={bookingId}
          products={apiBooking?.products || []}
          messages={apiBooking?.messages || []}
          isLocked={isLocked}
          onChanged={refetchAll}
        />
      )}
      {activeTab === "confirmations" && <ConfirmationsTab confirmations={b.confirmations} />}
      {activeTab === "payments" && <PaymentsTab booking={b} bookingId={bookingId} onPaymentChange={() => { refetchFinancePayments(); refetchAll(); }} />}
      {activeTab === "documents" && <DocumentsTab bookingRef={b.ref} bookingUpdatedAt={apiBooking?.updatedAt} customerEmail={b.customer?.email || ""} />}
      {activeTab === "emails" && <EmailsTab bookingRef={b.ref} />}
      {activeTab === "notes" && (
        <NotesTab
          bookingId={bookingId}
          officeNotes={apiBooking?.officeNotes || ""}
          invoiceNotes={apiBooking?.invoiceNotes || ""}
          isLocked={isLocked}
          onSaved={refetchAll}
        />
      )}
      {activeTab === "audit" && <AuditTab log={b.auditLog} />}
      </div>

      {/* Scroll-to-advance hints — driven imperatively by useScrollTabAdvance
          (visibility via data-visible, progress via scaleX on the fill bar). */}
      {nextTab && (
        <div ref={advPillRef} className={styles.scrollAdvPill} aria-hidden="true">
          <ChevronsDown size={14} className={styles.scrollAdvChevs} />
          <span className={styles.scrollAdvText}>
            Keep scrolling to open <b>{nextTab.label}</b>
          </span>
          <span className={styles.scrollAdvTrack}>
            <span ref={advBarRef} className={styles.scrollAdvFill} />
          </span>
        </div>
      )}
      {prevTab && (
        <div ref={advPrevPillRef} className={`${styles.scrollAdvPill} ${styles.scrollAdvPillTop}`} aria-hidden="true">
          <ChevronsUp size={14} className={styles.scrollAdvChevsUp} />
          <span className={styles.scrollAdvText}>
            Keep scrolling to go back to <b>{prevTab.label}</b>
          </span>
          <span className={styles.scrollAdvTrack}>
            <span ref={advPrevBarRef} className={styles.scrollAdvFill} />
          </span>
        </div>
      )}

      {/* Sticky bottom navigation — jump back to the previous tab without scrolling up.
          Sits in-flow as a sticky footer so it never permanently hides tab content. */}
      <div className={styles.tabNavBar}>
        {prevTab && PrevTabIcon ? (
          <button
            type="button"
            className={styles.tabNavPrev}
            onClick={() => setActiveTab(prevTab.key)}
            title={`Go to ${prevTab.label}`}
          >
            <span className={styles.tabNavArrow}><ArrowLeft size={16} /></span>
            <span className={styles.tabNavTextWrap}>
              <span className={styles.tabNavCaption}>Previous tab</span>
              <span className={styles.tabNavLabel}><PrevTabIcon size={13} /> {prevTab.label}</span>
            </span>
          </button>
        ) : (
          <span className={styles.tabNavHint}>You're at the first tab</span>
        )}
        <span className={styles.tabNavStep}>
          {activeTabIndex + 1}<span className={styles.tabNavStepOf}> / {TABS.length}</span>
        </span>
        {nextTab && NextTabIcon ? (
          <button
            type="button"
            className={styles.tabNavNext}
            onClick={() => setActiveTab(nextTab.key)}
            title={`Go to ${nextTab.label}`}
          >
            <span className={styles.tabNavTextWrap}>
              <span className={styles.tabNavCaption}>Next tab</span>
              <span className={styles.tabNavLabel}><NextTabIcon size={13} /> {nextTab.label}</span>
            </span>
            <span className={styles.tabNavArrow}><ArrowRight size={16} /></span>
          </button>
        ) : (
          <span className={styles.tabNavHint}>You're at the last tab</span>
        )}
      </div>

      <TypePickerModal
        open={addTypePickerOpen}
        onClose={() => setAddTypePickerOpen(false)}
        onSelect={handleTypePickerSelect}
      />

      <AddComponentModal
        open={addModalOpen}
        onClose={() => { setAddModalOpen(false); setAddModalType(null); }}
        mode="add"
        preselectedType={addModalType}
        onSave={(c) => { handleAddComponent(c); setAddModalType(null); }}
      />

      <AddComponentModal
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditingComponent(null); }}
        mode="edit"
        initialData={editingComponent}
        onSave={handleEditComponent}
      />

      <BaseModal
        open={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingComponent(null); }}
        title="Remove Component"
        width="440px"
        footer={
          <>
            <button className={styles.btnSecondary} onClick={() => { setDeleteModalOpen(false); setDeletingComponent(null); }}>Cancel</button>
            <LoadingButton className={styles.btnDanger} loading={deletingProduct} loadingText="Removing…" icon={<Trash2 size={13} />} onClick={handleDeleteComponent}>Remove</LoadingButton>
          </>
        }
      >
        <div className={styles.deleteConfirmText}>
          Are you sure you want to remove <strong>{deletingComponent?.type}</strong>
          {deletingComponent?.supplier ? ` (via ${deletingComponent.supplier})` : ""} from this booking?
          This action cannot be undone.
        </div>
      </BaseModal>

      <BaseModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title="Confirm Booking"
        width="480px"
        footer={
          <>
            <button className={styles.btnSecondary} onClick={() => setConfirmModalOpen(false)}>Cancel</button>
            <LoadingButton className={styles.btnConfirmAction} loading={confirmingBooking || submittingBooking} loadingText="Confirming…" icon={<Check size={13} />} onClick={handleConfirmBooking}>Confirm Booking</LoadingButton>
          </>
        }
      >
        <div className={styles.lockConfirmContent}>
          <div className={styles.confirmModalIcon}><CheckCircle size={22} /></div>
          <div className={styles.lockConfirmTitle}>Confirm this booking?</div>
          <div className={styles.lockConfirmText}>
            All {components.length} components are confirmed. The booking status will be set
            to Confirmed, enabling document generation and booking lock.
          </div>
          <div className={styles.lockConfirmChecklist}>
            {components.map((c) => (
              <div key={c.id} className={styles.lockCheckItem}>
                <Check size={14} className={styles.lockCheckIcon} />
                {c.type} — {c.supplier}
              </div>
            ))}
          </div>
        </div>
      </BaseModal>

      <BaseModal
        open={lockModalOpen}
        onClose={() => setLockModalOpen(false)}
        title="Lock Booking"
        width="480px"
        footer={
          <>
            <button className={styles.btnSecondary} onClick={() => setLockModalOpen(false)}>Cancel</button>
            <button className={styles.btnLockConfirm} onClick={handleLockBooking}><Lock size={13} /> Confirm Lock</button>
          </>
        }
      >
        <div className={styles.lockConfirmContent}>
          <div className={styles.lockConfirmIcon}><Lock size={22} /></div>
          <div className={styles.lockConfirmTitle}>Are you sure you want to lock this booking?</div>
          <div className={styles.lockConfirmText}>
            Once locked, itinerary components cannot be added, edited, or removed.
            Pricing and financial data will be preserved as-is. This action can only be reversed by an administrator.
          </div>
          <div className={styles.lockConfirmChecklist}>
            <div className={styles.lockCheckItem}><Check size={14} className={styles.lockCheckIcon} />Booking confirmed with {components.length} components</div>
            <div className={styles.lockCheckItem}><Check size={14} className={styles.lockCheckIcon} />Booking reference: {b.ref}</div>
          </div>
        </div>
      </BaseModal>

      <AdjustmentModal
        open={adjustmentModalOpen}
        onClose={() => { setAdjustmentModalOpen(false); setEditingAdjustmentIdx(null); }}
        onSave={handleSaveAdjustment}
        initialData={editingAdjustmentIdx !== null ? adjustments[editingAdjustmentIdx] : null}
        subtotal={subtotal}
      />

      {productEditScreen && (
        <div className={styles.productFormOverlay}>
          <ProductFormScreen
            type={productEditScreen.type}
            initialData={productEditScreen.data}
            onSave={handleProductFormSave}
            onClose={() => setProductEditScreen(null)}
            allProducts={components}
            saving={savingProduct}
          />
        </div>
      )}

      {apiBooking?.customerType !== "Company" && (
        <AddPrivateCustomerModal
          open={customerEditOpen}
          onClose={() => { setCustomerEditOpen(false); setCustomerEditError(null); }}
          onSave={handleCustomerSave}
          customer={customerProfileData}
          apiError={customerEditError}
        />
      )}
      {apiBooking?.customerType === "Company" && (
        <AddProfessionalCustomerModal
          open={customerEditOpen}
          onClose={() => { setCustomerEditOpen(false); setCustomerEditError(null); }}
          onSave={handleCustomerSave}
          company={customerProfileData}
          apiError={customerEditError}
        />
      )}
      <ChangeCustomerModal
        open={changeCustomerOpen}
        onClose={() => setChangeCustomerOpen(false)}
        onConfirm={handleChangeCustomer}
        loading={changingCustomer}
        currentCustomerName={b.customer?.name || "—"}
        onAddNew={(type, query) => {
          setChangeCustomerOpen(false);
          const path = type === "company" ? "/people/customers/professional" : "/people/customers/private";
          navigate(path, { state: { openAdd: true, prefillName: (query || "").trim() } });
        }}
      />
    </div>
  );
};

const AdjustmentModal = ({ open, onClose, onSave, initialData, subtotal }) => {
  const [adjType, setAdjType] = useState("Discount");
  const [adjMode, setAdjMode] = useState("Fixed");
  const [adjValue, setAdjValue] = useState("");
  const [adjTitle, setAdjTitle] = useState("");
  const [adjDescription, setAdjDescription] = useState("");
  const [adjIsFee, setAdjIsFee] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && initialData) {
      setAdjType(initialData.type || "Discount");
      setAdjMode(initialData.mode || "Fixed");
      setAdjValue(initialData.value != null ? String(initialData.value) : "");
      setAdjTitle(initialData.title || "");
      setAdjDescription(initialData.description || "");
      setAdjIsFee(!!initialData.isFee);
    } else if (open) {
      setAdjType("Discount"); setAdjMode("Fixed"); setAdjValue(""); setAdjTitle(""); setAdjDescription(""); setAdjIsFee(false);
    }
    setError("");
  }, [open, initialData]);

  const handleSave = () => {
    const value = parseFloat(adjValue);
    if (!adjTitle.trim()) { setError("Please enter a title"); return; }
    if (!adjValue || isNaN(value) || value <= 0) { setError("Please enter a valid amount"); return; }
    if (adjMode === "Percentage" && value > 100) { setError("Percentage cannot exceed 100%"); return; }
    onSave({ type: adjType, mode: adjMode, value, title: adjTitle.trim(), description: adjDescription.trim(), isFee: adjType === "Surcharge" ? adjIsFee : false, appliedAt: new Date().toISOString() });
  };

  const handleClose = () => {
    setAdjType("Discount"); setAdjMode("Fixed"); setAdjValue(""); setAdjTitle(""); setAdjDescription(""); setAdjIsFee(false); setError("");
    onClose();
  };

  const previewAmount = () => {
    const value = parseFloat(adjValue);
    if (!adjValue || isNaN(value) || value <= 0) return null;
    return adjMode === "Fixed" ? value : (subtotal * value) / 100;
  };
  const preview = previewAmount();
  const isDiscount = adjType === "Discount";

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={initialData ? "Edit Adjustment" : "Add Adjustment"}
      width="520px"
      footer={
        <>
          <button className={styles.btnSecondary} onClick={handleClose}>Cancel</button>
          <button className={styles.btnDiscount} onClick={handleSave}><Tag size={13} /> {initialData ? "Update" : "Apply"} Adjustment</button>
        </>
      }
    >
      <div className={styles.discountModalContent}>
        <div className={styles.discountModalInfo}>
          <div className={styles.discountModalInfoItem}>
            <span className={styles.discountModalInfoLabel}>Subtotal</span>
            <span className={styles.discountModalInfoValue}>{fmt(subtotal)}</span>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Adjustment Type</label>
          <div className={styles.discountTypeSelector}>
            <button className={`${styles.discountTypeBtn} ${adjType === "Discount" ? styles.discountTypeBtnActive : ""}`} onClick={() => setAdjType("Discount")}>
              <TrendingDown size={14} /> Discount
            </button>
            <button className={`${styles.discountTypeBtn} ${adjType === "Surcharge" ? styles.discountTypeBtnActive : ""}`} onClick={() => setAdjType("Surcharge")}>
              <TrendingUp size={14} /> Surcharge
            </button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Mode</label>
          <div className={styles.discountTypeSelector}>
            <button className={`${styles.discountTypeBtn} ${adjMode === "Fixed" ? styles.discountTypeBtnActive : ""}`} onClick={() => setAdjMode("Fixed")}>
              <Euro size={14} /> Fixed Amount
            </button>
            <button className={`${styles.discountTypeBtn} ${adjMode === "Percentage" ? styles.discountTypeBtnActive : ""}`} onClick={() => setAdjMode("Percentage")}>
              <Percent size={14} /> Percentage
            </button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Title <span style={{ color: "#ef4444" }}>*</span></label>
          <input
            type="text"
            className={styles.discountInput}
            placeholder={`e.g. Early bird discount, Peak season surcharge…`}
            value={adjTitle}
            onChange={(e) => { setAdjTitle(e.target.value); setError(""); }}
            style={{ padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: "8px", fontSize: "13px", width: "100%", boxSizing: "border-box" }}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{adjType} {adjMode === "Fixed" ? "Amount" : "Percentage"}</label>
          <div className={styles.discountInputWrapper}>
            {adjMode === "Fixed" && <span className={styles.discountInputPrefix}>€</span>}
            <input
              type="number"
              className={styles.discountInput}
              placeholder={adjMode === "Fixed" ? "0.00" : "0"}
              value={adjValue}
              onChange={(e) => { setAdjValue(e.target.value); setError(""); }}
              onFocus={(e) => e.target.select()}
              min="0"
              step={adjMode === "Fixed" ? "0.01" : "1"}
              max={adjMode === "Percentage" ? "100" : undefined}
            />
            {adjMode === "Percentage" && <span className={styles.discountInputSuffix}>%</span>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Description (optional)</label>
          <textarea
            className={styles.discountTextarea}
            placeholder={`Reason for this ${adjType.toLowerCase()}...`}
            value={adjDescription}
            onChange={(e) => { setAdjDescription(e.target.value); setError(""); }}
            rows={2}
          />
        </div>

        {adjType === "Surcharge" && (
          <div className={styles.formGroup}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: "13px", color: "#374151" }}>
              <input
                type="checkbox"
                checked={adjIsFee}
                onChange={(e) => setAdjIsFee(e.target.checked)}
                style={{ marginTop: 2, cursor: "pointer" }}
              />
              <span>
                <strong>Payment fee</strong> (e.g. Betaalkosten)
                <span style={{ display: "block", color: "#6b7280", fontSize: "12px", marginTop: 2 }}>
                  Automatically removed when its client payment is deleted.
                </span>
              </span>
            </label>
          </div>
        )}

        {error && <div className={styles.discountError}>{error}</div>}

        {preview !== null && preview > 0 && (
          <div className={styles.discountPreview}>
            <div className={styles.discountPreviewRow}>
              <span>{adjType} Amount</span>
              <span className={isDiscount ? styles.discountPreviewAmount : styles.discountPreviewTotal}>
                {isDiscount ? "−" : "+"} {fmt(preview)}
              </span>
            </div>
            <div className={styles.discountPreviewRow}>
              <span>New Total</span>
              <span className={styles.discountPreviewTotal}>
                {fmt(isDiscount ? subtotal - preview : subtotal + preview)}
              </span>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

const ChangeCustomerModal = ({ open, onClose, onConfirm, loading, currentCustomerName, onAddNew }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchCustomers = async (query = "") => {
    setSearching(true);
    setSelectedCustomer(null);
    try {
      const combined = [];
      if (searchType === "all" || searchType === "private") {
        try {
          const params = { limit: 25 };
          if (query.trim()) params.q = query.trim();
          const { data: res } = await axiosInstance.get("/people", { params });
          (res?.data || []).forEach((c) => combined.push({ ...c, _type: "private" }));
        } catch {}
      }
      if (searchType === "all" || searchType === "company") {
        try {
          const params = { limit: 25 };
          if (query.trim()) params.q = query.trim();
          const { data: res } = await axiosInstance.get("/people/professional-customers", { params });
          (res?.data || []).forEach((c) => combined.push({ ...c, _type: "professional" }));
        } catch {}
      }
      setResults(combined);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSelectedCustomer(null);
      setSearchType("all");
      fetchCustomers();
    } else {
      setResults([]);
    }
  }, [open]);

  const handleSearch = () => fetchCustomers(searchQuery);

  const renderRow = (c) => {
    const isPrivate = c._type === "private";
    const name = isPrivate
      ? `${c.firstName || ""} ${c.lastName || ""}`.trim()
      : c.tradingName || c.legalName || c.companyName || "—";
    const email = isPrivate ? c.email : c.invoiceEmail || c.primaryContactEmail || "—";
    const code = c.customerCode || c.id;
    const isSelected = selectedCustomer?.id === c.id && selectedCustomer?._type === c._type;

    return (
      <div
        key={`${c._type}-${c.id}`}
        className={`${styles.ccRow} ${isSelected ? styles.ccRowSelected : ""}`}
        onClick={() => setSelectedCustomer(c)}
      >
        <div className={styles.ccAvatar}>
          {isPrivate ? <User size={16} /> : <UserCircle size={16} />}
        </div>
        <div className={styles.ccRowInfo}>
          <span className={styles.ccRowName}>{name}</span>
          <span className={styles.ccRowEmail}>{email}</span>
        </div>
        <span className={`${styles.ccTypeBadge} ${isPrivate ? styles.badgePrivate : styles.badgeCompany}`}>
          {isPrivate ? "Private" : "Company"}
        </span>
        <span className={styles.ccRowCode}>{code}</span>
        {isSelected && <Check size={16} className={styles.ccCheck} />}
      </div>
    );
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Change Customer"
      width="660px"
      footer={
        <>
          <button className={styles.btnSecondaryOutline} onClick={onClose}>Cancel</button>
          <button
            className={styles.btnPrimaryAction}
            disabled={!selectedCustomer || loading}
            onClick={() => selectedCustomer && onConfirm(selectedCustomer)}
          >
            {loading ? <><Loader2 size={14} className={styles.spinIcon} /> Changing...</> : <><RefreshCw size={14} /> Assign Customer</>}
          </button>
        </>
      }
    >
      <div className={styles.ccCurrentBar}>
        <span className={styles.ccCurrentLabel}>Current Customer:</span>
        <strong>{currentCustomerName || "—"}</strong>
      </div>

      <div className={styles.ccSearchRow}>
        <select
          className={styles.ccTypeSelect}
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
        >
          <option value="all">All Customers</option>
          <option value="private">Private Only</option>
          <option value="company">Companies Only</option>
        </select>
        <div className={styles.ccSearchWrap}>
          <Search size={15} className={styles.ccSearchIcon} />
          <input
            className={styles.ccSearchInput}
            placeholder="Search by name, email, or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            autoFocus
          />
        </div>
        <button className={styles.ccSearchBtn} onClick={handleSearch} disabled={searching}>
          {searching ? <Loader2 size={14} className={styles.spinIcon} /> : <Search size={14} />}
          Search
        </button>
      </div>

      <div className={styles.ccResults}>
        {searching && (
          <div className={styles.ccEmpty}>
            <Loader2 size={28} className={styles.spinIcon} />
            <p>Loading customers...</p>
          </div>
        )}
        {!searching && results.length === 0 && (
          <div className={styles.ccEmpty}>
            <UserX size={32} strokeWidth={1.2} />
            <p>{searchQuery.trim() ? `No customers found matching "${searchQuery}"` : "No customers found"}</p>
            {onAddNew && (
              <button
                type="button"
                className={styles.ccAddNewBtn}
                onClick={() => onAddNew(searchType, searchQuery)}
              >
                <UserPlus size={15} /> Add New Client
              </button>
            )}
          </div>
        )}
        {!searching && results.map(renderRow)}
      </div>
    </BaseModal>
  );
};

const CustomerFieldSection = ({ icon: Icon, title, fields, onEdit }) => (
  <div className={styles.sectionCard}>
    <div className={styles.sectionHeader}>
      <div className={styles.sectionTitle}><Icon size={16} /> {title}</div>
      {onEdit && <button className={styles.btnEditOutline} onClick={onEdit}><Pencil size={12} /> Edit</button>}
    </div>
    <div className={styles.customerFieldList}>
      {fields.map((f) => (
        <div key={f.label} className={styles.customerFieldRow}>
          <span className={styles.customerFieldLabel}>{f.label}</span>
          <span className={styles.customerFieldValue}>{f.value}</span>
        </div>
      ))}
    </div>
  </div>
);

// ── Booking Overview Tab (review-and-finalize step) ────────────────────────
const PRETTY_TYPE = {
  Flight: "Flight", Hotel: "Hotel", Transfer: "Transfer", Insurance: "Travel Insurance",
  Package: "Package Holiday", CarRental: "Car Rental", Visa: "Visa Service", Cruise: "Cruise",
};
const prettyType = (t) => PRETTY_TYPE[t] || t;

/* Format a number as a de-DE percentage (e.g. 24,05%) */
const pctFmt = (n) =>
  `${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/* Collect every parseable date from a component so we can derive a travel-date range */
const ovComponentDates = (c) => {
  const out = [];
  const push = (d) => { if (d) { const dt = new Date(d); if (!isNaN(dt.getTime())) out.push(dt); } };
  const r = c._raw || {};
  const pd = (typeof r.productDetails === "string" ? (() => { try { return JSON.parse(r.productDetails); } catch { return {}; } })() : r.productDetails) || {};
  push(r.serviceDate); push(r.serviceDateEnd);
  push(pd.checkIn); push(pd.checkOut);
  push(pd.departureDate); push(pd.returnDate);
  push(pd.pickupDate); push(pd.dropoffDate);
  push(pd.insuranceStartDate || pd.validFrom); push(pd.insuranceExpireDate || pd.validTo);
  push(pd.visaIssueDate || pd.applicationDate); push(pd.visaExpiryDate);
  (pd.legs || []).forEach((l) => { push(l.departureDate); push(l.arrivalDate); });
  if (pd.schedule) push(pd.schedule.pickupDate);
  if (pd.arrivalLeg?.schedule) push(pd.arrivalLeg.schedule.pickupDate);
  if (pd.departureLeg?.schedule) push(pd.departureLeg.schedule.pickupDate);
  return out;
};
const ovDateRange = (dates) => {
  if (!dates.length) return null;
  const min = new Date(Math.min(...dates.map((d) => d.getTime())));
  const max = new Date(Math.max(...dates.map((d) => d.getTime())));
  return { min, max };
};
/* A short, human "what is this" line per component, derived from its built fields */
const ovProductSubtitle = (c) => {
  const f = Object.fromEntries((c.fields || []).map((x) => [x.label, x.value]));
  switch (c.type) {
    case "Flight": {
      // Full path across ALL legs, e.g. "BRU → AYT → BRU" or "BRU → IST → AYT".
      const r = c._raw || {};
      const pd = (typeof r.productDetails === "string" ? (() => { try { return JSON.parse(r.productDetails); } catch { return {}; } })() : r.productDetails) || {};
      const legs = Array.isArray(pd.legs) ? pd.legs : [];
      if (legs.length === 0) return "—";
      const path = [];
      legs.forEach((l, i) => {
        const dep = (l.departureAirport || "").toUpperCase();
        const arr = (l.arrivalAirport || "").toUpperCase();
        if (i === 0 && dep) path.push(dep);
        if (arr) path.push(arr);
        else if (dep) path.push(dep);
      });
      return path.length ? path.join(" → ") : "—";
    }
    case "Hotel": return f["Hotel Name"] && f["Hotel Name"] !== "—" ? f["Hotel Name"] : "Hotel stay";
    case "Transfer": return f.Pickup && f.Dropoff ? `${f.Pickup} → ${f.Dropoff}` : (f["Transfer Type"] || "Transfer");
    case "Insurance": return f["Insurance Type"] && f["Insurance Type"] !== "—" ? f["Insurance Type"] : (f["Insurance Name"] || "Insurance");
    case "Package": return f["Package Name"] && f["Package Name"] !== "—" ? f["Package Name"] : (f.Destination || "Package");
    case "CarRental": return f["Vehicle Type"] && f["Vehicle Type"] !== "—" ? f["Vehicle Type"] : "Car rental";
    case "Visa": return f["Visa Type"] && f["Visa Type"] !== "—" ? f["Visa Type"] : "Visa service";
    case "Cruise": return f.Cruise && f.Cruise !== "—" ? f.Cruise : (f.Ship || "Cruise");
    default: return prettyType(c.type);
  }
};

/* Every leg of a flight component (outbound, inbound, and each multi-city segment),
   with a direction label so the Overview can show the full itinerary at a glance. */
// Raw legs + flightType for the shared <FlightRoute> renderer (needs departure/
// arrival/direction/stops as stored, not the normalized {from,to} shape).
const getFlightRoute = (c) => {
  const r = c._raw || {};
  const pd = (typeof r.productDetails === "string"
    ? (() => { try { return JSON.parse(r.productDetails); } catch { return {}; } })()
    : r.productDetails) || {};
  return { legs: Array.isArray(pd.legs) ? pd.legs : [], flightType: pd.flightType || pd.tripType || "OW" };
};

const getFlightLegs = (c) => {
  const r = c._raw || {};
  const pd = (typeof r.productDetails === "string"
    ? (() => { try { return JSON.parse(r.productDetails); } catch { return {}; } })()
    : r.productDetails) || {};
  const legs = Array.isArray(pd.legs) ? pd.legs : [];
  const flightType = pd.flightType || pd.tripType || "";
  const isRT = /rt|round|return/i.test(flightType) || (!!pd.outboundPnr && !!pd.inboundPnr);
  const fmtDT = (date, time) => (date ? `${formatDate(date)}${time ? ` · ${time}` : ""}` : "—");
  return legs.map((leg, i) => ({
    key: i,
    dir: leg.direction || (legs.length <= 1 ? "Flight" : isRT ? (i < Math.ceil(legs.length / 2) ? "Outbound" : "Inbound") : `Segment ${i + 1}`),
    from: (leg.departureAirport || "—").toUpperCase(),
    to: (leg.arrivalAirport || "—").toUpperCase(),
    flightNo: [leg.airlineCode, leg.flightNumber].filter(Boolean).join(" ").toUpperCase(),
    dep: fmtDT(leg.departureDate, leg.departureTime),
    arr: fmtDT(leg.arrivalDate, leg.arrivalTime),
  }));
};

const OverviewTab = ({
  booking, components, bookingStatus, totalPrice, clientPaid, clientPaidActual, outstandingAmount, signedOutstanding,
  isLocked, isBookingConfirmed, canConfirm, allComponentsConfirmed, confirmTooltipText,
  onConfirm, generatingDocs, onGenerateDocuments, onSwitchTab,
}) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const allExpanded = components.length > 0 && components.every((c) => expanded[c.id]);
  const toggleAll = () => {
    if (allExpanded) setExpanded({});
    else setExpanded(Object.fromEntries(components.map((c) => [c.id, true])));
  };

  /* ── Money ── */
  // Total Selling Price is always the GROSS customer obligation. Any refund due from
  // cancellations surfaces naturally as customer credit / negative outstanding when
  // paid amount exceeds the booking total — never subtract it from the booking value.
  const netPayable = totalPrice;
  const costPrice = booking.pricing?.costPrice || 0;
  const vat = booking.pricing?.tomsVat || 0;
  const margin = netPayable - costPrice;
  const markupCommission = margin - vat;
  const marginPct = netPayable > 0 ? (margin / netPayable) * 100 : 0;
  const paidPct = netPayable > 0 ? Math.min(100, ((clientPaidActual ?? clientPaid) / netPayable) * 100) : 0;
  const outstandingPct = netPayable > 0 ? (outstandingAmount / netPayable) * 100 : 0;

  /* ── Travelers ── */
  const allTravelers = booking.travelers || [];
  const travelers = allTravelers.filter((t) => t.status !== "Cancelled");
  const cancelledTravelers = allTravelers.filter((t) => t.status === "Cancelled");
  const countType = (t) => travelers.filter((x) => x.type === t).length;
  const adults = countType("Adult");
  const children = countType("Child");
  const infants = countType("Infant");
  const travelersComplete = travelers.length > 0 && travelers.every((t) => t.name && t.name !== "—");
  const travelerGroups = [
    { label: "Adult", plural: "Adults", count: adults, range: "12+ Years", color: "#2563eb", bg: "#eff6ff" },
    { label: "Child", plural: "Children", count: children, range: "2 – 11 Years", color: "#0d7a4f", bg: "#ecfdf5" },
    { label: "Infant", plural: "Infants", count: infants, range: "0 – 23 Months", color: "#b06e0a", bg: "#fff7ed" },
  ].filter((g) => g.count > 0);

  /* ── Travel dates (across all components) ── */
  const allDates = components.flatMap(ovComponentDates);
  const range = ovDateRange(allDates);
  const travelDatesText = range
    ? `${formatDate(range.min)} – ${formatDate(range.max)}`
    : "—";
  const nights = range ? Math.max(0, Math.round((range.max - range.min) / 86400000)) : 0;

  /* ── Validation checks ── */
  const activeComps = components.filter((c) => c.status !== "Cancelled");
  const assignmentsOk = activeComps.length > 0 && activeComps.every((c) => c.travelersCount > 0);
  const pricingOk = activeComps.length > 0 && activeComps.every((c) => Number(c.sellingPrice) > 0);
  const refOf = (c) => {
    const r = c._raw || {};
    const pd = (typeof r.productDetails === "string" ? (() => { try { return JSON.parse(r.productDetails); } catch { return {}; } })() : r.productDetails) || {};
    return r.supplierReference || pd.supplierReservationNumber || pd.supplierRefNumber || "";
  };
  const refsOk = activeComps.length > 0 && activeComps.every((c) => !!refOf(c));
  const docsOk = allComponentsConfirmed;
  const checks = [
    { label: "Travelers", sub: travelersComplete ? "All travelers completed" : "Some travelers incomplete", ok: travelersComplete },
    { label: "Products & Assignments", sub: assignmentsOk ? "All products assigned" : "Unassigned products remain", ok: assignmentsOk },
    { label: "Pricing", sub: pricingOk ? "All products priced" : "Pricing incomplete", ok: pricingOk },
    { label: "Supplier References", sub: refsOk ? "All references entered" : "References missing", ok: refsOk },
    { label: "VAT & Taxes", sub: "Calculated", ok: true },
    { label: "Documents", sub: docsOk ? "Ready to generate" : "Confirm booking first", ok: docsOk },
  ];
  const passedChecks = checks.filter((c) => c.ok).length;

  /* ── Warnings ── */
  const warnings = [];
  activeComps.filter((c) => c.status !== "Confirmed").forEach((c) =>
    warnings.push(`${prettyType(c.type)} is currently ${(c.status || "pending").toLowerCase()}.`)
  );
  if (outstandingAmount > 0) warnings.push(`Outstanding balance of ${fmt(outstandingAmount)} remains to be collected.`);

  /* ── Special Services (SSR) — active services on the booking ── */
  const ssrStatusStyle = (status) => {
    switch (status) {
      case "Confirmed": return { bg: "#eef9f3", color: "#0d7a4f" };
      case "Cancelled":
      case "Denied":    return { bg: "#fef2f2", color: "#c42b2b" };
      default:          return { bg: "#fef3e2", color: "#b06e0a" };
    }
  };
  const travelerNameById = Object.fromEntries(
    (booking.travelers || []).map((t) => [String(t.id), t.name])
  );
  const ssrList = (booking.ssrs || []).filter((s) => s.status !== "Cancelled" && !s.deletedAt && !s.deleted_at);
  const ssrTotal = ssrList.reduce((sum, s) => sum + (s.sellingPrice || 0) * (s.quantity || 1), 0);
  const ssrQtyTotal = ssrList.reduce((sum, s) => sum + (s.quantity || 1), 0);
  const ssrConfirmedCount = ssrList.filter((s) => s.status === "Confirmed").length;
  const ssrPendingCount = ssrList.length - ssrConfirmedCount;
  const ssrCatCounts = {};
  ssrList.forEach((s) => { ssrCatCounts[s.ssrCategory] = (ssrCatCounts[s.ssrCategory] || 0) + 1; });
  /* group SSRs by traveler for a cleaner, scannable layout */
  const ssrGroups = (() => {
    const map = new Map();
    ssrList.forEach((s) => {
      const key = String(s.travelerId || "__unassigned__");
      if (!map.has(key)) map.set(key, { key, name: travelerNameById[String(s.travelerId)] || "Unassigned", items: [], total: 0 });
      const g = map.get(key);
      g.items.push(s);
      g.total += (s.sellingPrice || 0) * (s.quantity || 1);
    });
    return Array.from(map.values());
  })();

  /* ── Post-booking modifications — product + traveler, with a financial impact ── */
  const ovNum = (v) => parseFloat(v || 0);
  const modList = [
    ...(booking.productModifications || []).map((m) => ({
      key: `prod-${m.id}`, kind: "product",
      typeLabel: MOD_TYPE_LABELS[m.modificationType] || m.modificationType || "Modification",
      title: m.modificationTitle || "—",
      createdAt: m.createdAt,
      supplierCost: ovNum(m.supplierCost), agencyFee: ovNum(m.agencyFee),
      refundAmount: ovNum(m.refundAmount), impact: ovNum(m.totalModificationAmount),
    })),
    ...(booking.travelerModifications || [])
      .filter((m) => ovNum(m.supplierCost) || ovNum(m.agencyFee) || ovNum(m.refundAmount) || ovNum(m.totalModificationAmount))
      .map((m) => ({
        key: `trav-${m.id}`, kind: "traveler",
        typeLabel: ACTION_TYPE_LABELS[m.actionType] || m.actionType || "Traveler Modification",
        title: m.modificationTitle || ACTION_TYPE_LABELS[m.actionType] || "—",
        createdAt: m.createdAt,
        supplierCost: ovNum(m.supplierCost), agencyFee: ovNum(m.agencyFee),
        refundAmount: ovNum(m.refundAmount), impact: ovNum(m.totalModificationAmount),
      })),
  ].sort((a, z) => (a.createdAt ? new Date(a.createdAt).getTime() : 0) - (z.createdAt ? new Date(z.createdAt).getTime() : 0));
  const modTotal = modList.reduce((sum, m) => sum + m.impact, 0);

  return (
    <div className={styles.ovTab}>
      {/* ── Title row ── */}
      <div className={styles.ovHeadRow}>
        <div>
          <div className={styles.ovHeadTitle}><ClipboardCheck size={20} /> Booking Overview</div>
          <div className={styles.ovHeadSub}>Review all booking details before finalizing</div>
        </div>
        <div className={styles.ovHeadStatus}>
          <span className={styles.ovHeadStatusLabel}>Booking Status</span>
          <span className={`${styles.badge} ${statusClass(bookingStatus)}`}><span className={styles.dot} />{bookingStatus}</span>
        </div>
      </div>

      {/* ── Summary band ── */}
      <div className={styles.ovSummary}>
        {[
          { Icon: Briefcase, label: "Booking", value: booking.ref, sub: booking.origin },
          { Icon: UserCircle, label: "Customer", value: booking.customer?.name, sub: booking.customer?.email },
          { Icon: Calendar, label: "Travel Dates", value: travelDatesText, sub: nights ? `${nights} Night${nights !== 1 ? "s" : ""}` : "" },
          { Icon: Users, label: "Travelers", value: `${travelers.length} Active${cancelledTravelers.length ? ` · ${cancelledTravelers.length} Cancelled` : ""}`, sub: [adults && `${adults} Adult${adults !== 1 ? "s" : ""}`, children && `${children} Child`, infants && `${infants} Infant`].filter(Boolean).join(", ") },
          { Icon: Package, label: "Booking Type", value: booking.saleType === "PACKAGE_TRAVEL" ? "Package Booking" : "Separate Services", sub: `VAT: ${booking.pricing?.vatRegime || "—"}` },
        ].map((cell) => (
          <div key={cell.label} className={styles.ovSummaryCell}>
            <div className={styles.ovSummaryIcon}><cell.Icon size={16} /></div>
            <div className={styles.ovSummaryTxt}>
              <span className={styles.ovSummaryLabel}>{cell.label}</span>
              <span className={styles.ovSummaryValue}>{cell.value || "—"}</span>
              {cell.sub && <span className={styles.ovSummarySub}>{cell.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* ── KPI cards ── */}
      <div className={styles.ovKpiGrid}>
        <div className={`${styles.ovKpi} ${styles.ovKpiBlue}`}>
          <div className={styles.ovKpiTop}><span className={styles.ovKpiLabel}>Total Selling Price</span><DollarSign size={15} /></div>
          <div className={styles.ovKpiValue}>{fmt(netPayable)}</div>
          <div className={styles.ovKpiSub}>Incl. VAT</div>
        </div>
        <div className={`${styles.ovKpi} ${styles.ovKpiPurple}`}>
          <div className={styles.ovKpiTop}><span className={styles.ovKpiLabel}>Total Purchase Price</span><ShoppingCart size={15} /></div>
          <div className={styles.ovKpiValue}>{fmt(costPrice)}</div>
          <div className={styles.ovKpiSub}>Incl. VAT</div>
        </div>
        <div className={`${styles.ovKpi} ${styles.ovKpiGreen}`}>
          <div className={styles.ovKpiTop}><span className={styles.ovKpiLabel}>Gross Margin</span><TrendingUp size={15} /></div>
          <div className={styles.ovKpiValue}>{fmt(margin)}</div>
          <div className={styles.ovKpiSub}>{pctFmt(marginPct)}</div>
        </div>
        <div className={`${styles.ovKpi} ${styles.ovKpiAmber}`}>
          <div className={styles.ovKpiTop}><span className={styles.ovKpiLabel}>Outstanding Balance</span><AlertCircle size={15} /></div>
          <div className={styles.ovKpiValue}>{fmt(signedOutstanding)}</div>
          <div className={styles.ovKpiSub}>{signedOutstanding < -0.005 ? "Refund due to customer" : `${pctFmt(outstandingPct)} of total`}</div>
        </div>
      </div>

      {/* ── Travelers summary ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}><Users size={16} /> Travelers Summary</div>
            <div className={styles.sectionSubtitle}>
              {travelers.length} Active{cancelledTravelers.length ? ` · ${cancelledTravelers.length} Cancelled` : ""} · {allTravelers.length} Total
            </div>
          </div>
          <button className={styles.ovLinkBtn} onClick={() => onSwitchTab("travelers")}>View &amp; Manage Travelers</button>
        </div>
        <div className={styles.ovTravelerRow}>
          {travelerGroups.length > 0 ? travelerGroups.map((g) => (
            <div key={g.label} className={styles.ovTravelerStat}>
              <div className={styles.ovTravelerIcon} style={{ background: g.bg, color: g.color }}><User size={18} /></div>
              <div className={styles.ovTravelerTxt}>
                <span className={styles.ovTravelerCount}>{g.count} {g.count !== 1 ? g.plural : g.label}</span>
                <span className={styles.ovTravelerRange}>{g.range}</span>
              </div>
            </div>
          )) : <div className={styles.ovTravelerEmpty}>No travelers added yet.</div>}
          {cancelledTravelers.length > 0 && (
            <div className={styles.ovTravelerStat}>
              <div className={styles.ovTravelerIcon} style={{ background: "#fef2f2", color: "#dc2626" }}><XCircle size={18} /></div>
              <div className={styles.ovTravelerTxt}>
                <span className={styles.ovTravelerCount}>{cancelledTravelers.length} Cancelled</span>
                <span className={styles.ovTravelerRange}>{cancelledTravelers.map((t) => t.name || "—").join(", ")}</span>
              </div>
            </div>
          )}
          <div className={`${styles.ovTravelerComplete} ${travelersComplete ? styles.ovComplete : styles.ovIncomplete}`}>
            {travelersComplete ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
            <div>
              <div className={styles.ovTravelerCompleteTitle}>{travelersComplete ? "All travelers are complete" : "Traveler details incomplete"}</div>
              <div className={styles.ovTravelerCompleteSub}>{travelersComplete ? "Personal details and contacts are completed." : "Some travelers are missing required details."}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Products & Services ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}><Briefcase size={16} /> Products &amp; Services</div>
          <div className={styles.ovProdHeadRight}>
            <span className={styles.ovCountPill}>{components.length} Product{components.length !== 1 ? "s" : ""}</span>
            {components.length > 0 && (
              <button className={styles.ovLinkBtn} onClick={toggleAll}>{allExpanded ? "Collapse All" : "Expand All"}</button>
            )}
          </div>
        </div>
        <div className={styles.ovProdList}>
          {components.map((c) => {
            const CIcon = ICON_MAP[c.iconKey] || FileText;
            const ps = PRODUCT_STYLE[c.type] || { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };
            const r = ovDateRange(ovComponentDates(c));
            const dateLine = r ? (formatDate(r.min) === formatDate(r.max) ? formatDate(r.min) : `${formatDate(r.min)} – ${formatDate(r.max)}`) : "";
            const ref = refOf(c);
            const pnr = (c.fields || []).find((f) => f.label === "PNR" && f.value && f.value !== "—")?.value;
            const isOpen = !!expanded[c.id];
            const flightLegs = c.type === "Flight" ? getFlightLegs(c) : [];
            const flightRoute = c.type === "Flight" ? getFlightRoute(c) : null;
            return (
              <div key={c.id} className={`${styles.ovProdRow} ${isOpen ? styles.ovProdRowOpen : ""}`}>
                <button className={styles.ovProdMain} onClick={() => toggle(c.id)}>
                  <span className={styles.ovProdIcon} style={{ background: ps.bg, color: ps.color, borderColor: ps.border }}><CIcon size={16} /></span>
                  <div className={styles.ovProdInfo}>
                    <span className={styles.ovProdType}>{prettyType(c.type)}</span>
                    <span className={styles.ovProdName}>{ovProductSubtitle(c)}</span>
                    {dateLine && <span className={styles.ovProdDate}>{dateLine}</span>}
                  </div>
                  <div className={styles.ovProdSupplier}>
                    <span className={styles.ovProdSupplierName}>{c.supplier}</span>
                    {ref && <span className={styles.ovProdRef}>Ref: {ref}</span>}
                    {pnr && <span className={styles.ovProdRef}>PNR: {pnr}</span>}
                  </div>
                  <span className={`${styles.badge} ${statusClass(c.status)}`}><span className={styles.dot} />{c.status}</span>
                  <div className={styles.ovProdTrav}>
                    <span className={styles.ovProdTravLabel}>Travelers</span>
                    <span className={styles.ovProdTravValue}>{c.travelersCount}</span>
                  </div>
                  <div className={styles.ovProdPrice}>
                    <span className={styles.ovProdPriceValue}>{fmt(c.sellingPrice)}</span>
                    <span className={styles.ovProdPriceLabel}>Selling Price</span>
                  </div>
                  <ChevronDown size={16} className={`${styles.ovProdChevron} ${isOpen ? styles.ovProdChevronOpen : ""}`} />
                </button>
                {flightRoute && flightRoute.legs.length > 0 && (
                  <div className={styles.ovRouteSummary} style={{ padding: "8px 16px 0" }}>
                    <FlightRoute legs={flightRoute.legs} flightType={flightRoute.flightType} />
                  </div>
                )}
                {flightLegs.length > 0 && (
                  <div className={styles.ovLegs}>
                    {flightLegs.map((leg) => (
                      <div key={leg.key} className={styles.ovLeg}>
                        <span className={styles.ovLegDir} data-dir={leg.dir}>{leg.dir}</span>
                        <span className={styles.ovLegRoute}>
                          <span className={styles.ovLegAir}>{leg.from}</span>
                          <Plane size={12} className={styles.ovLegPlane} />
                          <span className={styles.ovLegAir}>{leg.to}</span>
                        </span>
                        {leg.flightNo && <span className={styles.ovLegNo}>{leg.flightNo}</span>}
                        <span className={styles.ovLegTimes}>
                          <span className={styles.ovLegTime}>{leg.dep}</span>
                          <span className={styles.ovLegTimeSep}>→</span>
                          <span className={styles.ovLegTime}>{leg.arr}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {isOpen && (
                  <div className={styles.ovProdDetail}>
                    {(c.fields || []).map((f, i) => (
                      <div key={i} className={styles.ovProdField}>
                        <span className={styles.ovProdFieldLabel}>{f.label}</span>
                        <span className={styles.ovProdFieldValue}>{f.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {components.length === 0 && <div className={styles.ovTravelerEmpty}>No products added yet.</div>}
        </div>
      </div>

      {/* ── Special Services (SSR) ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}><Sparkles size={16} /> Special Services (SSR)</div>
            <div className={styles.sectionSubtitle}>
              {ssrList.length} service{ssrList.length !== 1 ? "s" : ""}
              {Object.keys(ssrCatCounts).length > 0 && ` · ${Object.keys(ssrCatCounts).length} categor${Object.keys(ssrCatCounts).length !== 1 ? "ies" : "y"}`}
            </div>
          </div>
          <div className={styles.ovProdHeadRight}>
            {ssrList.length > 0 && <span className={styles.ovCountPill}>{fmt(ssrTotal)}</span>}
            <button className={styles.ovLinkBtn} onClick={() => onSwitchTab("travelers")}>Manage SSRs</button>
          </div>
        </div>
        {ssrList.length > 0 ? (
          <>
            {/* stat strip */}
            <div className={styles.ovSsrStats}>
              <div className={styles.ovSsrStat}>
                <span className={`${styles.ovSsrStatIcon} ${styles.ovSsrStatIconBlue}`}><Sparkles size={15} /></span>
                <div className={styles.ovSsrStatTxt}>
                  <span className={styles.ovSsrStatVal}>{ssrQtyTotal}</span>
                  <span className={styles.ovSsrStatLabel}>Total Services</span>
                </div>
              </div>
              <div className={styles.ovSsrStat}>
                <span className={`${styles.ovSsrStatIcon} ${styles.ovSsrStatIconGreen}`}><CheckCircle size={15} /></span>
                <div className={styles.ovSsrStatTxt}>
                  <span className={styles.ovSsrStatVal}>{ssrConfirmedCount}</span>
                  <span className={styles.ovSsrStatLabel}>Confirmed</span>
                </div>
              </div>
              <div className={styles.ovSsrStat}>
                <span className={`${styles.ovSsrStatIcon} ${styles.ovSsrStatIconAmber}`}><Clock size={15} /></span>
                <div className={styles.ovSsrStatTxt}>
                  <span className={styles.ovSsrStatVal}>{ssrPendingCount}</span>
                  <span className={styles.ovSsrStatLabel}>Pending</span>
                </div>
              </div>
              <div className={styles.ovSsrStat}>
                <span className={`${styles.ovSsrStatIcon} ${styles.ovSsrStatIconPurple}`}><Euro size={15} /></span>
                <div className={styles.ovSsrStatTxt}>
                  <span className={styles.ovSsrStatVal}>{fmt(ssrTotal)}</span>
                  <span className={styles.ovSsrStatLabel}>Total Value</span>
                </div>
              </div>
            </div>

            {/* category distribution */}
            <div className={styles.ovSsrCats}>
              {Object.entries(ssrCatCounts).sort((a, z) => z[1] - a[1]).map(([code, count]) => {
                const cat = SSR_CATEGORIES.find((c) => c.code === code) || { name: code, color: "#6b7280", bg: "#f9fafb" };
                return (
                  <span key={code} className={styles.ovSsrCatChip} style={{ background: cat.bg, color: cat.color }}>
                    <span className={styles.ovSsrCatChipIcon}><SSRCatIconRender code={code} /></span>
                    {cat.name} <strong>{count}</strong>
                  </span>
                );
              })}
            </div>

            {/* SSRs grouped by traveler */}
            <div className={styles.ovSsrGroups}>
              {ssrGroups.map((g) => {
                const initial = (g.name || "?").trim().charAt(0).toUpperCase() || "?";
                return (
                  <div key={g.key} className={styles.ovSsrGroup}>
                    <div className={styles.ovSsrGroupHead}>
                      <span className={styles.ovSsrAvatar}>{initial}</span>
                      <div className={styles.ovSsrGroupInfo}>
                        <span className={styles.ovSsrGroupName}>{g.name}</span>
                        <span className={styles.ovSsrGroupMeta}>{g.items.length} service{g.items.length !== 1 ? "s" : ""}</span>
                      </div>
                      <span className={styles.ovSsrGroupTotal}>{fmt(g.total)}</span>
                    </div>
                    <div className={styles.ovSsrList}>
                      {g.items.map((s) => {
                        const cat = SSR_CATEGORIES.find((c) => c.code === s.ssrCategory) || { color: "#6b7280", bg: "#f9fafb" };
                        const st = ssrStatusStyle(s.status);
                        return (
                          <div key={s.id} className={styles.ovSsrRow}>
                            <span className={styles.ovSsrIcon} style={{ background: cat.bg, color: cat.color }}>
                              <SSRCatIconRender code={s.ssrCategory} />
                            </span>
                            <span className={styles.ovSsrCode} style={{ background: cat.bg, color: cat.color }}>{s.ssrCode}</span>
                            <div className={styles.ovSsrInfo}>
                              <span className={styles.ovSsrName}>{s.ssrName || s.ssrCode}{s.quantity > 1 ? ` ×${s.quantity}` : ""}</span>
                              {s.ssrDescription && <span className={styles.ovSsrDesc}>{s.ssrDescription}</span>}
                            </div>
                            <span className={styles.ovSsrPrice}>{fmt((s.sellingPrice || 0) * (s.quantity || 1))}</span>
                            <span className={styles.ovSsrStatus} style={{ background: st.bg, color: st.color }}>{s.status}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className={styles.ovEmptyState}>
            <span className={styles.ovEmptyIcon}><Sparkles size={20} /></span>
            <div>
              <div className={styles.ovEmptyTitle}>No special services added</div>
              <div className={styles.ovEmptySub}>Seats, meals, baggage and other SSRs will appear here once added on the Travelers tab.</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Pricing summary ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}><Euro size={16} /> Pricing Summary</div>
          <button className={styles.ovLinkBtn} onClick={() => onSwitchTab("pricing")}>View Price Breakdown</button>
        </div>
        <div className={styles.ovPricingGrid}>
          <div className={styles.ovPricingRows}>
            <div className={styles.ovPricingLine}><span>Total Selling Price (Incl. VAT)</span><span>{fmt(netPayable)}</span></div>
            <div className={styles.ovPricingLine}><span>Total Purchase Price (Incl. VAT)</span><span>{fmt(costPrice)}</span></div>
            <div className={styles.ovPricingLine}><span>Total Markup / Commission</span><span>{fmt(markupCommission)}</span></div>
            <div className={styles.ovPricingLine}><span>Total VAT</span><span>{fmt(vat)}</span></div>
          </div>
          <div className={styles.ovMarginBox}>
            <span className={styles.ovMarginLabel}>Gross Margin</span>
            <div className={styles.ovMarginValue}>{fmt(margin)}</div>
            <div className={styles.ovMarginSub}>{pctFmt(marginPct)} of selling price</div>
            <svg className={styles.ovSparkline} viewBox="0 0 120 36" preserveAspectRatio="none" aria-hidden="true">
              <polyline points="0,28 20,24 40,26 60,16 80,18 100,8 120,4" fill="none" stroke="#0d7a4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="120" cy="4" r="2.5" fill="#0d7a4f" />
            </svg>
          </div>
        </div>
      </div>

      {/* ── Post-Booking Modifications ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}><Wrench size={16} /> Post-Booking Modifications</div>
            <div className={styles.sectionSubtitle}>{modList.length} modification{modList.length !== 1 ? "s" : ""} applied after booking</div>
          </div>
          <div className={styles.ovProdHeadRight}>
            {modList.length > 0 && (
              <span className={`${styles.ovCountPill} ${modTotal >= 0 ? styles.ovPillPos : styles.ovPillNeg}`}>
                {modTotal >= 0 ? "+" : "−"}{fmt(Math.abs(modTotal))}
              </span>
            )}
            <button className={styles.ovLinkBtn} onClick={() => onSwitchTab("pricing")}>View in Pricing</button>
          </div>
        </div>
        {modList.length > 0 ? (
          <div className={styles.ovModList}>
            {modList.map((m) => (
              <div key={m.key} className={styles.ovModRow}>
                <span className={styles.ovModKindIcon} data-kind={m.kind}>
                  {m.kind === "traveler" ? <User size={14} /> : <Package size={14} />}
                </span>
                <div className={styles.ovModInfo}>
                  <span className={styles.ovModType}>
                    {m.typeLabel}
                    <span className={styles.ovModKindTag} data-kind={m.kind}>{m.kind === "traveler" ? "Traveler" : "Product"}</span>
                  </span>
                  <span className={styles.ovModTitle}>{m.title}</span>
                  {m.createdAt && <span className={styles.ovModDate}><Clock size={11} /> {formatDate(m.createdAt)}</span>}
                </div>
                <div className={styles.ovModFin}>
                  <span className={styles.ovModFinItem}><span className={styles.ovModFinLabel}>Supplier</span><span className={styles.ovModFinVal}>{fmt(m.supplierCost)}</span></span>
                  <span className={styles.ovModFinItem}><span className={styles.ovModFinLabel}>Fee</span><span className={styles.ovModFinVal}>{fmt(m.agencyFee)}</span></span>
                  <span className={styles.ovModFinItem}><span className={styles.ovModFinLabel}>Refund</span><span className={styles.ovModFinVal}>{fmt(m.refundAmount)}</span></span>
                </div>
                <span className={`${styles.ovModImpact} ${m.impact >= 0 ? styles.ovModImpactPos : styles.ovModImpactNeg}`}>
                  <span className={styles.ovModImpactLabel}>Net impact</span>
                  <span className={styles.ovModImpactVal}>{m.impact >= 0 ? "+" : "−"}{fmt(Math.abs(m.impact))}</span>
                </span>
              </div>
            ))}
            <div className={styles.ovModFooter}>
              <span>Net impact of {modList.length} modification{modList.length !== 1 ? "s" : ""}</span>
              <span className={modTotal >= 0 ? styles.ovModImpactPos : styles.ovModImpactNeg}>
                {modTotal >= 0 ? "+" : "−"}{fmt(Math.abs(modTotal))}
              </span>
            </div>
          </div>
        ) : (
          <div className={styles.ovEmptyState}>
            <span className={styles.ovEmptyIcon}><Wrench size={20} /></span>
            <div>
              <div className={styles.ovEmptyTitle}>No post-booking modifications</div>
              <div className={styles.ovEmptySub}>Changes after confirmation — name changes, date/route changes, cancellation fees — are tracked here with their financial impact.</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Validation & checks ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}><Shield size={16} /> Validation &amp; Checks</div>
            <div className={styles.sectionSubtitle}>{passedChecks} of {checks.length} checks passed</div>
          </div>
        </div>
        <div className={styles.ovCheckGrid}>
          {checks.map((ck) => (
            <div key={ck.label} className={`${styles.ovCheckItem} ${ck.ok ? styles.ovCheckOk : styles.ovCheckWarn}`}>
              {ck.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              <div className={styles.ovCheckTxt}>
                <span className={styles.ovCheckLabel}>{ck.label}</span>
                <span className={styles.ovCheckSub}>{ck.sub}</span>
              </div>
            </div>
          ))}
        </div>
        {warnings.length > 0 && (
          <div className={styles.ovWarnBox}>
            <div className={styles.ovWarnHead}><AlertTriangle size={15} /> {warnings.length} Warning{warnings.length !== 1 ? "s" : ""}</div>
            <ul className={styles.ovWarnList}>
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* ── Financial status ── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}><Landmark size={16} /> Financial Status</div>
          <button className={styles.ovLinkBtn} onClick={() => onSwitchTab("payments")}>View Payment Details</button>
        </div>
        <div className={styles.ovFinGrid}>
          <div className={styles.ovFinCell}>
            <span className={styles.ovFinLabel}>Booking Total</span>
            <span className={styles.ovFinValue}>{fmt(netPayable)}</span>
          </div>
          <div className={styles.ovFinCell}>
            <span className={styles.ovFinLabel}>Paid Amount</span>
            <span className={`${styles.ovFinValue} ${styles.ovFinGreen}`}>{fmt(clientPaidActual ?? clientPaid)}</span>
            <span className={styles.ovFinSub}>{pctFmt(paidPct)} paid</span>
          </div>
          <div className={styles.ovFinCell}>
            <span className={styles.ovFinLabel}>Outstanding Balance</span>
            <span className={`${styles.ovFinValue} ${Math.abs(signedOutstanding) > 0.005 ? styles.ovFinAmber : styles.ovFinGreen}`}>{fmt(signedOutstanding)}</span>
            <span className={styles.ovFinSub}>{signedOutstanding < -0.005 ? "Refund due to customer" : `${pctFmt(outstandingPct)} remaining`}</span>
          </div>
          <div className={styles.ovFinCell}>
            <span className={styles.ovFinLabel}>Currency</span>
            <span className={styles.ovFinValue}>EUR</span>
          </div>
        </div>
        <div className={styles.ovPayTrack}>
          <div className={styles.ovPayFill} style={{ width: `${Math.min(100, Math.max(0, paidPct))}%` }} />
        </div>
      </div>

      {/* ── Finalize action bar ── */}
      <div className={styles.ovFooter}>
        <button className={styles.btnSecondary} onClick={() => onSwitchTab("itinerary")}>
          <ArrowLeft size={14} /> Back to Edit
        </button>
        <div className={styles.ovFooterRight}>
          <LoadingButton
            className={styles.btnSecondary}
            disabled={isLocked}
            loading={generatingDocs}
            loadingText="Generating…"
            icon={<FilePlus size={14} />}
            onClick={onGenerateDocuments}
          >
            Generate Documents
          </LoadingButton>
          {!isBookingConfirmed ? (
            <button
              className={`${styles.ovConfirmBtn} ${!canConfirm ? styles.ovConfirmBtnDisabled : ""}`}
              disabled={!canConfirm}
              onClick={onConfirm}
              title={confirmTooltipText}
            >
              <Check size={16} /> Confirm Booking
            </button>
          ) : (
            <span className={styles.ovConfirmedTag}><CheckCircle size={16} /> Booking Confirmed</span>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomerOverviewTab = ({ customer, onEdit, onChangeCustomer }) => {
  if (customer.type === "Company") {
    return (
      <div>
        <div className={styles.changeCustomerBar}>
          <div className={styles.changeCustomerInfo}>
            <UserCircle size={18} />
            <span>Current Customer: <strong>{customer.tradingName || customer.name || "—"}</strong></span>
            <span className={styles.changeCustomerBadge}>Company</span>
          </div>
          {onChangeCustomer && (
            <button className={styles.btnChangeCustomer} onClick={onChangeCustomer}>
              <RefreshCw size={13} /> Change Customer
            </button>
          )}
        </div>
        <div className={styles.customerOverviewGrid}>
          <CustomerFieldSection icon={UserCircle} title="Company Identity" onEdit={onEdit} fields={[
          { label: "Trading Name", value: customer.tradingName || "—" },
          { label: "Legal Name", value: customer.legalName || "—" },
          { label: "VAT Number", value: customer.vatNumber || "—" },
          { label: "Industry", value: customer.industry || "—" },
          { label: "Website", value: customer.website || "—" },
        ]} />
        <CustomerFieldSection icon={Phone} title="Registered Address" onEdit={onEdit} fields={[
          { label: "Street", value: customer.street || "—" },
          { label: "House No.", value: customer.houseNumber || "—" },
          { label: "Box No.", value: customer.boxNumber || "—" },
          { label: "City", value: customer.city || "—" },
          { label: "Postal Code", value: customer.postalCode || "—" },
          { label: "Country", value: customer.country || "—" },
        ]} />
        <CustomerFieldSection icon={Euro} title="Invoicing Details" fields={[
          { label: "Invoice Email", value: customer.invoiceEmail || "—" },
          { label: "Payment Terms", value: customer.paymentTerms ? `${customer.paymentTerms} days` : "—" },
          { label: "Invoice Address", value: customer.invoicingAddress || "—" },
        ]} />
        <CustomerFieldSection icon={User} title="Primary Contact" fields={[
          { label: "First Name", value: customer.contactFirstName || "—" },
          { label: "Last Name", value: customer.contactLastName || "—" },
          { label: "Email", value: customer.contactEmail || "—" },
          { label: "Phone", value: customer.contactPhone || "—" },
          { label: "Emergency contact", value: customer.emergencyPhone || "—" },
          { label: "Role", value: customer.contactRole || "—" },
          { label: "Language", value: customer.contactLanguage || "—" },
        ]} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.changeCustomerBar}>
        <div className={styles.changeCustomerInfo}>
          <UserCircle size={18} />
          <span>Current Customer: <strong>{customer.firstName || customer.name?.split(" ")[0] || ""} {(customer.lastName || customer.name?.split(" ").slice(1).join(" ") || "").toUpperCase()}</strong></span>
          <span className={styles.changeCustomerBadge}>Private</span>
        </div>
        {onChangeCustomer && (
          <button className={styles.btnChangeCustomer} onClick={onChangeCustomer}>
            <RefreshCw size={13} /> Change Customer
          </button>
        )}
      </div>
      <div className={styles.customerOverviewGrid}>
        <CustomerFieldSection icon={User} title="Personal Information" onEdit={onEdit} fields={[
          { label: "First Name", value: customer.firstName || customer.name?.split(" ")[0] || "—" },
          { label: "Last Name", value: (customer.lastName || customer.name?.split(" ").slice(1).join(" ") || "").toUpperCase() || "—" },
          { label: "Date of Birth", value: customer.dateOfBirth || "—" },
          { label: "Gender", value: customer.gender || "—" },
          { label: "Nationality", value: customer.nationality || "—" },
          { label: "Language", value: customer.language || "—" },
        ]} />
        <CustomerFieldSection icon={Phone} title="Contact Information" onEdit={onEdit} fields={[
          { label: "Email", value: customer.email || "—" },
          { label: "Phone", value: customer.phone || "—" },
          // Someone who is NOT travelling. Asked at checkout for exactly this moment: an
          // agent with a problem on a trip needs a number that is not on that trip.
          { label: "Emergency contact", value: customer.emergencyPhone || "—" },
          { label: "Street", value: customer.street || "—" },
          { label: "House No.", value: customer.houseNumber || "—" },
          { label: "Box No.", value: customer.boxNumber || "—" },
          { label: "City", value: customer.city || "—" },
          { label: "Postal Code", value: customer.postalCode || "—" },
          { label: "Country", value: customer.country || "—" },
        ]} />
      </div>
    </div>
  );
};

/* ItineraryTab is now imported from ./ItineraryTab.jsx */

/* ── SSR Category SVG Icons (clean, no emojis) ── */
const SeatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 18v-6a5 5 0 0 1 10 0v6" /><path d="M5 18h14" /><path d="M6 10V5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5" />
  </svg>
);
const MealIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
  </svg>
);
const HandBagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M12 12v4" />
  </svg>
);
const BaggageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 20h0a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h0" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><circle cx="8" cy="20" r="2" /><circle cx="16" cy="20" r="2" /><path d="M12 6v14" />
  </svg>
);
const AssistIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="4.5" r="2.5" /><path d="m10 10-1.5 5h7L14 10" /><path d="M15.5 15l1.5 6" /><path d="M8.5 15L7 21" /><circle cx="12" cy="12" r="0.5" />
  </svg>
);
const SportIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" />
  </svg>
);
const PawIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="4" r="2" /><circle cx="4.5" cy="9" r="2" /><circle cx="17.5" cy="9" r="2" /><circle cx="8" cy="14" r="2" /><circle cx="16" cy="14" r="2" /><path d="M12 18c-3 0-5-2.5-5-4 0-2 2-3 5-3s5 1 5 3c0 1.5-2 4-5 4z" />
  </svg>
);
const ClipboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" /><path d="M9 2h6v3H9z" /><path d="M9 12h6" /><path d="M9 16h4" />
  </svg>
);
const SSRPlaneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
  </svg>
);
const SSRRouteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" />
  </svg>
);
const SSRUserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const SSRSearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M10 10l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const SSRXIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const SSRPlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 13 13" fill="none">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const SSRArrowLeftIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M8 2.5L4 6.5l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SSR_CATEGORY_ICONS = {
  SEAT: SeatIcon,
  MEAL: MealIcon,
  HANDBAG: HandBagIcon,
  BAGGAGE: BaggageIcon,
  ASSIST: AssistIcon,
  SPORT: SportIcon,
  PET: PawIcon,
  OTHER: ClipboardIcon,
};

const SSRCatIconRender = ({ code, style }) => {
  const Icon = SSR_CATEGORY_ICONS[code] || ClipboardIcon;
  return <Icon />;
};

const TravelersTab = ({ travelers, travelerModifications = [], bookingId, isLocked, onRefetch, onSwitchTab, ssrs = [], components = [], setSSRPricingApi }) => {
  const { showToast } = useToast();
  const [actionMenuId, setActionMenuId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTraveler, setEditingTraveler] = useState(null);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removingTraveler, setRemovingTraveler] = useState(null);
  const [postBookingModalOpen, setPostBookingModalOpen] = useState(false);
  const [postBookingTraveler, setPostBookingTraveler] = useState(null);
  const [ssrPricingEdit, setSSRPricingEdit] = useState(null);
  const [ssrPricingSaving, setSSRPricingSaving] = useState(false);
  const [addMode, setAddMode] = useState("search");
  const [addForm, setAddForm] = useState({ firstName: "", lastName: "", gender: "", dateOfBirth: "" });
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", gender: "", dateOfBirth: "" });
  const [modForm, setModForm] = useState({ actionType: "", modificationTitle: "", internalNotes: "", newFirstName: "", newLastName: "", supplierCost: "", agencyFee: "", refundAmount: "", regenerateDocuments: true, notifyCustomer: true });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  /* ── SSR Management State ── */
  const [ssrModalOpen, setSSRModalOpen] = useState(false);
  const [ssrTraveler, setSSRTraveler] = useState(null);
  const [ssrActiveCategory, setSSRActiveCategory] = useState(null);
  const [ssrSearchQuery, setSSRSearchQuery] = useState("");
  const [ssrSelectedFlightId, setSSRSelectedFlightId] = useState("");
  // Segment starts UNSELECTED — the user must explicitly pick All Segments or a
  // specific leg before the SSR category catalog appears.
  const [ssrSelectedLegId, setSSRSelectedLegId] = useState("");
  const [ssrDeleteConfirm, setSSRDeleteConfirm] = useState(null);
  const [expandedSSRTraveler, setExpandedSSRTraveler] = useState(null);

  /* ── SSR API hooks ── */
  const { execute: addSSRsApi, loading: addingSSR } = useAddBookingSSRs();
  const { execute: deleteSSRApi, loading: deletingSSR } = useDeleteBookingSSR();
  const { execute: updateSSRStatusApi } = useUpdateSSRStatus();
  const { execute: updateSSRDetailsApi } = useUpdateSSRDetails();
  const [ssrStatusSavingId, setSSRStatusSavingId] = useState(null);
  /* Inline editor for the SSR free-text detail (ssrDescription) */
  const [ssrDetailEdit, setSSRDetailEdit] = useState(null);
  const [ssrDetailSaving, setSSRDetailSaving] = useState(false);
  const { execute: calculateVAT } = useCalculateVAT();
  const [ssrVatLoading, setSSRVatLoading] = useState(false);
  const ssrVatTimerRef = useRef(null);
  const { data: apiCategories } = useGetSSRCategories();
  const { data: apiItems } = useGetSSRItems({ status: "active", limit: 500 });

  /* ── Derive flights from components ── */
  const flights = useMemo(() =>
    components.filter((c) => c.type === "Flight").map((c) => {
      const raw = c._raw || {};
      const d = raw.productDetails || {};
      const rawLegs = d.legs || [];
      const normLegs = rawLegs.map((leg) => ({
        ...leg,
        departure: (leg.departure || leg.departureAirport || "").toUpperCase(),
        arrival: (leg.arrival || leg.arrivalAirport || "").toUpperCase(),
        date: leg.date || leg.departureDate || "",
      }));
      return { id: c.id, serverId: raw.id, legs: normLegs, supplier: c.supplierName || c.supplier || "", flightType: d.flightType || "OW" };
    }), [components]);
  const hasFlights = flights.length > 0;

  /* ── SSR category list ── */
  const SSR_CAT_STYLE = SSR_CATEGORIES.reduce((acc, c) => { acc[c.code] = { color: c.color, bg: c.bg, icon: c.icon }; return acc; }, {});
  const categoryList = useMemo(() => {
    const source = (apiCategories && apiCategories.length > 0) ? apiCategories : SSR_CATEGORIES;
    return source.map((c) => {
      const style = SSR_CAT_STYLE[c.code] || {};
      return { id: c.id, code: c.code, name: c.name, description: c.description, icon: c.icon || style.icon || "📋", color: c.color || style.color || "#6b7280", bg: style.bg || "#f9fafb" };
    });
  }, [apiCategories]);

  /* ── SSR catalog items ── */
  const ssrCatalog = useMemo(() =>
    (apiItems || []).map((it) => {
      const parts = [];
      if (it.maxWeight != null) parts.push(`Max ${it.maxWeight} kg`);
      if (it.maxSize) parts.push(`Max size ${it.maxSize}`);
      if (it.maxPieces != null) parts.push(`Max ${it.maxPieces} pcs`);
      const unit = it.travelerRequired && it.flightSegmentRequired ? "per person/segment" : it.flightSegmentRequired ? "per segment" : it.travelerRequired ? "per person" : "per item";
      return { id: it.id, code: it.code, label: it.description || it.code, desc: parts.join(" · ") || it.description || "", category: it.category, unit };
    }), [apiItems]);

  /* ── SSR helpers ── */
  const getCatStyle = (code) => categoryList.find((c) => c.code === code) || SSR_CATEGORIES.find((c) => c.code === code) || { icon: "📋", color: "#6b7280", bg: "#f9fafb" };
  const getTravelerSSRs = (travelerId) => ssrs.filter((s) => s.travelerId === travelerId);
  const ssrCountByTraveler = useMemo(() => {
    const map = {};
    ssrs.forEach((s) => { map[s.travelerId] = (map[s.travelerId] || 0) + 1; });
    return map;
  }, [ssrs]);

  /* ── Open SSR Modal ── */
  const openSSRModal = (t) => {
    setSSRTraveler(t);
    setSSRActiveCategory(null);
    setSSRSearchQuery("");
    setSSRSelectedFlightId(flights.length === 1 ? flights[0].id : "");
    setSSRSelectedLegId(""); // nothing pre-selected — user picks a segment first
    setSSRModalOpen(true);
    setActionMenuId(null);
  };

  // The SSR category catalog is gated on a chosen SEGMENT (not just a chosen
  // flight). A flight with no legs is ready immediately; otherwise the user must
  // pick All Segments or a specific leg first.
  const ssrSelFlightLegs = (flights.find((f) => f.id === ssrSelectedFlightId)?.legs) || [];
  const ssrSegmentReady = !!ssrSelectedFlightId && (ssrSelFlightLegs.length === 0 || !!ssrSelectedLegId);
  const ssrNeedsSegment = !!ssrSelectedFlightId && ssrSelFlightLegs.length > 0 && !ssrSelectedLegId;

  // Leg id → route ("CGN → AYT") across every flight in the booking, so each
  // Current Service row can show which segment its SSR is for.
  const legRouteById = useMemo(() => {
    const m = {};
    flights.forEach((f) => (f.legs || []).forEach((leg) => {
      if (leg.id == null) return;
      if (leg.departure && leg.arrival) m[String(leg.id)] = `${leg.departure} → ${leg.arrival}`;
    }));
    return m;
  }, [flights]);
  // null segmentId = applies to all segments; otherwise resolve to its route.
  const ssrRouteLabel = (ssr) => (ssr.segmentId == null ? null : (legRouteById[String(ssr.segmentId)] || null));
  // Show every SSR when no specific leg is picked (unselected / All Segments);
  // once a leg is chosen, show that leg's SSRs plus any all-segment ones.
  const ssrMatchesSelectedLeg = (ssr) => {
    if (!ssrSelectedLegId || ssrSelectedLegId === "__all__") return true;
    if (ssr.segmentId == null) return true;
    return String(ssr.segmentId) === String(ssrSelectedLegId);
  };

  /* ── Add SSR for traveler ── */
  const handleAddSSR = async (item) => {
    if (!ssrTraveler || !bookingId) return;
    const selectedFlight = flights.find((f) => f.id === ssrSelectedFlightId);
    const productServerId = selectedFlight?.serverId || null;
    const payload = [{
      ssrCode: item.code,
      ssrName: item.label,
      ssrCategory: item.category,
      ssrDescription: item.desc || "",
      quantity: 1,
      travelerId: ssrTraveler.id,
      productId: productServerId,
      segmentId: ssrSelectedLegId && ssrSelectedLegId !== "__all__" ? ssrSelectedLegId : null,
    }];
    const res = await addSSRsApi(bookingId, payload);
    if (res.success) {
      showToast(`SSR "${item.code}" added for ${ssrTraveler.name}`, "success");
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to add SSR", "error");
    }
  };

  /* ── Delete SSR ── */
  const handleDeleteSSR = async (ssr) => {
    if (!bookingId || !ssr.id) return;
    const res = await deleteSSRApi(bookingId, ssr.id);
    if (res.success) {
      showToast("SSR removed successfully", "success");
      setSSRDeleteConfirm(null);
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to remove SSR", "error");
    }
  };

  /* ── Change SSR status (Requested / Confirmed / Cancelled / Denied) ── */
  const handleUpdateSSRStatus = async (ssr, newStatus) => {
    if (!bookingId || !ssr?.id || newStatus === ssr.status) return;
    setSSRStatusSavingId(ssr.id);
    const res = await updateSSRStatusApi(bookingId, ssr.id, newStatus);
    if (res.success) {
      showToast(`SSR status set to ${newStatus}`, "success");
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to update SSR status", "error");
    }
    setSSRStatusSavingId(null);
  };

  /* ── Save SSR Pricing ── */
  const handleSaveSSRPricing = async () => {
    if (!ssrPricingEdit || !bookingId || !setSSRPricingApi) return;
    setSSRPricingSaving(true);
    const res = await setSSRPricingApi(bookingId, {
      ssrId: ssrPricingEdit.id,
      costPrice: parseFloat(ssrPricingEdit.costPrice || 0),
      markup: parseFloat(ssrPricingEdit.markup || 0),
      vatTax: parseFloat(ssrPricingEdit.vatTax || 0),
    });
    if (res.success) {
      showToast("SSR pricing saved", "success");
      setSSRPricingEdit(null);
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to save SSR pricing", "error");
    }
    setSSRPricingSaving(false);
  };

  /* ── Save SSR Detail (grey ssrDescription text — shown on the voucher) ── */
  const handleSaveSSRDetail = async () => {
    if (!ssrDetailEdit || !bookingId) return;
    setSSRDetailSaving(true);
    const res = await updateSSRDetailsApi(bookingId, ssrDetailEdit.id, {
      ssrDescription: (ssrDetailEdit.ssrDescription || "").trim(),
    });
    if (res.success) {
      showToast("Service detail saved", "success");
      setSSRDetailEdit(null);
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to save service detail", "error");
    }
    setSSRDetailSaving(false);
  };

  /* ── VAT auto-calculation for SSR Pricing Edit ── */
  const triggerSSRVATCalc = (editState) => {
    if (ssrVatTimerRef.current) clearTimeout(ssrVatTimerRef.current);
    ssrVatTimerRef.current = setTimeout(async () => {
      const costPrice = parseFloat(editState.costPrice) || 0;
      const markup = parseFloat(editState.markup) || 0;
      const payload = {
        ssrId: editState.id,
        markup,
        cost_price: costPrice,
        selling_price: costPrice + markup,
      };
      setSSRVatLoading(true);
      try {
        const result = await calculateVAT(payload);
        if (result.success && result.data) {
          const vatAmount = result.data.vat_amount || 0;
          setSSRPricingEdit((prev) => prev ? { ...prev, vatTax: vatAmount } : prev);
        }
      } finally {
        setSSRVatLoading(false);
      }
    }, 600);
  };

  const fmtSSR = (v) => `€ ${parseFloat(v || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  /* ── Filtered SSR catalog items ── */
  const filteredSSRItems = useMemo(() => {
    let items = ssrCatalog;
    if (ssrActiveCategory) items = items.filter((i) => i.category === ssrActiveCategory);
    if (ssrSearchQuery) {
      const q = ssrSearchQuery.toLowerCase();
      items = items.filter((i) => (i.label || "").toLowerCase().includes(q) || (i.code || "").toLowerCase().includes(q) || (i.desc || "").toLowerCase().includes(q));
    }
    return items;
  }, [ssrActiveCategory, ssrSearchQuery, ssrCatalog]);

  const ssrCountByCategory = useMemo(() => {
    if (!ssrTraveler) return {};
    const travSSRs = getTravelerSSRs(ssrTraveler.id);
    const map = {};
    travSSRs.forEach((s) => { map[s.ssrCategory] = (map[s.ssrCategory] || 0) + 1; });
    return map;
  }, [ssrTraveler, ssrs]);

  const { data: searchResults, pagination: searchPagination, loading: searchLoading } = useTravelersList({
    q: debouncedQ,
    page: searchPage,
    limit: 6,
    status: "Active",
  });

  const handleSearchQueryChange = (val) => {
    setSearchQuery(val);
    setSearchPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(val), 300);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    if (addModalOpen && addMode === "search") {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [addModalOpen, addMode]);

  const { execute: addTravelerApi, loading: addingTraveler } = useAddBookingTravelers();
  const { execute: deleteTravelerApi, loading: deletingTraveler } = useDeleteBookingTraveler();
  const { execute: editTravelerApi, loading: editingSaving } = useEditBookingTraveler();
  const { execute: createModApi, loading: modSaving } = useCreateTravelerModification();

  const existingTravelerIds = travelers.map((t) => t.id);

  const handleSelectExisting = async (person) => {
    const gender = person.gender ? person.gender.charAt(0).toUpperCase() + person.gender.slice(1).toLowerCase() : "";
    const dob = person.dateOfBirth ? person.dateOfBirth.slice(0, 10) : "";
    const res = await addTravelerApi(bookingId, [{ firstName: person.firstName || "", lastName: (person.lastName || "").toUpperCase(), gender, dob }]);
    if (res.success) {
      showToast(`${person.firstName} ${person.lastName} added`, "success");
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to add traveler", "error");
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setActionMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openEdit = (t) => {
    setEditingTraveler(t);
    setEditForm({ firstName: t.firstName, lastName: t.lastName, gender: t.gender === "—" ? "" : t.gender, dateOfBirth: t.rawDob || "" });
    setEditModalOpen(true);
    setActionMenuId(null);
  };

  const openRemove = (t) => {
    setRemovingTraveler(t);
    setRemoveModalOpen(true);
    setActionMenuId(null);
  };

  const openPostBooking = (t, actionType) => {
    setPostBookingTraveler(t);
    setModForm({ actionType, modificationTitle: "", internalNotes: "", newFirstName: t.firstName, newLastName: t.lastName, supplierCost: "", agencyFee: "", refundAmount: "", regenerateDocuments: true, notifyCustomer: true });
    setPostBookingModalOpen(true);
    setActionMenuId(null);
  };

  const handleAdd = async () => {
    if (!addForm.firstName || !addForm.lastName || !addForm.gender || !addForm.dateOfBirth) {
      showToast("All fields are required", "error"); return;
    }
    const res = await addTravelerApi(bookingId, [{
      firstName: addForm.firstName,
      lastName: addForm.lastName,
      gender: addForm.gender,
      dob: addForm.dateOfBirth,
    }]);
    if (res.success) {
      showToast("Traveler added successfully", "success");
      setAddModalOpen(false);
      setAddForm({ firstName: "", lastName: "", gender: "", dateOfBirth: "" });
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to add traveler", "error");
    }
  };

  const handleEdit = async () => {
    if (!editingTraveler) return;
    const res = await editTravelerApi(bookingId, editingTraveler.id, { firstName: editForm.firstName, lastName: editForm.lastName, gender: editForm.gender, dateOfBirth: editForm.dateOfBirth });
    if (res.success) {
      showToast("Traveler updated successfully", "success");
      setEditModalOpen(false);
      setEditingTraveler(null);
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to update traveler", "error");
    }
  };

  const handleRemove = async () => {
    if (!removingTraveler) return;
    const res = await deleteTravelerApi(bookingId, removingTraveler.id);
    if (res.success) {
      showToast("Traveler removed successfully", "success");
      setRemoveModalOpen(false);
      setRemovingTraveler(null);
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to remove traveler", "error");
    }
  };

  const handlePostBookingSave = async () => {
    if (!postBookingTraveler) return;
    const payload = {
      actionType: modForm.actionType,
      modificationTitle: modForm.modificationTitle,
      internalNotes: modForm.internalNotes,
      newFirstName: modForm.newFirstName,
      newLastName: modForm.newLastName,
      supplierCost: parseFloat(modForm.supplierCost || 0),
      agencyFee: parseFloat(modForm.agencyFee || 0),
      refundAmount: parseFloat(modForm.refundAmount || 0),
      regenerateDocuments: modForm.regenerateDocuments,
      notifyCustomer: modForm.notifyCustomer,
    };
    const res = await createModApi(bookingId, postBookingTraveler.id, payload);
    if (res.success) {
      showToast("Modification applied successfully", "success");
      setPostBookingModalOpen(false);
      setPostBookingTraveler(null);
      onRefetch?.();
    } else {
      showToast(res.message || "Failed to apply modification", "error");
    }
  };

  const totalMod = parseFloat(modForm.supplierCost || 0) + parseFloat(modForm.agencyFee || 0) - parseFloat(modForm.refundAmount || 0);
  // Agency fee is the SUNSKY fee — VAT-inclusive (21% embedded). Supplier cost is a
  // pass-through (no VAT) and the refund carries none, so VAT is extracted from the
  // fee only with the inclusive fraction 21/121, never added on top.
  const modAgencyFee = parseFloat(modForm.agencyFee || 0);
  const vatMod = modAgencyFee > 0 ? parseFloat((modAgencyFee * (21 / 121)).toFixed(2)) : 0;

  /* ── SSR Status Color Helper (canonical — shared with Add Booking + voucher) ── */
  const ssrStatusStyle = (status) => ssrStatusColor(status);

  return (
    <>
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}><User size={16} /> Travelers</div>
            <div className={styles.sectionSubtitle}>{travelers.length} traveler{travelers.length !== 1 ? "s" : ""} on this booking{ssrs.length > 0 ? ` · ${ssrs.length} SSR${ssrs.length !== 1 ? "s" : ""} linked` : ""}</div>
          </div>
        </div>
        <table className={styles.table}>
          <thead>
            <tr><th>#</th><th>Client Name</th><th>Date of Birth</th><th>Type</th><th>Gender</th><th>SSRs</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {travelers.map((t, i) => {
              const travSSRCount = ssrCountByTraveler[t.id] || 0;
              const travSSRs = getTravelerSSRs(t.id);
              const isExpanded = expandedSSRTraveler === t.id;

              return (
                <Fragment key={t.id}>
                  <tr>
                    <td className={styles.tdMuted}>{i + 1}</td>
                    <td><div className={styles.travelerName}>{t.name}</div></td>
                    <td>{t.dob}</td>
                    <td><span className={`${styles.typePill} ${styles[`pax${t.type}`]}`}>{t.type}</span></td>
                    <td>{t.gender}</td>
                    <td>
                      {travSSRCount > 0 ? (
                        <button
                          className={styles.ssrCountBadge}
                          onClick={() => setExpandedSSRTraveler(isExpanded ? null : t.id)}
                          title={isExpanded ? "Collapse SSRs" : "View SSRs"}
                        >
                          <Plane size={10} />
                          <span>{travSSRCount} SSR{travSSRCount !== 1 ? "s" : ""}</span>
                          <span className={`${styles.ssrExpandArrow} ${isExpanded ? styles.ssrExpandArrowOpen : ""}`}>&#9662;</span>
                        </button>
                      ) : (
                        <span className={styles.ssrCountEmpty}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`${styles.badge} ${t.status === "Confirmed" ? styles.badgeConfirmed : t.status === "Cancelled" ? styles.badgeCancelled : styles.badgePending}`}>
                        <span className={styles.dot} />{t.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.travelerActionsWrap}>
                        <button
                          className={styles.travelerActionsBtn}
                          onClick={() => setActionMenuId(actionMenuId === t.id ? null : t.id)}
                          disabled={isLocked}
                        >
                          <MoreVertical size={14} />
                        </button>
                        {actionMenuId === t.id && (
                          <div className={styles.travelerActionsMenu} ref={menuRef}>
                            <div className={styles.menuGroup}>
                              <div className={styles.menuGroupLabel}>Standard Actions</div>
                              <button className={styles.menuItem} onClick={() => openEdit(t)}>
                                <Pencil size={13} /> Edit Traveler
                              </button>
                              <button className={styles.menuItem} onClick={() => openRemove(t)}>
                                <Trash2 size={13} /> Remove Traveler
                              </button>
                              <button className={styles.menuItem} onClick={() => { setActionMenuId(null); onSwitchTab?.("assignments"); }}>
                                <Link2 size={13} /> Manage Assignments
                              </button>
                              <button className={styles.menuItem} onClick={() => { setActionMenuId(null); onSwitchTab?.("assignments"); }}>
                                <Eye size={13} /> View Assignments
                              </button>
                              {hasFlights && (
                                <button className={styles.menuItem} onClick={() => openSSRModal(t)}>
                                  <ClipboardList size={13} /> Manage SSRs
                                </button>
                              )}
                            </div>
                            <div className={styles.menuDivider} />
                            <div className={styles.menuGroup}>
                              <div className={styles.menuGroupLabel}>Post-Booking Actions</div>
                              <button className={styles.menuItem} onClick={() => openPostBooking(t, "NAME_CORRECTION")} disabled={t.status === "Cancelled"}>
                                <Pencil size={13} /> Name Correction
                              </button>
                              <button className={styles.menuItem} onClick={() => openPostBooking(t, "NAME_CHANGE")} disabled={t.status === "Cancelled"}>
                                <UserCircle size={13} /> Name Change
                              </button>
                              <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={() => openPostBooking(t, "CANCEL_TRAVELER")} disabled={t.status === "Cancelled"}>
                                <UserX size={13} /> Cancel Traveler
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* ── Cancellation reason sub-row (what the traveler was cancelled for) ── */}
                  {t.status === "Cancelled" && (() => {
                    const cm = (travelerModifications || [])
                      .filter((m) => m.actionType === "CANCEL_TRAVELER" && String(m.travelerId) === String(t.id))
                      .slice(-1)[0];
                    return (
                      <tr key={`cancel-${t.id}`} className={styles.cancelInfoRow}>
                        <td colSpan={8}>
                          <div className={styles.cancelInfoPanel}>
                            <Ban size={13} />
                            <span className={styles.cancelInfoText}>
                              <strong>Traveler cancelled</strong>
                              {cm?.modificationTitle ? <> — {cm.modificationTitle}</> : null}
                              {cm?.internalNotes ? <span className={styles.cancelInfoNotes}> · {cm.internalNotes}</span> : null}
                              {cm && parseFloat(cm.refundAmount || 0) > 0 ? <span className={styles.cancelInfoNotes}> · Refund {fmt(parseFloat(cm.refundAmount))}</span> : null}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                  {/* ── Expanded SSR Rows ── */}
                  {isExpanded && travSSRs.length > 0 && (
                    <tr key={`ssr-${t.id}`} className={styles.ssrExpandedRow}>
                      <td colSpan={8}>
                        <div className={styles.ssrExpandedPanel}>
                          <div className={styles.ssrExpandedHeader}>
                            <span className={styles.ssrExpandedTitle}>
                              <ClipboardList size={13} /> SSRs for {t.name}
                            </span>
                            {hasFlights && !isLocked && (
                              <button className={styles.ssrAddInlineBtn} onClick={() => openSSRModal(t)}>
                                <Plus size={11} /> Add SSR
                              </button>
                            )}
                          </div>
                          <div className={styles.ssrExpandedList}>
                            {travSSRs.map((ssr) => {
                              const cat = getCatStyle(ssr.ssrCategory);
                              const stStyle = ssrStatusStyle(ssr.status);
                              return (
                                <div key={ssr.id} className={styles.ssrExpandedItem}>
                                  <span className={styles.ssrExpandedCatIcon} style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}>
                                    <SSRCatIconRender code={ssr.ssrCategory} />
                                  </span>
                                  <span className={styles.ssrExpandedCode} style={{ background: cat.bg, color: cat.color }}>{ssr.ssrCode}</span>
                                  <span className={styles.ssrExpandedName}>{ssr.ssrName || ssr.ssrCode}</span>
                                  {ssr.quantity > 1 && <span className={styles.ssrExpandedQty}>x{ssr.quantity}</span>}
                                  <span className={styles.ssrExpandedPricing}>
                                    <span className={styles.ssrPriceItem} title="Cost Price">
                                      <span className={styles.ssrPriceLabel}>Cost</span>
                                      <span className={styles.ssrPriceVal}>{fmtSSR(ssr.costPrice)}</span>
                                    </span>
                                    <span className={styles.ssrPriceItem} title="Markup">
                                      <span className={styles.ssrPriceLabel}>Markup</span>
                                      <span className={styles.ssrPriceVal}>{fmtSSR(ssr.markup)}</span>
                                    </span>
                                    <span className={styles.ssrPriceItem} title="Selling Price">
                                      <span className={styles.ssrPriceLabel}>Sell</span>
                                      <span className={styles.ssrPriceValSell}>{fmtSSR(ssr.sellingPrice)}</span>
                                    </span>
                                    {ssr.quantity > 1 && (
                                      <span className={styles.ssrPriceItem} title="Total (qty × sell)">
                                        <span className={styles.ssrPriceLabel}>Total</span>
                                        <span className={styles.ssrPriceValTotal}>{fmtSSR(ssr.sellingPrice * ssr.quantity)}</span>
                                      </span>
                                    )}
                                  </span>
                                  {isLocked ? (
                                    <span className={styles.ssrExpandedStatus} style={{ background: stStyle.bg, color: stStyle.color }}>
                                      {ssr.status || "Requested"}
                                    </span>
                                  ) : (
                                    <select
                                      value={ssr.status || "Requested"}
                                      disabled={ssrStatusSavingId === ssr.id}
                                      onChange={(e) => handleUpdateSSRStatus(ssr, e.target.value)}
                                      title="Change SSR status"
                                      style={{ background: stStyle.bg, color: stStyle.color, border: `1px solid ${stStyle.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 6px", cursor: ssrStatusSavingId === ssr.id ? "wait" : "pointer", outline: "none" }}
                                    >
                                      {SSR_STATUS_VALUES.map((v) => (
                                        <option key={v} value={v} style={{ background: "#fff", color: "#111827" }}>{v}</option>
                                      ))}
                                    </select>
                                  )}
                                  {!isLocked && (
                                    <button
                                      className={styles.ssrExpandedEditPrice}
                                      onClick={() => setSSRPricingEdit({ id: ssr.id, costPrice: ssr.costPrice, markup: ssr.markup, vatTax: ssr.vatTax })}
                                      title="Edit Pricing"
                                    >
                                      <Pencil size={11} />
                                    </button>
                                  )}
                                  {!isLocked && (
                                    <button
                                      className={styles.ssrExpandedRemove}
                                      onClick={() => setSSRDeleteConfirm(ssr)}
                                      title="Remove SSR"
                                    >
                                      <X size={12} />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {!isLocked && (
          <div className={styles.travelerAddFooter}>
            <button className={styles.travelerAddWide} onClick={() => { setAddForm({ firstName: "", lastName: "", gender: "", dateOfBirth: "" }); setAddModalOpen(true); }}>
              <Plus size={15} /> Add Traveler
            </button>
          </div>
        )}
      </div>

      {/* ── SSR Delete Confirmation (inline, no separate modal) ── */}
      {ssrDeleteConfirm && !ssrModalOpen && (
        <BaseModal
          open={!!ssrDeleteConfirm}
          onClose={() => setSSRDeleteConfirm(null)}
          title="Remove SSR"
          width="420px"
          footer={
            <>
              <button className={styles.btnSecondary} onClick={() => setSSRDeleteConfirm(null)}>Cancel</button>
              <LoadingButton className={styles.btnDanger} loading={deletingSSR} loadingText="Removing…" icon={<Trash2 size={13} />} onClick={() => handleDeleteSSR(ssrDeleteConfirm)}>Remove SSR</LoadingButton>
            </>
          }
        >
          <div className={styles.deleteConfirmText}>
            Are you sure you want to remove SSR <strong>{ssrDeleteConfirm?.ssrCode}</strong> ({ssrDeleteConfirm?.ssrName || "—"}) from this traveler?
          </div>
        </BaseModal>
      )}

      {/* ── SSR Management Modal (StepSSR-matched design) ── */}
      <BaseModal
        open={ssrModalOpen}
        onClose={() => { setSSRModalOpen(false); setSSRTraveler(null); setSSRDeleteConfirm(null); }}
        title={`Manage SSRs — ${ssrTraveler?.name || ""}`}
        width="860px"
        footer={
          <button className={styles.btnSecondary} onClick={() => { setSSRModalOpen(false); setSSRTraveler(null); setSSRDeleteConfirm(null); }}>Close</button>
        }
      >
        <div className={styles.ssrModalBody}>
          {/* ── Toolbar: summary pill + search ── */}
          <div className={styles.ssrModalToolbar}>
            <div className={styles.ssrModalSummaryPill}>
              <span className={styles.ssrModalPillNum}>{ssrTraveler ? getTravelerSSRs(ssrTraveler.id).length : 0}</span>
              <span className={styles.ssrModalPillLabel}>services linked</span>
            </div>
            {!isLocked && hasFlights && (
              <div className={styles.ssrModalSearchWrap}>
                <div className={styles.ssrModalSearchBox}>
                  <span className={styles.ssrModalSearchIcon}><SSRSearchIcon /></span>
                  <input
                    className={styles.ssrModalSearchInput}
                    placeholder="Search SSR items..."
                    value={ssrSearchQuery}
                    onChange={(e) => { setSSRSearchQuery(e.target.value); if (e.target.value) setSSRActiveCategory(null); }}
                  />
                  {ssrSearchQuery && (
                    <button className={styles.ssrModalSearchClear} onClick={() => setSSRSearchQuery("")}><SSRXIcon /></button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Context Selector Panel (Flight → Leg) ── */}
          {!isLocked && hasFlights && (
            <div className={styles.ssrCtxPanel}>
              {/* Flight selection */}
              <div className={styles.ssrCtxSection}>
                <div className={styles.ssrCtxSectionHeader}>
                  <span className={styles.ssrCtxSectionIcon}><SSRPlaneIcon /></span>
                  <span className={styles.ssrCtxSectionTitle}>Flight</span>
                </div>
                <div className={styles.ssrCtxChipRow}>
                  {flights.map((fl) => {
                    const firstLeg = (fl.legs || [])[0] || {};
                    const lastLeg = (fl.legs || [])[(fl.legs || []).length - 1] || firstLeg;
                    const route = firstLeg.departure && lastLeg.arrival ? `${firstLeg.departure} → ${lastLeg.arrival}` : "No route";
                    const isActive = ssrSelectedFlightId === fl.id;
                    return (
                      <button
                        key={fl.id}
                        className={`${styles.ssrCtxFlightCard} ${isActive ? styles.ssrCtxFlightCardActive : ""}`}
                        onClick={() => { setSSRSelectedFlightId(fl.id); setSSRSelectedLegId(""); }}
                      >
                        <div className={styles.ssrCtxFlightRoute}>
                          <SSRPlaneIcon /> {route}
                        </div>
                        <div className={styles.ssrCtxFlightMeta}>
                          {fl.supplier && <span>{fl.supplier}</span>}
                          {fl.legs && <span>{fl.legs.length} leg{fl.legs.length !== 1 ? "s" : ""}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Leg / Segment selection */}
              {ssrSelectedFlightId && (() => {
                const selFlight = flights.find((f) => f.id === ssrSelectedFlightId);
                const legs = selFlight?.legs || [];
                if (legs.length === 0) return null;
                const dirLabel = (dir) => {
                  if (!dir) return "";
                  if (dir === "Outbound") return "OUT";
                  if (dir === "Inbound") return "INB";
                  return dir.substring(0, 3).toUpperCase();
                };
                return (
                  <div className={styles.ssrCtxSection}>
                    <div className={styles.ssrCtxSectionHeader}>
                      <span className={styles.ssrCtxSectionIcon}><SSRRouteIcon /></span>
                      <span className={styles.ssrCtxSectionTitle}>Segment / Leg</span>
                      <span className={styles.ssrCtxHint}>Select which leg this SSR applies to</span>
                    </div>
                    <div className={styles.ssrCtxChipRow}>
                      <button
                        className={`${styles.ssrCtxLegChip} ${ssrSelectedLegId === "__all__" ? styles.ssrCtxLegChipActive : ""}`}
                        onClick={() => setSSRSelectedLegId("__all__")}
                      >
                        <span className={styles.ssrCtxLegDir}>ALL</span>
                        <span>All Segments</span>
                      </button>
                      {legs.map((leg, idx) => {
                        const route = leg.departure && leg.arrival ? `${leg.departure} → ${leg.arrival}` : `Leg ${idx + 1}`;
                        const dir = dirLabel(leg.direction);
                        const isActive = ssrSelectedLegId === (leg.id || String(idx));
                        return (
                          <button
                            key={leg.id || idx}
                            className={`${styles.ssrCtxLegChip} ${isActive ? styles.ssrCtxLegChipActive : ""}`}
                            onClick={() => setSSRSelectedLegId(leg.id || String(idx))}
                          >
                            {dir && <span className={styles.ssrCtxLegDir}>{dir}</span>}
                            <span>{route}</span>
                            {leg.airlineCode && leg.flightNumber && (
                              <span style={{ fontSize: "10px", fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>
                                {leg.airlineCode}{leg.flightNumber}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Needs segment selection prompt ── */}
          {!isLocked && hasFlights && ssrNeedsSegment && (
            <div className={styles.ssrEmptyState} style={{ marginTop: 12 }}>
              <strong>Select a segment above</strong> — choose <em>All Segments</em> or a specific leg to see the SSR categories.
            </div>
          )}

          {/* ── Active context bar ── */}
          {!isLocked && hasFlights && ssrSegmentReady && (
            <div className={styles.ssrActiveCtxBar}>
              <span className={styles.ssrActiveCtxLabel}>Adding for:</span>
              <span className={styles.ssrActiveCtxChip}>
                <SSRUserIcon /> {ssrTraveler?.name || "—"}
              </span>
              <span className={styles.ssrActiveCtxChip}>
                <SSRPlaneIcon /> {(() => {
                  const fl = flights.find((f) => f.id === ssrSelectedFlightId);
                  const firstLeg = (fl?.legs || [])[0] || {};
                  return firstLeg.departure && firstLeg.arrival ? `${firstLeg.departure} → ${firstLeg.arrival}` : "Flight";
                })()}
              </span>
              {ssrSelectedLegId !== "__all__" ? (
                <span className={styles.ssrActiveCtxChip}>
                  <SSRRouteIcon /> {(() => {
                    const fl = flights.find((f) => f.id === ssrSelectedFlightId);
                    const leg = (fl?.legs || []).find((l) => l.id === ssrSelectedLegId);
                    return leg && leg.departure && leg.arrival ? `${leg.departure} → ${leg.arrival}` : ssrSelectedLegId;
                  })()}
                </span>
              ) : (
                <span className={styles.ssrActiveCtxChipMuted}>All Segments</span>
              )}
            </div>
          )}

          {/* ── SSR Catalog: Category Grid / Search Results / Category Items ── */}
          {!isLocked && hasFlights && ssrSegmentReady && (
            <>
              {ssrSearchQuery ? (
                <div>
                  <div className={styles.ssrSectionHeading}>
                    Search results for "{ssrSearchQuery}" — {filteredSSRItems.length} item{filteredSSRItems.length !== 1 ? "s" : ""}
                  </div>
                  {filteredSSRItems.length === 0 ? (
                    <div className={styles.ssrEmptyState}>No SSR items found matching your search.</div>
                  ) : (
                    <div className={styles.ssrItemList}>
                      {filteredSSRItems.map((item) => {
                        const cat = getCatStyle(item.category);
                        return (
                          <div key={item.id} className={styles.ssrItemRow}>
                            <span className={styles.ssrItemCatIcon} style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}>
                              <SSRCatIconRender code={item.category} />
                            </span>
                            <span className={styles.ssrItemCodeBadge} style={{ background: cat.bg, color: cat.color }}>{item.code}</span>
                            <div className={styles.ssrItemBody}>
                              <span className={styles.ssrItemName}>{item.label}</span>
                              {item.desc && <span className={styles.ssrItemDesc}>{item.desc}</span>}
                            </div>
                            <span className={styles.ssrItemUnit}>{item.unit}</span>
                            <LoadingButton className={styles.ssrItemAddBtn} loading={addingSSR} loadingText="Adding…" spinnerSize={11} icon={<SSRPlusIcon />} onClick={() => handleAddSSR(item)}>Add</LoadingButton>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : ssrActiveCategory ? (
                <div>
                  <div className={styles.ssrBreadcrumb}>
                    <button className={styles.ssrBreadcrumbBack} onClick={() => setSSRActiveCategory(null)}>
                      <SSRArrowLeftIcon /> All Categories
                    </button>
                    <span className={styles.ssrBreadcrumbSep}>/</span>
                    <span className={styles.ssrBreadcrumbCurrent} style={{ color: getCatStyle(ssrActiveCategory).color }}>
                      <SSRCatIconRender code={ssrActiveCategory} /> {getCatStyle(ssrActiveCategory).name}
                    </span>
                  </div>
                  {filteredSSRItems.length === 0 ? (
                    <div className={styles.ssrEmptyState}>No items in this category.</div>
                  ) : (
                    <div className={styles.ssrItemList}>
                      {filteredSSRItems.map((item) => {
                        const cat = getCatStyle(item.category);
                        return (
                          <div key={item.id} className={styles.ssrItemRow}>
                            <span className={styles.ssrItemCatIcon} style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}>
                              <SSRCatIconRender code={item.category} />
                            </span>
                            <span className={styles.ssrItemCodeBadge} style={{ background: cat.bg, color: cat.color }}>{item.code}</span>
                            <div className={styles.ssrItemBody}>
                              <span className={styles.ssrItemName}>{item.label}</span>
                              {item.desc && <span className={styles.ssrItemDesc}>{item.desc}</span>}
                            </div>
                            <span className={styles.ssrItemUnit}>{item.unit}</span>
                            <LoadingButton className={styles.ssrItemAddBtn} loading={addingSSR} loadingText="Adding…" spinnerSize={11} icon={<SSRPlusIcon />} onClick={() => handleAddSSR(item)}>Add</LoadingButton>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className={styles.ssrSectionHeading}>Browse by Category</div>
                  <div className={styles.ssrCatGrid}>
                    {categoryList.map((cat) => (
                      <button
                        key={cat.code}
                        className={styles.ssrCatCard}
                        onClick={() => setSSRActiveCategory(cat.code)}
                        style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}
                      >
                        <span className={styles.ssrCatIcon}>
                          <SSRCatIconRender code={cat.code} />
                        </span>
                        <div className={styles.ssrCatInfo}>
                          <span className={styles.ssrCatLabel}>{cat.name}</span>
                          <span className={styles.ssrCatDesc}>{cat.description}</span>
                        </div>
                        {(ssrCountByCategory[cat.code] || 0) > 0 && (
                          <span className={styles.ssrCatBadge} style={{ background: cat.color }}>{ssrCountByCategory[cat.code]}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Current SSRs for this traveler (Selected Services) ── */}
          {ssrTraveler && getTravelerSSRs(ssrTraveler.id).length > 0 && (() => {
            const visibleSSRs = getTravelerSSRs(ssrTraveler.id).filter(ssrMatchesSelectedLeg);
            const legScoped = !!ssrSelectedLegId && ssrSelectedLegId !== "__all__";
            return (
            <div>
              <div className={styles.ssrSectionHeading}>
                Current Services
                <span className={styles.ssrSelectedCount}>{visibleSSRs.length}</span>
                {legScoped && (
                  <span className={styles.ssrCurrentScopeChip}>
                    {(legRouteById[String(ssrSelectedLegId)]) || "Selected leg"}
                  </span>
                )}
              </div>
              {visibleSSRs.length === 0 ? (
                <div className={styles.ssrCurrentEmptyScope}>
                  No services for this segment. Pick <em>All Segments</em> to see every service.
                </div>
              ) : (
              <div className={styles.ssrCurrentList}>
                {visibleSSRs.map((ssr) => {
                  const cat = getCatStyle(ssr.ssrCategory);
                  const ssrRoute = ssrRouteLabel(ssr);
                  const stStyle = ssrStatusStyle(ssr.status);
                  const isDeleting = ssrDeleteConfirm?.id === ssr.id;
                  return (
                    <div key={ssr.id} className={`${styles.ssrCurrentRow} ${isDeleting ? styles.ssrCurrentRowDeleting : ""}`}>
                      {isDeleting ? (
                        /* ── Inline delete confirmation (no nested modal!) ── */
                        <div className={styles.ssrDeleteConfirmBar}>
                          <span className={styles.ssrDeleteConfirmText}>
                            Remove <strong>{ssr.ssrCode}</strong>?
                          </span>
                          <div className={styles.ssrDeleteConfirmActions}>
                            <button className={styles.ssrDeleteBtnCancel} onClick={() => setSSRDeleteConfirm(null)}>Cancel</button>
                            <LoadingButton className={styles.ssrDeleteBtnConfirm} loading={deletingSSR} loadingText="Removing…" spinnerSize={11} onClick={() => handleDeleteSSR(ssr)}>Remove</LoadingButton>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className={styles.ssrCurrentCatIcon} style={{ "--ssr-cat-color": cat.color, "--ssr-cat-bg": cat.bg }}>
                            <SSRCatIconRender code={ssr.ssrCategory} />
                          </span>
                          <span className={styles.ssrCurrentCodeBadge} style={{ background: cat.bg, color: cat.color }}>{ssr.ssrCode}</span>
                          <div className={styles.ssrCurrentBody}>
                            <span className={styles.ssrCurrentLabelRow}>
                              <span className={styles.ssrCurrentLabel}>{ssr.ssrName || ssr.ssrCode}</span>
                              <span className={`${styles.ssrCurrentRouteChip} ${!ssrRoute ? styles.ssrCurrentRouteChipAll : ""}`}>
                                {ssrRoute || "All segments"}
                              </span>
                            </span>
                            {isLocked ? (
                              ssr.ssrDescription ? <span className={styles.ssrCurrentDesc}>{ssr.ssrDescription}</span> : null
                            ) : (
                              <button
                                type="button"
                                className={`${styles.ssrCurrentDescEdit} ${!ssr.ssrDescription ? styles.ssrCurrentDescEditEmpty : ""}`}
                                onClick={() => setSSRDetailEdit({ id: ssr.id, ssrCode: ssr.ssrCode, ssrCategory: ssr.ssrCategory, ssrName: ssr.ssrName || ssr.ssrCode, ssrDescription: ssr.ssrDescription || "" })}
                                title="Edit detail (shown on the flight voucher)"
                              >
                                <span className={styles.ssrCurrentDescText}>{ssr.ssrDescription || "Add detail"}</span>
                                <Pencil size={9} className={styles.ssrCurrentDescPencil} />
                              </button>
                            )}
                          </div>
                          <div className={styles.ssrCurrentPriceGroup}>
                            <span className={styles.ssrCurrentPriceChip} title="Cost Price">
                              <span className={styles.ssrCurrentPriceChipLabel}>Cost</span>
                              {fmtSSR(ssr.costPrice)}
                            </span>
                            <span className={styles.ssrCurrentPriceChip} title="Markup">
                              <span className={styles.ssrCurrentPriceChipLabel}>Markup</span>
                              {fmtSSR(ssr.markup)}
                            </span>
                            <span className={`${styles.ssrCurrentPriceChip} ${styles.ssrCurrentPriceChipSell}`} title="Selling Price">
                              <span className={styles.ssrCurrentPriceChipLabel}>Sell</span>
                              {fmtSSR(ssr.sellingPrice)}
                            </span>
                          </div>
                          {ssr.quantity > 1 && (
                            <span className={styles.ssrCurrentQty}>×{ssr.quantity}</span>
                          )}
                          {isLocked ? (
                            <span className={styles.ssrCurrentStatus} style={{ background: stStyle.bg, color: stStyle.color }}>
                              {ssr.status || "Requested"}
                            </span>
                          ) : (
                            <select
                              value={ssr.status || "Requested"}
                              disabled={ssrStatusSavingId === ssr.id}
                              onChange={(e) => handleUpdateSSRStatus(ssr, e.target.value)}
                              title="Change SSR status"
                              style={{ background: stStyle.bg, color: stStyle.color, border: `1px solid ${stStyle.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 6px", cursor: ssrStatusSavingId === ssr.id ? "wait" : "pointer", outline: "none" }}
                            >
                              {SSR_STATUS_VALUES.map((v) => (
                                <option key={v} value={v} style={{ background: "#fff", color: "#111827" }}>{v}</option>
                              ))}
                            </select>
                          )}
                          {!isLocked && (
                            <button
                              className={styles.ssrCurrentEditPrice}
                              onClick={() => setSSRPricingEdit({ id: ssr.id, costPrice: ssr.costPrice, markup: ssr.markup, vatTax: ssr.vatTax })}
                              title="Edit Pricing"
                            >
                              <Pencil size={11} />
                            </button>
                          )}
                          {!isLocked && (
                            <button className={styles.ssrCurrentRemove} onClick={() => setSSRDeleteConfirm(ssr)} title="Remove SSR">
                              <SSRXIcon />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
            );
          })()}

          {/* ── No flights info ── */}
          {!hasFlights && !isLocked && (
            <div className={styles.ssrNoFlightsBox}>
              <Plane size={22} />
              <div>
                <strong>No flights in this booking.</strong>
                <br />
                <span style={{ fontSize: 12, color: "#9ca3af" }}>SSRs can only be added when there is at least one flight in the itinerary.</span>
              </div>
            </div>
          )}

          {/* ── Empty state ── */}
          {ssrTraveler && getTravelerSSRs(ssrTraveler.id).length === 0 && !ssrSearchQuery && !ssrActiveCategory && hasFlights && ssrSelectedFlightId && (
            <div className={styles.ssrEmptyState}>
              <strong>No SSRs linked yet.</strong> Select a category above to browse available services, or use the search bar.
            </div>
          )}
        </div>
      </BaseModal>

      {/* ── SSR Pricing Edit Modal ── */}
      {ssrPricingEdit && (
        <BaseModal
          open={!!ssrPricingEdit}
          onClose={() => setSSRPricingEdit(null)}
          title="Edit SSR Pricing"
          width="420px"
          footer={
            <>
              <button className={styles.btnSecondary} onClick={() => setSSRPricingEdit(null)}>Cancel</button>
              <LoadingButton className={styles.btnSuccess} loading={ssrPricingSaving} loadingText="Saving…" icon={<Save size={13} />} onClick={handleSaveSSRPricing}>Save Pricing</LoadingButton>
            </>
          }
        >
          <div className={styles.ssrPricingEditForm}>
            <div className={styles.ssrPricingEditRow}>
              <label className={styles.ssrPricingEditLabel}>Cost Price</label>
              <div className={styles.ssrPricingEditInputWrap}>
                <span className={styles.ssrPricingEditCurrency}>€</span>
                <input
                  type="number"
                  className={styles.ssrPricingEditInput}
                  value={ssrPricingEdit.costPrice}
                  onChange={(e) => {
                    const updated = { ...ssrPricingEdit, costPrice: e.target.value };
                    setSSRPricingEdit(updated);
                    triggerSSRVATCalc(updated);
                  }}
                  onFocus={(e) => e.target.select()}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className={styles.ssrPricingEditRow}>
              <label className={styles.ssrPricingEditLabel}>Markup</label>
              <div className={styles.ssrPricingEditInputWrap}>
                <span className={styles.ssrPricingEditCurrency}>€</span>
                <input
                  type="number"
                  className={styles.ssrPricingEditInput}
                  value={ssrPricingEdit.markup}
                  onChange={(e) => {
                    const updated = { ...ssrPricingEdit, markup: e.target.value };
                    setSSRPricingEdit(updated);
                    triggerSSRVATCalc(updated);
                  }}
                  onFocus={(e) => e.target.select()}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className={styles.ssrPricingEditRow}>
              <label className={styles.ssrPricingEditLabel}>
                VAT Tax
                {ssrVatLoading && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--text-tertiary)", fontStyle: "italic" }}>calculating…</span>}
              </label>
              <div className={styles.ssrPricingEditInputWrap}>
                <span className={styles.ssrPricingEditCurrency}>€</span>
                <input
                  type="number"
                  className={styles.ssrPricingEditInput}
                  value={ssrPricingEdit.vatTax}
                  onChange={(e) => setSSRPricingEdit({ ...ssrPricingEdit, vatTax: e.target.value })}
                  onFocus={(e) => e.target.select()}
                  min="0"
                  step="0.01"
                  readOnly={ssrVatLoading}
                />
              </div>
            </div>
            <div className={styles.ssrPricingEditSummary}>
              <span className={styles.ssrPricingEditSummaryLabel}>Selling Price</span>
              <span className={styles.ssrPricingEditSummaryValue}>
                {fmtSSR(parseFloat(ssrPricingEdit.costPrice || 0) + parseFloat(ssrPricingEdit.markup || 0))}
              </span>
            </div>
          </div>
        </BaseModal>
      )}

      {/* ── SSR Detail Edit Modal (grey ssrDescription text) ── */}
      {ssrDetailEdit && (
        <BaseModal
          open={!!ssrDetailEdit}
          onClose={() => setSSRDetailEdit(null)}
          title="Edit Service Detail"
          width="440px"
          footer={
            <>
              <button className={styles.btnSecondary} onClick={() => setSSRDetailEdit(null)}>Cancel</button>
              <LoadingButton className={styles.btnSuccess} loading={ssrDetailSaving} loadingText="Saving…" icon={<Save size={13} />} onClick={handleSaveSSRDetail}>Save Detail</LoadingButton>
            </>
          }
        >
          <div className={styles.ssrPricingEditForm}>
            <div className={styles.ssrDetailEditService}>
              <span className={styles.ssrCurrentCodeBadge} style={{ background: getCatStyle(ssrDetailEdit.ssrCategory).bg, color: getCatStyle(ssrDetailEdit.ssrCategory).color }}>{ssrDetailEdit.ssrCode}</span>
              <span className={styles.ssrDetailEditServiceName}>{ssrDetailEdit.ssrName}</span>
            </div>
            <div className={styles.ssrDetailEditRow}>
              <label className={styles.ssrPricingEditLabel}>Detail</label>
              <input
                type="text"
                className={styles.ssrDetailEditInput}
                value={ssrDetailEdit.ssrDescription}
                autoFocus
                placeholder="e.g. Seat 12A (window)"
                maxLength={500}
                onChange={(e) => setSSRDetailEdit({ ...ssrDetailEdit, ssrDescription: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveSSRDetail(); }}
                onFocus={(e) => e.target.select()}
              />
              <span className={styles.ssrDetailEditHint}>Shown in the “Detail” column on the flight voucher.</span>
            </div>
          </div>
        </BaseModal>
      )}

      {/* Add Traveler Modal */}
      <BaseModal
        open={addModalOpen}
        onClose={() => { setAddModalOpen(false); setSearchQuery(""); setDebouncedQ(""); setSearchPage(1); }}
        title="Add Traveler"
        width="760px"
        footer={addMode === "manual" ? (
          <>
            <button className={styles.btnSecondary} onClick={() => setAddModalOpen(false)}>Cancel</button>
            <LoadingButton className={styles.btnSuccess} loading={addingTraveler} loadingText="Adding…" icon={<Plus size={13} />} onClick={handleAdd}>Add Traveler</LoadingButton>
          </>
        ) : (
          <button className={styles.btnSecondary} onClick={() => setAddModalOpen(false)}>Close</button>
        )}
      >
        <div className={styles.addTravelerTabs}>
          <button className={`${styles.addTravelerTab} ${addMode === "search" ? styles.addTravelerTabActive : ""}`} onClick={() => setAddMode("search")}>
            <User size={13} /> Search Existing
          </button>
          <button className={`${styles.addTravelerTab} ${addMode === "manual" ? styles.addTravelerTabActive : ""}`} onClick={() => setAddMode("manual")}>
            <Plus size={13} /> Add Manually
          </button>
        </div>

        {addMode === "search" && (
          <div className={styles.searchExistingWrap}>
            <div className={styles.searchExistingBar}>
              <User size={14} className={styles.searchExistingIcon} />
              <input
                ref={searchInputRef}
                className={styles.searchExistingInput}
                placeholder="Search by name, code, or nationality..."
                value={searchQuery}
                onChange={(e) => handleSearchQueryChange(e.target.value)}
              />
              {searchQuery && (
                <button className={styles.searchExistingClear} onClick={() => handleSearchQueryChange("")}><X size={14} /></button>
              )}
            </div>
            <div className={styles.searchExistingResults}>
              {searchLoading && searchResults.length === 0 && (
                <div className={styles.searchExistingEmpty}>Loading travelers...</div>
              )}
              {!searchLoading && searchResults.length === 0 && (
                <div className={styles.searchExistingEmpty}>
                  <User size={18} style={{ opacity: 0.3 }} />
                  {debouncedQ ? "No travelers found" : "Start typing to search"}
                </div>
              )}
              {searchResults.map((person) => {
                const ageType = person.ageType || classifyTraveler(person.dateOfBirth);
                const typeStyles = { ADT: { bg: "#eef2ff", color: "#4338ca" }, CHD: { bg: "#fef3e2", color: "#b45309" }, INF: { bg: "#ecfdf5", color: "#047857" } };
                const ts = typeStyles[ageType] || { bg: "#f3f4f6", color: "#9ca3af" };
                const alreadyAdded = existingTravelerIds.includes(person.id);
                const dobFmt = person.dateOfBirth ? person.dateOfBirth.slice(0, 10) : "";

                return (
                  <button
                    key={person.travellerCode || person.id}
                    className={`${styles.searchPersonRow} ${alreadyAdded ? styles.searchPersonRowAdded : ""}`}
                    onClick={() => !alreadyAdded && handleSelectExisting(person)}
                    disabled={alreadyAdded || addingTraveler}
                  >
                    <div className={styles.searchPersonAvatar}>
                      {(person.firstName || "?")[0]}{(person.lastName || "?")[0]}
                    </div>
                    <div className={styles.searchPersonInfo}>
                      <div className={styles.searchPersonName}>
                        {person.firstName} <strong>{(person.lastName || "").toUpperCase()}</strong>
                      </div>
                      <div className={styles.searchPersonMeta}>
                        {person.travellerCode && <span className={styles.searchPersonCode}>{person.travellerCode}</span>}
                        {person.nationality && <><span className={styles.searchPersonDot}>·</span><span>{person.nationality}</span></>}
                        {dobFmt && <><span className={styles.searchPersonDot}>·</span><span>{dobFmt}</span></>}
                      </div>
                    </div>
                    <span className={styles.searchPersonType} style={{ background: ts.bg, color: ts.color }}>{ageType}</span>
                    {alreadyAdded ? (
                      <span className={styles.searchPersonAdded}><Check size={12} /> Added</span>
                    ) : (
                      <span className={styles.searchPersonSelect}><Plus size={14} /></span>
                    )}
                  </button>
                );
              })}
            </div>
            {(searchPagination?.totalPages || 1) > 1 && (
              <div className={styles.searchExistingPagination}>
                <button className={styles.searchPageBtn} disabled={searchPage <= 1} onClick={() => setSearchPage((p) => Math.max(1, p - 1))}>←</button>
                <span className={styles.searchPageInfo}>Page {searchPage} of {searchPagination?.totalPages || 1}</span>
                <button className={styles.searchPageBtn} disabled={searchPage >= (searchPagination?.totalPages || 1)} onClick={() => setSearchPage((p) => p + 1)}>→</button>
              </div>
            )}
          </div>
        )}

        {addMode === "manual" && (
          <div className={styles.travelerFormGrid}>
            <div className={styles.travelerFormField}>
              <label className={styles.travelerFormLabel}>First Name *</label>
              <input className={styles.travelerFormInput} value={addForm.firstName} onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })} placeholder="Enter first name" />
            </div>
            <div className={styles.travelerFormField}>
              <label className={styles.travelerFormLabel}>Last Name *</label>
              <input className={styles.travelerFormInput} value={addForm.lastName} onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value.toUpperCase() })} placeholder="Enter last name" style={{ textTransform: "uppercase" }} />
            </div>
            <div className={styles.travelerFormField}>
              <label className={styles.travelerFormLabel}>Gender *</label>
              <select className={styles.travelerFormInput} value={addForm.gender} onChange={(e) => setAddForm({ ...addForm, gender: e.target.value })}>
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className={styles.travelerFormField}>
              <label className={styles.travelerFormLabel}>Date of Birth *</label>
              <input className={styles.travelerFormInput} type="date" value={addForm.dateOfBirth} onChange={(e) => setAddForm({ ...addForm, dateOfBirth: e.target.value })} />
            </div>
          </div>
        )}
      </BaseModal>

      {/* Edit Traveler Modal */}
      <BaseModal open={editModalOpen} onClose={() => { setEditModalOpen(false); setEditingTraveler(null); }} title="Edit Traveler" width="480px" footer={
        <>
          <button className={styles.btnSecondary} onClick={() => { setEditModalOpen(false); setEditingTraveler(null); }}>Cancel</button>
          <LoadingButton className={styles.btnSuccess} loading={editingSaving} loadingText="Saving…" icon={<Check size={13} />} onClick={handleEdit}>Save Changes</LoadingButton>
        </>
      }>
        <div className={styles.travelerFormGrid}>
          <div className={styles.travelerFormField}>
            <label className={styles.travelerFormLabel}>First Name *</label>
            <input className={styles.travelerFormInput} value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
          </div>
          <div className={styles.travelerFormField}>
            <label className={styles.travelerFormLabel}>Last Name *</label>
            <input className={styles.travelerFormInput} value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value.toUpperCase() })} style={{ textTransform: "uppercase" }} />
          </div>
          <div className={styles.travelerFormField}>
            <label className={styles.travelerFormLabel}>Gender *</label>
            <select className={styles.travelerFormInput} value={editForm.gender} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}>
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className={styles.travelerFormField}>
            <label className={styles.travelerFormLabel}>Date of Birth *</label>
            <input className={styles.travelerFormInput} type="date" value={editForm.dateOfBirth} onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })} />
          </div>
        </div>
      </BaseModal>

      {/* Remove Traveler Modal */}
      <BaseModal open={removeModalOpen} onClose={() => { setRemoveModalOpen(false); setRemovingTraveler(null); }} title="Remove Traveler" width="440px" footer={
        <>
          <button className={styles.btnSecondary} onClick={() => { setRemoveModalOpen(false); setRemovingTraveler(null); }}>Cancel</button>
          <LoadingButton className={styles.btnDanger} loading={deletingTraveler} loadingText="Removing…" icon={<Trash2 size={13} />} onClick={handleRemove}>Remove</LoadingButton>
        </>
      }>
        <div className={styles.deleteConfirmText}>
          Are you sure you want to remove <strong>{removingTraveler?.name}</strong> from this booking?
          This will also remove all their assignments and SSRs.
        </div>
      </BaseModal>

      {/* Post-Booking Action Modal */}
      <BaseModal
        open={postBookingModalOpen}
        onClose={() => { setPostBookingModalOpen(false); setPostBookingTraveler(null); }}
        title={`Post-Booking Action — ${postBookingTraveler?.name || ""}`}
        width="860px"
        footer={
          <>
            <button className={styles.btnSecondary} onClick={() => { setPostBookingModalOpen(false); setPostBookingTraveler(null); }}>Cancel</button>
            <LoadingButton className={styles.btnSuccess} loading={modSaving} loadingText="Saving…" icon={<Check size={13} />} onClick={handlePostBookingSave}>Save</LoadingButton>
          </>
        }
      >
        <div className={styles.postBookingForm}>
          <div className={styles.travelerFormField}>
            <label className={styles.travelerFormLabel}>Traveler Post-Booking Action</label>
            <select
              className={styles.travelerFormInput}
              value={modForm.actionType}
              onChange={(e) => setModForm({ ...modForm, actionType: e.target.value })}
            >
              <option value="">Select action...</option>
              <option value="NAME_CORRECTION">Name Correction</option>
              <option value="NAME_CHANGE">Name Change</option>
              <option value="CANCEL_TRAVELER">Cancel Traveler</option>
            </select>
          </div>

          {(modForm.actionType === "NAME_CORRECTION" || modForm.actionType === "NAME_CHANGE") && (
            <div className={styles.travelerFormGrid}>
              <div className={styles.travelerFormField}>
                <label className={styles.travelerFormLabel}>New First Name</label>
                <input className={styles.travelerFormInput} value={modForm.newFirstName} onChange={(e) => setModForm({ ...modForm, newFirstName: e.target.value })} />
              </div>
              <div className={styles.travelerFormField}>
                <label className={styles.travelerFormLabel}>New Last Name</label>
                <input className={styles.travelerFormInput} value={modForm.newLastName} onChange={(e) => setModForm({ ...modForm, newLastName: e.target.value.toUpperCase() })} style={{ textTransform: "uppercase" }} />
              </div>
            </div>
          )}

          <div className={styles.travelerFormField}>
            <label className={styles.travelerFormLabel}>Modification Title <span className={styles.optionalLabel}>(optional)</span></label>
            <input className={styles.travelerFormInput} value={modForm.modificationTitle} onChange={(e) => setModForm({ ...modForm, modificationTitle: e.target.value })} placeholder="Brief title for this modification" />
          </div>

          <div className={styles.travelerFormField}>
            <label className={styles.travelerFormLabel}>Internal Notes <span className={styles.optionalLabel}>(optional)</span></label>
            <textarea className={styles.travelerFormTextarea} rows={3} value={modForm.internalNotes} onChange={(e) => setModForm({ ...modForm, internalNotes: e.target.value })} placeholder="Internal notes about this modification..." />
          </div>

          <div className={styles.postBookingSectionLabel}>
            <Euro size={13} /> Financial Impact
          </div>
          <div className={styles.travelerFormGrid}>
            <div className={styles.travelerFormField}>
              <label className={styles.travelerFormLabel}>Supplier Cost</label>
              <input className={styles.travelerFormInput} type="number" step="0.01" min="0" value={modForm.supplierCost} onChange={(e) => setModForm({ ...modForm, supplierCost: e.target.value })} onFocus={(e) => e.target.select()} placeholder="0.00" />
            </div>
            <div className={styles.travelerFormField}>
              <label className={styles.travelerFormLabel}>Agency Fee</label>
              <input className={styles.travelerFormInput} type="number" step="0.01" min="0" value={modForm.agencyFee} onChange={(e) => setModForm({ ...modForm, agencyFee: e.target.value })} onFocus={(e) => e.target.select()} placeholder="0.00" />
            </div>
            <div className={styles.travelerFormField}>
              <label className={styles.travelerFormLabel}>Refund Amount</label>
              <input className={styles.travelerFormInput} type="number" step="0.01" min="0" value={modForm.refundAmount} onChange={(e) => setModForm({ ...modForm, refundAmount: e.target.value })} onFocus={(e) => e.target.select()} placeholder="0.00" />
            </div>
          </div>

          <div className={styles.modTotalRow}>
            <div className={styles.modTotalItem}>
              <span className={styles.modTotalLabel}>Total Modification Amount</span>
              <span className={styles.modTotalValue}>{fmt(totalMod)}</span>
            </div>
            <div className={styles.modTotalItem}>
              <span className={styles.modTotalLabel}>VAT <span className={styles.optionalLabel}>(auto recalculated)</span></span>
              <span className={styles.modTotalValue}>{fmt(vatMod)}</span>
            </div>
          </div>

          <div className={styles.postBookingSectionLabel}>
            <FileText size={13} /> Document Impact
          </div>
          <div className={styles.modCheckboxRow}>
            <label className={styles.modCheckboxLabel}>
              <input type="checkbox" checked={modForm.regenerateDocuments} onChange={(e) => setModForm({ ...modForm, regenerateDocuments: e.target.checked })} />
              Regenerate Documents
            </label>
            <label className={styles.modCheckboxLabel}>
              <input type="checkbox" checked={modForm.notifyCustomer} onChange={(e) => setModForm({ ...modForm, notifyCustomer: e.target.checked })} />
              Notify Customer
            </label>
          </div>
        </div>
      </BaseModal>
    </>
  );
};

/* ── Flight Assignment Status (post-cancellation) ──────────────────────────
   Once a booking / product / flight-leg / traveler is cancelled, the Assignment
   Tab must reflect it without ever offering to re-assign a cancelled traveler.
   Four states drive the grid:
     • Assigned           — active traveler, fully assigned                (green)
     • Partially Assigned — split flight, one direction live, another gone (orange)
     • Cancelled          — traveler or whole product cancelled            (red)
     • Not Assigned       — active traveler, no assignment yet             (grey)
   Per the project rule, only the status text is coloured. */
const ASSIGN_STATUS_META = {
  Assigned:             { label: "Assigned",            bg: "#ecfdf5", color: "#059669" },
  "Partially Assigned": { label: "Partially Assigned",  bg: "#fff7ed", color: "#d97706" },
  Cancelled:            { label: "Cancelled",           bg: "#fef2f2", color: "#dc2626" },
  "Not Assigned":       { label: "Not Assigned",        bg: "#f5f6f8", color: "#6b7280" },
};
const AssignStatusBadge = ({ status }) => {
  const m = ASSIGN_STATUS_META[status] || ASSIGN_STATUS_META["Not Assigned"];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 11px", borderRadius: 7, fontSize: 11.5, fontWeight: 700, background: m.bg, color: m.color, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />
      {m.label}
    </span>
  );
};

const AssignmentTab = ({ components, travelers, assignments, originalAssignments, isLocked, assignmentLockData, bookingId, showToast, onRemove, onAdd, onUpdateAssignment, ssrs = [] }) => {
  const [rowMenu, setRowMenu] = useState(null);
  const [cardTab, setCardTab] = useState({});
  const [busyAction, setBusyAction] = useState(null);
  const [adminOverrideModal, setAdminOverrideModal] = useState(null);
  const [adminReason, setAdminReason] = useState("");

  const assignLocked = assignmentLockData?.isLocked || false;
  const canOverride = assignmentLockData?.canOverride || false;

  const getAssignedTravelers = (productId) =>
    (assignments || []).filter((a) => a.productId === productId);

  const handleRemoveClick = (assignment, comp) => {
    if (assignLocked && !canOverride) {
      showToast?.("Assignments are locked. Only administrators can modify locked assignments.", "error");
      return;
    }
    if (assignLocked && canOverride) {
      setAdminOverrideModal({ type: "remove", assignmentId: assignment.id, travelerId: assignment.travelerId, productId: comp.id });
      setAdminReason("");
      return;
    }
    return performRemove(assignment.id, assignment.travelerId, comp.id);
  };

  const handleAddClick = (trav, comp) => {
    if (assignLocked && !canOverride) {
      showToast?.("Assignments are locked. Only administrators can modify locked assignments.", "error");
      return;
    }
    if (assignLocked && canOverride) {
      setAdminOverrideModal({ type: "add", travelerId: trav.id, productId: comp.id, assignmentType: comp.type });
      setAdminReason("");
      return;
    }
    return performAdd(trav.id, comp.id, comp.type);
  };

  const performRemove = async (assignmentId, travelerId, productId, reason) => {
    setBusyAction(`remove-${travelerId}-${productId}`);
    await onRemove?.(assignmentId, travelerId, productId, reason);
    setBusyAction(null);
  };

  const performAdd = async (travelerId, productId, assignmentType, reason) => {
    setBusyAction(`add-${travelerId}-${productId}`);
    await onAdd?.(travelerId, productId, assignmentType, reason);
    setBusyAction(null);
  };

  const changeRoom = async (assignment, roomNumber) => {
    if (!assignment?.id) return;
    setBusyAction(`room-${assignment.id}`);
    await onUpdateAssignment?.(assignment.id, { roomNumber });
    setBusyAction(null);
  };

  const handleAdminOverrideConfirm = async () => {
    if (!adminReason.trim()) {
      showToast?.("Please provide a reason for overriding the assignment lock.", "error");
      return;
    }
    const modal = adminOverrideModal;
    setAdminOverrideModal(null);
    if (modal.type === "remove") {
      await performRemove(modal.assignmentId, modal.travelerId, modal.productId, adminReason);
    } else if (modal.type === "add") {
      await performAdd(modal.travelerId, modal.productId, modal.assignmentType, adminReason);
    }
    setAdminReason("");
  };

  const PRODUCT_ICON_MAP_LOCAL = {
    Flight: Plane, Hotel: Hotel, Transfer: Bus, Insurance: Shield,
    Package: Package, CarRental: Car, Visa: FileText, Cruise: Ship,
  };

  /* Soft per-product accents (client: fewer/softer colours, not black & white).
     Matches the itinerary .uIcon* / .uCard* palette for a consistent look. */
  const PRODUCT_STYLE_LOCAL = {
    Flight:    { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
    Hotel:     { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
    Transfer:  { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
    CarRental: { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
    Visa:      { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
    Package:   { bg: "#eef2ff", color: "#4f46e5", border: "#c7d2fe" },
    Insurance: { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
    Cruise:    { bg: "#f0f9ff", color: "#0284c7", border: "#bae6fd" },
  };

  const LOCK_ICON_MAP = {
    hasPricing: { label: "Pricing Exists", icon: <DollarSign size={13} /> },
    hasSupplierPayment: { label: "Supplier Payment", icon: <DollarSign size={13} /> },
    hasClientPayment: { label: "Client Payment", icon: <DollarSign size={13} /> },
    hasInvoice: { label: "Invoice", icon: <FileText size={13} /> },
    hasCreditNote: { label: "Credit Note", icon: <FileText size={13} /> },
    hasDebitNote: { label: "Debit Note", icon: <FileText size={13} /> },
    isBookingLocked: { label: "Booking Locked", icon: <Lock size={13} /> },
  };

  return (
    <div className={styles.assignmentTabWrap}>
      {/* Assignment Lock Banner */}
      {assignLocked && (
        <div style={{
          background: "#fef3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "12px 16px",
          marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 10
        }}>
          <Lock size={16} style={{ color: "#856404", marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: "#856404", fontSize: 13, marginBottom: 4 }}>
              Assignments Locked
            </div>
            <div style={{ fontSize: 12, color: "#664d03", lineHeight: 1.5 }}>
              {assignmentLockData?.warningMessage || "Traveler assignments are locked due to existing financial records."}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              {assignmentLockData?.locks && Object.entries(assignmentLockData.locks).map(([key, val]) => {
                if (!val) return null;
                const info = LOCK_ICON_MAP[key];
                if (!info) return null;
                return (
                  <span key={key} style={{
                    display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px",
                    background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 4, fontSize: 11,
                    color: "#664d03", fontWeight: 500
                  }}>
                    {info.icon} {info.label}
                  </span>
                );
              })}
            </div>
            {canOverride && (
              <div style={{ fontSize: 11, color: "#856404", marginTop: 6, fontStyle: "italic" }}>
                As an administrator, you can override this lock. Each override requires a reason and is recorded in the audit log.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Override Modal */}
      {adminOverrideModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: 24, maxWidth: 480, width: "90%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <AlertTriangle size={20} style={{ color: "#dc3545" }} />
              <h3 style={{ margin: 0, fontSize: 16, color: "#1a1a2e" }}>Admin Override Required</h3>
            </div>
            <div style={{
              background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: 12,
              marginBottom: 16, fontSize: 12, color: "#664d03", lineHeight: 1.5
            }}>
              <strong>Warning:</strong> Modifying assignments when financial records exist may cause inconsistencies
              in supplier payments, invoices, credit notes, and debit notes. This action will be recorded in the audit log.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Reason for override <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <textarea
                value={adminReason}
                onChange={(e) => setAdminReason(e.target.value)}
                placeholder="Explain why this assignment change is necessary despite existing financial records..."
                style={{
                  width: "100%", minHeight: 80, padding: "8px 10px", border: "1px solid #d1d5db",
                  borderRadius: 6, fontSize: 13, resize: "vertical", fontFamily: "inherit"
                }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={() => { setAdminOverrideModal(null); setAdminReason(""); }}
                style={{
                  padding: "8px 16px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff",
                  cursor: "pointer", fontSize: 13, color: "#374151"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAdminOverrideConfirm}
                disabled={!adminReason.trim()}
                style={{
                  padding: "8px 16px", border: "none", borderRadius: 6,
                  background: adminReason.trim() ? "#dc3545" : "#e5e7eb",
                  color: adminReason.trim() ? "#fff" : "#9ca3af",
                  cursor: adminReason.trim() ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 600
                }}
              >
                Confirm Override
              </button>
            </div>
          </div>
        </div>
      )}

      {components.length === 0 ? (
        <div className={styles.sectionCard}>
          <div className={styles.empty}>
            <div className={styles.emptyIcon}><Link2 size={32} /></div>
            No products to display assignments for.
          </div>
        </div>
      ) : (
        components.map((comp) => {
          const Icon = PRODUCT_ICON_MAP_LOCAL[comp.type] || FileText;
          const ps = PRODUCT_STYLE_LOCAL[comp.type] || {};
          const assigned = getAssignedTravelers(comp.id);

          const raw = comp._raw;
          const dd = raw?.productDetails || {};
          const ftMap = { OW: "One Way", RT: "Round Trip", Multi: "Multi-City" };
          const flightTypeLabel = dd.flightType ? ftMap[dd.flightType] || dd.flightType : "";

          /* ── Cancellation context (drives the Assignment Status) ──
             • compCancelled  — whole product cancelled (assignment rows are gone)
             • isTravCancelled — traveler cancelled at traveler level (rows remain)
             • flightDirCtx   — per-direction live/cancelled state for split flights */
          const compCancelled = comp.status === "Cancelled";
          const isTravCancelled = (t) => t.status === "Cancelled";
          const norm = (s) => String(s ?? "").trim().toLowerCase();
          const flightDirCtx = (() => {
            if (comp.type !== "Flight") return null;
            const legs = dd.legs || [];
            if (legs.length === 0) return null;
            const order = [];
            const groups = {};
            legs.forEach((l, i) => {
              const dir = l.direction || (legs.length === 1 ? "Flight" : `Segment ${i + 1}`);
              if (!groups[dir]) { groups[dir] = { direction: dir, legs: [] }; order.push(dir); }
              groups[dir].legs.push(l);
            });
            const cancelledSet = new Set((dd.cancellation?.cancelledDirections || []).map(norm));
            const dirs = order.map((dir) => {
              const g = groups[dir];
              const cancelled = cancelledSet.has(norm(dir)) || g.legs.every((l) => l.legStatus === "Cancelled" || l.flightStatus === "Cancelled");
              const legPnr = g.legs.find((l) => l.pnr || l.airlinePnr) || {};
              const byName = /out/.test(norm(dir)) ? (dd.outboundPnr || dd.airlinePnr)
                : /in/.test(norm(dir)) ? dd.inboundPnr
                : (dd.airlinePnr || dd.outboundPnr);
              const pnr = (legPnr.pnr || legPnr.airlinePnr || byName || "").toString().toUpperCase();
              return { direction: dir, cancelled, pnr };
            });
            return {
              dirs,
              multi: dirs.length > 1,
              hasCancelled: dirs.some((d) => d.cancelled),
              anyActive: dirs.some((d) => !d.cancelled),
            };
          })();
          /* Unified per-traveler assignment status used across every column. */
          const assignStatusFor = (trav, a) => {
            if (compCancelled || isTravCancelled(trav)) return "Cancelled";
            if (!a) return "Not Assigned";
            if (comp.type === "Flight" && flightDirCtx && flightDirCtx.hasCancelled && flightDirCtx.anyActive) return "Partially Assigned";
            return "Assigned";
          };
          /* Cancelled travelers are excluded from assignment completion entirely. */
          const activeTravelers = travelers.filter((t) => !compCancelled && !isTravCancelled(t));
          const assignedActiveCount = activeTravelers.filter((t) => assigned.find((x) => x.travelerId === t.id)).length;
          const cancelledCount = travelers.length - activeTravelers.length;

          /* One-line assignment context shown in the traveler table */
          const assignInfo = () => {
            if (comp.type === "Flight") {
              const pnr = (dd.airlinePnr || dd.outboundPnr || "").toUpperCase();
              return pnr ? `PNR: ${pnr}` : "Assigned to flight";
            }
            if (comp.type === "Hotel") {
              const r = (dd.rooms && dd.rooms[0]) || {};
              return r.roomName || r.roomType || dd.roomName || dd.roomType || "Room 1";
            }
            if (comp.type === "Transfer") {
              const s = (dd.transferMoment === "RoundTrip" ? dd.arrivalLeg?.schedule : (dd.schedule || dd)) || {};
              return s.pickupLocation && s.dropoffLocation ? `${s.pickupLocation} → ${s.dropoffLocation}` : "Assigned to transfer";
            }
            const lbl = comp.type === "CarRental" ? "car rental" : comp.type.toLowerCase();
            return `Assigned to ${lbl}`;
          };

          const notesText = dd.officeNotes || raw?.internalNotes || raw?.supplierNote || "No special requests";

          /* Footer info blocks (screenshot bottom row) */
          const footerBlocks = (() => {
            const blocks = [];
            if (comp.type === "Flight") {
              const l0 = (dd.legs || [])[0] || {};
              const codes = (dd.legs || []).map((l) => [l.airlineCode, l.flightNumber].filter(Boolean).join("")).filter(Boolean).join(" / ");
              blocks.push({ icon: <Plane size={17} />, title: "Flight Information", rows: [{ v: l0.airlineName || "—" }, { v: codes || "—" }, { v: l0.cabinClass || "" }] });
              blocks.push({ icon: <Package size={17} />, title: "Baggage Allowance", rows: [
                { l: "Checked", v: l0.baggage?.checked?.kg ? `${l0.baggage.checked.kg} kg` : "—" },
                l0.baggage?.cabin?.kg ? { l: "Cabin", v: `${l0.baggage.cabin.kg} kg` } : null,
              ] });
              blocks.push({ icon: <Tag size={17} />, title: "PNR & Ticket", rows: [
                { l: "PNR", v: (dd.airlinePnr || dd.outboundPnr || "—").toUpperCase() },
                { l: "Ticket", v: dd.ticketNumber || "—" },
              ] });
            } else if (comp.type === "Hotel") {
              const stars = dd.hotelStars ? "★".repeat(Math.min(5, Math.round(dd.hotelStars))) : "";
              blocks.push({ icon: <Hotel size={17} />, title: "Property Information", rows: [
                { v: dd.hotelName || "—" }, stars ? { v: stars } : null,
                { v: [dd.hotelCity, dd.hotelCountry].filter(Boolean).join(", ") || dd.hotelAddress || "" },
              ] });
              blocks.push({ icon: <ClipboardList size={17} />, title: "Stay Information", rows: [
                { l: "Check-in", v: dd.checkIn ? formatDate(dd.checkIn) : "—" },
                { l: "Check-out", v: dd.checkOut ? formatDate(dd.checkOut) : "—" },
                { l: "Nights", v: dd.nights ? `${dd.nights} Nights` : "—" },
                { l: "Rooms", v: `${dd.numberOfRooms || (dd.rooms || []).length || 1} Room${(dd.numberOfRooms || (dd.rooms || []).length || 1) !== 1 ? "s" : ""}` },
              ] });
              blocks.push({ icon: <Tag size={17} />, title: "Board Information", rows: [
                { l: "Board Type", v: dd.boardName || dd.boardCode || (dd.rooms || [])[0]?.boardName || "—" },
              ] });
            } else if (comp.type === "Transfer") {
              const s = (dd.transferMoment === "RoundTrip" ? dd.arrivalLeg?.schedule : (dd.schedule || dd)) || {};
              const sd = (dd.transferMoment === "RoundTrip" ? dd.departureLeg?.schedule : null) || null;
              blocks.push({ icon: <MapIcon size={17} />, title: "Pickup / Drop-off Information", rows: [
                { l: "Pickup", v: s.pickupLocation || "—" },
                { l: "Drop-off", v: s.dropoffLocation || "—" },
              ] });
              blocks.push({ icon: <Clock size={17} />, title: "Schedule Information", rows: [
                { l: dd.transferMoment === "RoundTrip" ? "Arrival Transfer" : "Pickup", v: s.pickupDate ? `${formatDate(s.pickupDate)} • ${s.pickupTime || "—"}` : "—" },
                sd ? { l: "Departure Transfer", v: sd.pickupDate ? `${formatDate(sd.pickupDate)} • ${sd.pickupTime || "—"}` : "—" } : null,
              ] });
              blocks.push({ icon: <Bus size={17} />, title: "Vehicle Information", rows: [
                { l: "Vehicle Type", v: dd.vehicleType || "—" },
              ] });
            } else if (comp.type === "CarRental") {
              blocks.push({ icon: <MapIcon size={17} />, title: "Pick-up / Drop-off", rows: [
                { l: "Pick-up", v: `${dd.pickupLocation || "—"}${dd.pickupDate ? ` • ${formatDate(dd.pickupDate)}${dd.pickupTime ? ` ${dd.pickupTime}` : ""}` : ""}` },
                { l: "Drop-off", v: `${dd.dropoffLocation || "—"}${dd.dropoffDate ? ` • ${formatDate(dd.dropoffDate)}${dd.dropoffTime ? ` ${dd.dropoffTime}` : ""}` : ""}` },
                dd.rentalDays ? { l: "Duration", v: `${dd.rentalDays} Day${dd.rentalDays !== 1 ? "s" : ""}` } : null,
              ] });
              blocks.push({ icon: <Car size={17} />, title: "Vehicle Information", rows: [
                { l: "Vehicle", v: `${dd.vehicleMakeModel || dd.carCategory || "—"}${dd.orSimilar ? " or similar" : ""}` },
                { l: "Category", v: dd.carCategory || "—" },
                { l: "Insurance", v: dd.insuranceIncluded ? "Included" : "Not included" },
              ] });
              blocks.push({ icon: <User size={17} />, title: "Driver", rows: [
                { l: "Lead Driver", v: dd.leadDriverName || "—" },
                dd.leadDriverAge ? { l: "Age", v: String(dd.leadDriverAge) } : null,
                { l: "Credit Card", v: dd.creditCardRequired ? "Required at pick-up" : "Not required" },
              ] });
            } else {
              const f = (comp.fields || []).filter((x) => x && x.value && x.value !== "—");
              blocks.push({ icon: <Icon size={17} />, title: `${comp.type} Information`, rows: f.slice(0, 4).map((x) => ({ l: x.label, v: x.value })) });
              if (f.length > 4) blocks.push({ icon: <ClipboardList size={17} />, title: "Details", rows: f.slice(4, 8).map((x) => ({ l: x.label, v: x.value })) });
            }
            blocks.push({ icon: <FileText size={17} />, title: "Notes", rows: [{ v: notesText }] });
            return blocks;
          })();

          /* ── Screenshot-exact card pieces ── */
          const TYPE_LABEL = comp.type === "CarRental" ? "CAR RENTAL" : comp.type === "Package" ? "EXTERNAL PACKAGE" : comp.type.toUpperCase();
          const TAB_SETS = {
            Flight: ["TRAVELER ASSIGNMENT", "SEAT ASSIGNMENT", "SSR ASSIGNMENT", "FLIGHT DETAILS"],
            Hotel: ["TRAVELER ASSIGNMENT", "ROOM ASSIGNMENT", "BOARD & SERVICES", "SPECIAL REQUESTS", "HOTEL DETAILS"],
            Transfer: ["TRAVELER ASSIGNMENT", "TRANSFER DETAILS", "VEHICLE & DRIVER", "SPECIAL REQUESTS", "TRANSFER NOTES"],
            CarRental: ["DRIVER ASSIGNMENT", "VEHICLE & PICK-UP", "EXTRAS & COVERAGE", "SPECIAL REQUESTS", "CAR RENTAL DETAILS"],
            Visa: ["TRAVELER ASSIGNMENT", "VISA DETAILS", "DOCUMENTS", "SPECIAL REQUESTS", "VISA NOTES"],
            Insurance: ["TRAVELER ASSIGNMENT", "COVERAGE DETAILS", "POLICY BENEFITS", "SPECIAL REQUESTS", "INSURANCE NOTES"],
            Package: ["TRAVELER ASSIGNMENT", "PACKAGE DETAILS", "INCLUSIONS", "SPECIAL REQUESTS", "PACKAGE NOTES"],
            Cruise: ["TRAVELER ASSIGNMENT", "CRUISE DETAILS", "CABINS", "SPECIAL REQUESTS", "CRUISE NOTES"],
          };
          const tabs = TAB_SETS[comp.type] || ["TRAVELER ASSIGNMENT", `${comp.type.toUpperCase()} DETAILS`, "SPECIAL REQUESTS"];
          const activeCardTab = cardTab[comp.id] || tabs[0];
          const unassignedList = travelers.filter((t) => !compCancelled && !isTravCancelled(t) && !assigned.find((x) => x.travelerId === t.id));
          const compSSRs = (ssrs || []).filter((s) => s.productId === comp.id);
          const showStatusCol = comp.type !== "Flight";

          /* Product summary (title / subtitle / leg blocks / attribute stats) */
          const summary = (() => {
            const legs = dd.legs || [];
            const l0 = legs[0] || {};
            const lN = legs[legs.length - 1] || l0;
            if (comp.type === "Flight") {
              return {
                title: (
                  <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <FlightRoute legs={legs} flightType={dd.flightType || dd.tripType || "OW"} />
                    <span style={{ fontWeight: 600, color: "#6b7280", fontSize: 13 }}>• {formatDate(l0.departureDate || l0.date)}</span>
                  </span>
                ),
                sub: flightTypeLabel, subAccent: true,
                refLines: [
                  dd.outboundPnr || dd.inboundPnr
                    ? [dd.outboundPnr && `Outbound PNR: ${dd.outboundPnr.toUpperCase()}`, dd.inboundPnr && `Inbound PNR: ${dd.inboundPnr.toUpperCase()}`].filter(Boolean).join("  •  ")
                    : (dd.airlinePnr ? `PNR: ${dd.airlinePnr.toUpperCase()}` : null),
                ].filter(Boolean),
                blocks: legs.map((leg, i) => ({
                  icon: <Plane size={15} />,
                  label: leg.direction || `Segment ${i + 1}`,
                  lines: [
                    `${formatDate(leg.departureDate || leg.date)} • ${((leg.departureTime || leg.time || "")).slice(0, 5) || "—"}`,
                    `${(leg.departureAirport || leg.departure || "").toUpperCase()} → ${(leg.arrivalAirport || leg.arrival || "").toUpperCase()}`,
                  ],
                })),
                stats: [],
              };
            }
            if (comp.type === "Hotel") {
              const rooms = dd.rooms || [];
              const nRooms = dd.numberOfRooms || rooms.length || 1;
              return {
                title: dd.hotelName || "Hotel",
                sub: dd.checkIn && dd.checkOut ? `${formatDate(dd.checkIn)} → ${formatDate(dd.checkOut)}${dd.nights ? ` • ${dd.nights} Nights` : ""}` : "",
                blocks: [],
                stats: [
                  { icon: <Clock size={16} />, label: "Check-in", value: dd.checkIn ? formatDate(dd.checkIn) : "—" },
                  { icon: <Clock size={16} />, label: "Check-out", value: dd.checkOut ? formatDate(dd.checkOut) : "—" },
                  { icon: <Hotel size={16} />, label: "Rooms", value: `${nRooms} Room${nRooms !== 1 ? "s" : ""}` },
                  { icon: <Tag size={16} />, label: "Board", value: dd.boardName || dd.boardCode || rooms[0]?.boardName || "—" },
                  { icon: <ClipboardList size={16} />, label: "Room Type", value: rooms[0]?.roomName || rooms[0]?.roomType || "—" },
                  rooms[0]?.roomCharacteristic ? { icon: <Eye size={16} />, label: "Room Characteristic", value: rooms[0].roomCharacteristic } : null,
                  { icon: <MapIcon size={16} />, label: "City / Country", value: [dd.hotelCity, dd.hotelCountry].filter(Boolean).join(", ") || dd.hotelAddress || "—" },
                ].filter(Boolean),
              };
            }
            if (comp.type === "Transfer") {
              const isRT = dd.transferMoment === "RoundTrip";
              const sA = (isRT ? dd.arrivalLeg?.schedule : (dd.schedule || dd)) || {};
              const sD = isRT ? (dd.departureLeg?.schedule || {}) : null;
              const pax = dd.passengers || {};
              return {
                title: `${sA.pickupLocation || "—"} → ${sA.dropoffLocation || "—"}`,
                sub: isRT ? "Round Trip" : (dd.transferMoment || ""), subAccent: true,
                blocks: [
                  { icon: <Bus size={15} />, label: isRT ? "Arrival Transfer" : (dd.transferMoment ? `${dd.transferMoment} Transfer` : "Transfer"), lines: [`${sA.pickupDate ? formatDate(sA.pickupDate) : "—"} • ${sA.pickupTime || "—"}`, `${sA.pickupLocation || "—"} → ${sA.dropoffLocation || "—"}`] },
                  sD ? { icon: <Bus size={15} />, label: "Departure Transfer", lines: [`${sD.pickupDate ? formatDate(sD.pickupDate) : "—"} • ${sD.pickupTime || "—"}`, `${sD.pickupLocation || "—"} → ${sD.dropoffLocation || "—"}`] } : null,
                ].filter(Boolean),
                stats: [
                  { icon: <Bus size={16} />, label: "Transfer Type", value: dd.transferType ? `${dd.transferType} Transfer` : "—" },
                  { icon: <Car size={16} />, label: "Vehicle Type", value: dd.vehicleType || "—" },
                  (pax.adults || pax.children) ? { icon: <User size={16} />, label: "Passengers", value: `${pax.adults || 0} Adult${(pax.adults || 0) !== 1 ? "s" : ""}${pax.children ? `, ${pax.children} Child${pax.children !== 1 ? "ren" : ""}` : ""}` } : null,
                ].filter(Boolean),
              };
            }
            if (comp.type === "CarRental") {
              return {
                title: `${dd.vehicleMakeModel || dd.carCategory || "Vehicle"}${dd.pickupDate ? `  •  ${formatDate(dd.pickupDate)} → ${formatDate(dd.dropoffDate)}` : ""}`,
                sub: dd.rentalDays ? `${dd.rentalDays} Days` : "",
                blocks: [
                  { icon: <Car size={15} />, label: "Pick-up", lines: [`${dd.pickupDate ? formatDate(dd.pickupDate) : "—"} • ${dd.pickupTime || "—"}`, dd.pickupLocation || "—"] },
                  { icon: <Car size={15} />, label: "Drop-off", lines: [`${dd.dropoffDate ? formatDate(dd.dropoffDate) : "—"} • ${dd.dropoffTime || "—"}`, dd.dropoffLocation || "—"] },
                ],
                stats: [
                  { icon: <Car size={16} />, label: "Vehicle Type", value: dd.carCategory || "—" },
                  dd.leadDriverName ? { icon: <User size={16} />, label: "Driver", value: dd.leadDriverName } : null,
                  dd.leadDriverAge ? { icon: <User size={16} />, label: "Driver Age", value: `${dd.leadDriverAge}+` } : null,
                ].filter(Boolean),
              };
            }
            const f = (comp.fields || []).filter((x) => x && x.value && x.value !== "—");
            return {
              title: f[0]?.value || comp.type,
              sub: "",
              subRows: f.slice(1, 4).map((x) => ({ l: x.label, v: x.value })),
              blocks: [],
              stats: f.slice(4, 10).map((x) => ({ icon: <Tag size={16} />, label: x.label, value: x.value })),
            };
          })();

          /* Table assignment-cell helpers */
          const notAssignedCell = (hint) => (
            <span style={{ display: "inline-block" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#d97706" }}>
                <AlertCircle size={14} style={{ flexShrink: 0 }} /> Not Assigned
              </span>
              {hint ? <span style={{ display: "block", fontSize: 11.5, color: "#9ca3af", marginTop: 2, paddingLeft: 20 }}>{hint}</span> : null}
            </span>
          );
          const assignedLines = (l1, l2) => (
            <span style={{ display: "inline-block" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#374151" }}>
                <CheckCircle size={14} style={{ color: "#059669", flexShrink: 0 }} /> {l1}
              </span>
              {l2 ? <span style={{ display: "block", fontSize: 11.5, color: "#6b7280", marginTop: 2, paddingLeft: 20 }}>{l2}</span> : null}
            </span>
          );

          /* Product-specific table columns (between Type and Pricing) */
          const midCols = (() => {
            if (comp.type === "Flight") {
              return [
                { h: "Flight Assignment Status", render: (trav, a) => {
                    const status = assignStatusFor(trav, a);
                    const dirs = flightDirCtx?.dirs || [];
                    return (
                      <span style={{ display: "inline-flex", flexDirection: "column", gap: 5, alignItems: "flex-start" }}>
                        <AssignStatusBadge status={status} />
                        {a && dirs.length > 0 && dirs.map((d, i) => (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: d.cancelled ? "#dc2626" : "#374151" }}>
                            {d.cancelled
                              ? <Ban size={12} style={{ color: "#dc2626", flexShrink: 0 }} />
                              : <CheckCircle size={12} style={{ color: "#059669", flexShrink: 0 }} />}
                            {flightDirCtx.multi && <span style={{ fontWeight: 700 }}>{d.direction}:</span>}
                            {d.pnr && <span>PNR {d.pnr}</span>}
                            <span style={{ fontWeight: 700 }}>{d.cancelled ? "Cancelled" : "Assigned"}</span>
                          </span>
                        ))}
                        {!a && status === "Not Assigned" && <span style={{ fontSize: 11.5, color: "#9ca3af", fontWeight: 600 }}>Assign to PNR</span>}
                      </span>
                    );
                  } },
                { h: "SSR Status", render: (trav) => {
                    const list = compSSRs.filter((s) => s.travelerId === trav.id);
                    if (!list.length) return <span style={{ fontSize: 12.5, color: "#9ca3af", fontWeight: 600 }}>No SSR</span>;
                    const travGone = compCancelled || isTravCancelled(trav);
                    const cancelled = list.filter((s) => travGone || s.status === "Cancelled");
                    const active = travGone ? [] : list.filter((s) => s.status !== "Cancelled");
                    const sample = active[0] || list[0];
                    return (
                      <span style={{ display: "inline-flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                        {active.length > 0 && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#059669" }}>
                            <CheckCircle size={13} /> {active.length} SSR Active
                          </span>
                        )}
                        {cancelled.length > 0 && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#dc2626" }}>
                            <Ban size={13} /> {cancelled.length} SSR Cancelled
                          </span>
                        )}
                        {sample && <span style={{ fontSize: 11, color: "#9ca3af" }}>{sample.ssrName || sample.ssrCode || ""}</span>}
                      </span>
                    );
                  } },
              ];
            }
            if (comp.type === "Hotel") {
              const roomList = (dd.rooms && dd.rooms.length > 0) ? dd.rooms : [{ roomName: dd.roomName, roomType: dd.roomType, boardName: dd.boardName }];
              const r0 = roomList[0] || {};
              const roomLabel = (n) => {
                const r = roomList[(n || 1) - 1] || {};
                return r.roomName || r.roomType || dd.roomName || null;
              };
              const multiRoom = roomList.length > 1;
              return [
                { h: "Room Assignment", render: (trav, a) => {
                    if (!a) return notAssignedCell("Assign to room");
                    const current = a.roomNumber || 1;
                    // Single-room hotels (or a locked booking) just show the room.
                    if (!multiRoom || isLocked) return assignedLines(`Room ${current}`, roomLabel(current));
                    // Multi-room: pick which room this traveler occupies. Persists
                    // the assignment's roomNumber (metadata only — not an unlink).
                    return (
                      <select
                        value={current}
                        disabled={busyAction === `room-${a.id}`}
                        onChange={(e) => changeRoom(a, Number(e.target.value))}
                        style={{ fontSize: 12.5, fontWeight: 600, color: "#111827", padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", maxWidth: 210 }}
                      >
                        {roomList.map((r, i) => (
                          <option key={i} value={i + 1}>
                            Room {i + 1}{(r.roomName || r.roomType) ? ` — ${r.roomName || r.roomType}` : ""}
                          </option>
                        ))}
                      </select>
                    );
                  } },
                { h: "Board", render: () => <span style={{ fontSize: 12.5, fontWeight: 600, color: "#374151" }}>{dd.boardName || dd.boardCode || r0.boardName || "—"}</span> },
                { h: "Additional Services", render: () => <span style={{ fontSize: 12.5, color: "#6b7280" }}>No extras</span> },
              ];
            }
            if (comp.type === "Transfer") {
              const isRT = dd.transferMoment === "RoundTrip";
              const sA = (isRT ? dd.arrivalLeg?.schedule : (dd.schedule || dd)) || {};
              const sD = isRT ? (dd.departureLeg?.schedule || {}) : null;
              return [
                { h: "Assignment", render: (trav, a) => a
                    ? assignedLines(`${isRT ? "Arrival: " : ""}${sA.pickupLocation || "—"} → ${sA.dropoffLocation || "—"}`, sD ? `Departure: ${sD.pickupLocation || "—"} → ${sD.dropoffLocation || "—"}` : null)
                    : notAssignedCell("Assign to transfer") },
                { h: "Seat Assignment", render: (trav, a, idx) => <span style={{ fontSize: 12.5, fontWeight: 700, color: "#111827" }}>{a ? `Seat ${idx + 1}` : "—"}</span> },
              ];
            }
            if (comp.type === "CarRental") {
              return [
                { h: "Driver Assignment", render: (trav, a) => a
                    ? assignedLines("Primary Driver", dd.leadDriverName || trav.name)
                    : notAssignedCell("Assign as driver") },
                { h: "Driving License", render: (trav, a) => <span style={{ fontSize: 12.5, fontWeight: 600, color: "#374151" }}>{a && dd.leadDriverAge ? `Age ${dd.leadDriverAge}` : "—"}</span> },
              ];
            }
            return [
              { h: "Assignment", render: (trav, a) => a ? assignedLines(assignInfo(), null) : notAssignedCell(null) },
            ];
          })();

          /* Secondary tab content panels */
          const tabPanel = (label) => {
            const empty = (msg) => (
              <div style={{ padding: "30px 20px", textAlign: "center", fontSize: 12.5, color: "#9ca3af" }}>{msg}</div>
            );
            if (/NOTES|SPECIAL REQUESTS/.test(label)) {
              return (
                <div style={{ padding: "16px 20px", fontSize: 13, color: "#374151" }}>
                  <div style={{ marginBottom: 6 }}>{notesText}</div>
                  {dd.clientRemarks ? <div style={{ marginBottom: 4 }}><span style={{ fontWeight: 700 }}>Client remarks: </span>{dd.clientRemarks}</div> : null}
                  {dd.supplierRemarks ? <div><span style={{ fontWeight: 700 }}>Supplier remarks: </span>{dd.supplierRemarks}</div> : null}
                </div>
              );
            }
            if (/DETAILS$|VEHICLE & PICK-UP|INCLUSIONS|POLICY BENEFITS/.test(label)) {
              const f = (comp.fields || []).filter((x) => x && x.value);
              if (!f.length) return empty("No details recorded for this product yet.");
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", padding: "8px 10px" }}>
                  {f.map((x, i) => (
                    <div key={i} style={{ padding: "10px 12px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{x.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{x.value}</div>
                    </div>
                  ))}
                </div>
              );
            }
            if (label === "SSR ASSIGNMENT") {
              if (!compSSRs.length) return empty("No SSRs added for this flight yet.");
              return (
                <div style={{ padding: "6px 18px 12px" }}>
                  {compSSRs.map((s, i) => {
                    const trav = travelers.find((t) => t.id === s.travelerId);
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: i < compSSRs.length - 1 ? "1px solid #f0f0f0" : "none", fontSize: 12.5 }}>
                        <CheckCircle size={14} style={{ color: "#059669", flexShrink: 0 }} />
                        <span style={{ fontWeight: 700, color: "#111827" }}>{s.ssrCode || s.code || "SSR"}</span>
                        <span style={{ color: "#374151" }}>{s.ssrName || s.description || ""}</span>
                        {trav && <span style={{ color: "#6b7280", marginLeft: "auto" }}>{trav.name}</span>}
                      </div>
                    );
                  })}
                </div>
              );
            }
            if (label === "ROOM ASSIGNMENT") {
              const rooms = dd.rooms || [];
              if (!rooms.length) return empty("No room details recorded.");
              return (
                <div style={{ padding: "6px 18px 12px" }}>
                  {rooms.map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: i < rooms.length - 1 ? "1px solid #f0f0f0" : "none", fontSize: 12.5 }}>
                      <Hotel size={14} style={{ color: ps.color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, color: "#111827" }}>Room {i + 1}</span>
                      <span style={{ color: "#374151" }}>{r.roomName || r.roomType || "—"}</span>
                      {r.boardName && <span style={{ color: "#6b7280" }}>{r.boardName}</span>}
                    </div>
                  ))}
                </div>
              );
            }
            return empty(`No ${label.toLowerCase()} recorded for this product yet.`);
          };

          const sumLbl = { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 };

          return (
            <div key={comp.id} className={styles.assignProductCard} style={{ background: "#f8fafc", borderLeft: "1px solid #d5dce4", borderRight: "1px solid #d5dce4", borderBottom: "1px solid #d5dce4", borderTop: `3px solid ${ps.color}`, borderRadius: 14, boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.05)", padding: 14, display: "flex", flexDirection: "column", gap: 12, overflow: "visible" }}>
              {/* Header island */}
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 42, height: 42, borderRadius: 10, background: ps.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon size={20} />
                  </span>
                  <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: "0.04em", color: ps.color }}>{TYPE_LABEL}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 7, fontSize: 12, fontWeight: 700, background: comp.status === "Confirmed" ? "#ecfdf5" : comp.status === "Cancelled" ? "#fef2f2" : "#fff7ed", color: comp.status === "Confirmed" ? "#059669" : comp.status === "Cancelled" ? "#dc2626" : "#d97706" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                    {comp.status}
                  </span>
                  {assignLocked && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px",
                      background: "#fef3cd", border: "1px solid #ffc107", borderRadius: 4,
                      fontSize: 11, color: "#856404", fontWeight: 600
                    }}>
                      <Lock size={11} /> Locked
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
                  {raw?.updatedAt && (
                    <span style={{ fontSize: 12, color: "#6b7280" }}>Last updated: {formatDate(raw.updatedAt)}</span>
                  )}
                  <span className={styles.assignCount}>
                    {assignedActiveCount} / {activeTravelers.length} assigned{cancelledCount > 0 ? ` · ${cancelledCount} cancelled` : ""}
                  </span>
                  {(!isLocked || canOverride) && (
                    <>
                      <button
                        onClick={() => setRowMenu(rowMenu === `actions-${comp.id}` ? null : `actions-${comp.id}`)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, color: ps.color, background: "#fff", border: `1px solid ${ps.border}`, borderRadius: 8, cursor: "pointer" }}
                      >
                        Actions <ChevronDown size={13} />
                      </button>
                      {rowMenu === `actions-${comp.id}` && (
                        <div style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 60, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 220, padding: 4 }}>
                          <button
                            disabled={unassignedList.length === 0}
                            onClick={async () => {
                              setRowMenu(null);
                              if (assignLocked) { showToast?.("Assignments are locked — use the row Assign buttons for admin override.", "error"); return; }
                              for (const t of unassignedList) await performAdd(t.id, comp.id, comp.type);
                            }}
                            style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "8px 10px", border: "none", background: "none", borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: unassignedList.length ? "pointer" : "not-allowed", color: unassignedList.length ? "#374151" : "#9ca3af", textAlign: "left" }}
                          >
                            <Plus size={13} /> Assign all unassigned ({unassignedList.length})
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Product summary island */}
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1.2fr 0.8fr" }}>
                  <div style={{ padding: "14px 18px" }}>
                    <div style={sumLbl}>Product Summary</div>
                    <div style={{ fontSize: 15.5, fontWeight: 800, color: "#111827" }}>{summary.title}</div>
                    {summary.sub ? (
                      <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 3, color: summary.subAccent ? ps.color : "#6b7280" }}>{summary.sub}</div>
                    ) : null}
                    {(summary.subRows || []).map((r, i) => (
                      <div key={i} style={{ fontSize: 12, marginTop: 2 }}>
                        <span style={{ color: "#6b7280" }}>{r.l}: </span>
                        <span style={{ fontWeight: 600, color: "#374151" }}>{r.v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: "14px 18px", borderLeft: "1px solid #f0f0f0" }}>
                    <div style={sumLbl}>Supplier</div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>{comp.supplier || "—"}</div>
                  </div>
                  <div style={{ padding: "14px 18px", borderLeft: "1px solid #f0f0f0" }}>
                    <div style={sumLbl}>Supplier Ref{comp.type === "Flight" ? " & PNR" : ""}</div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>{raw?.supplierReference || "—"}</div>
                    {(summary.refLines || []).map((ln, i) => (
                      <div key={i} style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>{ln}</div>
                    ))}
                  </div>
                  <div style={{ padding: "14px 18px", borderLeft: "1px solid #f0f0f0" }}>
                    <div style={sumLbl}>VAT Role</div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>{raw?.supplierCommercialRole || "Merchant"}</div>
                  </div>
                </div>
                {(summary.blocks.length > 0 || summary.stats.length > 0) && (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", borderTop: "1px solid #f0f0f0", padding: "12px 6px" }}>
                    {summary.blocks.map((b, i) => (
                      <div key={`b${i}`} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "4px 16px", borderLeft: i > 0 ? "1px solid #f0f0f0" : "none", minWidth: 180 }}>
                        <span style={{ width: 30, height: 30, borderRadius: 8, background: ps.bg, color: ps.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{b.icon}</span>
                        <span>
                          <span style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#374151" }}>{b.label}</span>
                          {b.lines.map((ln, li) => (
                            <span key={li} style={{ display: "block", fontSize: 12, color: li === 0 ? "#111827" : "#6b7280", fontWeight: li === 0 ? 700 : 500 }}>{ln}</span>
                          ))}
                        </span>
                      </div>
                    ))}
                    {summary.stats.map((s, i) => (
                      <div key={`s${i}`} style={{ display: "flex", gap: 9, alignItems: "center", padding: "4px 16px", borderLeft: (summary.blocks.length > 0 || i > 0) ? "1px solid #f0f0f0" : "none", flex: "1 1 auto", minWidth: 130 }}>
                        <span style={{ color: ps.color, display: "flex", flexShrink: 0 }}>{s.icon}</span>
                        <span>
                          <span style={{ display: "block", fontSize: 9.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</span>
                          <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#111827" }}>{s.value}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>




              {/* Completion strip — Total / Assigned / Unassigned / Completion / Status */}
              {(() => {
                const total = travelers.length;
                const activeTotal = activeTravelers.length;
                const asg = assignedActiveCount;
                const canc = cancelledCount;
                // Cancelled travelers never count against completion: assignment of all
                // ACTIVE travelers is 100%, even if some travelers were cancelled.
                const un = Math.max(0, activeTotal - asg);
                const pct = activeTotal > 0 ? Math.round((asg / activeTotal) * 100) : (canc > 0 ? 100 : 0);
                const C = 2 * Math.PI * 15.5;
                const stripStatus = pct === 100
                  ? { label: "Completed", bg: "#ecfdf5", color: "#059669" }
                  : asg > 0
                    ? { label: "In Progress", bg: "#fff7ed", color: "#d97706" }
                    : { label: "Not Started", bg: "#f5f6f8", color: "#6b7280" };
                const cellL = { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 };
                const cellV = { fontSize: 18, fontWeight: 800, color: "#1a1d23" };
                const cell = { flex: "1 1 0", minWidth: 140, display: "flex", alignItems: "center", gap: 12, padding: "6px 18px", borderLeft: "1px solid #f0f0f0" };
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 0" }}>
                    <div style={{ ...cell, borderLeft: "none" }}>
                      <User size={22} style={{ color: "#64748b", flexShrink: 0 }} />
                      <div><div style={cellL}>Total Travelers</div><div style={cellV}>{total}</div></div>
                    </div>
                    <div style={cell}>
                      <CheckCircle size={22} style={{ color: "#059669", flexShrink: 0 }} />
                      <div><div style={cellL}>Assigned</div><div style={cellV}>{asg}</div></div>
                    </div>
                    <div style={cell}>
                      <AlertCircle size={22} style={{ color: "#d97706", flexShrink: 0 }} />
                      <div><div style={cellL}>Unassigned</div><div style={cellV}>{un}</div></div>
                    </div>
                    <div style={cell}>
                      <Ban size={22} style={{ color: "#dc2626", flexShrink: 0 }} />
                      <div><div style={cellL}>Cancelled</div><div style={cellV}>{canc}</div></div>
                    </div>
                    <div style={cell}>
                      <svg width="40" height="40" viewBox="0 0 40 40" style={{ flexShrink: 0 }}>
                        <circle cx="20" cy="20" r="15.5" fill="none" stroke="#e9edf2" strokeWidth="5" />
                        <circle cx="20" cy="20" r="15.5" fill="none" stroke={ps.color} strokeWidth="5"
                          strokeDasharray={`${(C * pct) / 100} ${C}`} strokeLinecap="round" transform="rotate(-90 20 20)" />
                      </svg>
                      <div><div style={cellL}>Completion</div><div style={cellV}>{pct}%</div></div>
                    </div>
                    <div style={cell}>
                      <div>
                        <div style={cellL}>Status</div>
                        <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: stripStatus.bg, color: stripStatus.color }}>
                          {stripStatus.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Tabs + content island. overflow:visible (not hidden) so the row
                  Actions/Manage dropdowns can extend past the card edge instead of
                  being clipped and hidden behind the footer/notes block below. */}
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "visible" }}>
                <div style={{ display: "flex", gap: 2, padding: "0 10px", borderBottom: "1px solid #e5e7eb", overflowX: "auto" }}>
                  {tabs.map((t) => {
                    const act = t === activeCardTab;
                    return (
                      <button
                        key={t}
                        onClick={() => setCardTab((p) => ({ ...p, [comp.id]: t }))}
                        style={{ padding: "13px 14px", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", border: "none", background: "none", cursor: "pointer", whiteSpace: "nowrap", color: act ? ps.color : "#6b7280", borderBottom: act ? `2.5px solid ${ps.color}` : "2.5px solid transparent", marginBottom: -1 }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
                {activeCardTab !== tabs[0] ? (
                  tabPanel(activeCardTab)
                ) : (
              <div className={styles.assignProductBody}>
                {travelers.length === 0 ? (
                  <div className={styles.assignEmpty}>
                    No travelers in this booking yet.
                  </div>
                ) : (
                  <table className={styles.assignTable}>
                    <thead>
                      <tr>
                        <th style={{ width: 40, textAlign: "center" }}>#</th>
                        <th style={{ width: 200, textAlign: "left" }}>Traveler</th>
                        <th style={{ width: 80, textAlign: "center" }}>Type</th>
                        {midCols.map((c) => (
                          <th key={c.h} style={{ textAlign: "left", width: "auto", minWidth: 170 }}>{c.h}</th>
                        ))}
                        <th style={{ width: 150, textAlign: "left" }}>Pricing Entered</th>
                        {showStatusCol && <th style={{ width: 110, textAlign: "center" }}>Status</th>}
                        {(!isLocked || canOverride) && <th style={{ width: 130, textAlign: "center" }}>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {travelers.map((trav, idx) => {
                        const a = assigned.find((x) => x.travelerId === trav.id);
                        const canRemove = a && !a.hasPricing;
                        const menuKey = `${comp.id}-${trav.id}`;
                        const busyRemove = busyAction === `remove-${trav.id}-${comp.id}`;
                        const busyAdd = busyAction === `add-${trav.id}-${comp.id}`;
                        const openMenuUp = travelers.length > 1 && idx >= travelers.length - 1;
                        // A cancelled traveler (or a traveler on a fully-cancelled product) can
                        // never be (re)assigned — no Assign / Manage action is offered.
                        const rowCancelled = compCancelled || isTravCancelled(trav);
                        const rowStatus = assignStatusFor(trav, a);
                        return (
                          <tr key={trav.id} className={trav.status === "Cancelled" ? styles.assignRowCancelled : undefined}>
                            <td className={styles.tdMuted}>{idx + 1}</td>
                            <td>
                              <span className={styles.travelerName} style={trav.status === "Cancelled" ? { textDecoration: "line-through", opacity: 0.65 } : undefined}>{trav.name}</span>
                              {trav.status === "Cancelled" && (
                                <span className={`${styles.badge} ${styles.badgeCancelled}`} style={{ marginLeft: 8 }}>
                                  <span className={styles.dot} />Cancelled
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`${styles.assignTypePill} ${styles[`assignPax${trav.type}`]}`}>
                                {trav.type}
                              </span>
                            </td>
                            {midCols.map((c, ci) => (
                              <td key={ci} style={{ textAlign: "left" }}>{c.render(trav, a, idx)}</td>
                            ))}
                            <td style={{ textAlign: "left" }}>
                              {rowCancelled ? (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#dc2626" }}>
                                  <Ban size={14} /> Pricing Cancelled
                                </span>
                              ) : a ? (
                                a.hasPricing ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#059669" }}>
                                    <CheckCircle size={14} /> Completed
                                  </span>
                                ) : (
                                  <span className={styles.assignPricingNo}>No pricing</span>
                                )
                              ) : (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#d97706" }}>
                                  <AlertCircle size={14} /> Pending
                                </span>
                              )}
                            </td>
                            {showStatusCol && (
                              <td style={{ textAlign: "center" }}>
                                <AssignStatusBadge status={rowStatus} />
                              </td>
                            )}
                            {(!isLocked || canOverride) && (
                              <td style={{ position: "relative", textAlign: "center" }}>
                                {rowCancelled ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }} title="Cancelled travelers cannot be assigned">
                                    <Ban size={12} /> Cancelled
                                  </span>
                                ) : a ? (
                                  <>
                                    <button
                                      onClick={() => setRowMenu(rowMenu === menuKey ? null : menuKey)}
                                      disabled={busyRemove}
                                      style={{
                                        display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 14px",
                                        fontSize: 12, fontWeight: 700, color: "#2563eb", background: "#fff",
                                        border: "1px solid #bfdbfe", borderRadius: 8, cursor: "pointer",
                                      }}
                                    >
                                      {busyRemove ? <Loader2 size={12} className={styles.spin} /> : null}
                                      Manage <ChevronDown size={12} />
                                    </button>
                                    {rowMenu === menuKey && (
                                      <div style={{
                                        position: "absolute", right: 8, zIndex: 50,
                                        ...(openMenuUp
                                          ? { bottom: "calc(100% - 4px)" }
                                          : { top: "calc(100% - 4px)" }),
                                        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 190, padding: 4, textAlign: "left",
                                      }}>
                                        <button
                                          disabled={!canRemove || busyRemove}
                                          onClick={async () => {
                                            setRowMenu(null);
                                            if (!canRemove) return;
                                            await handleRemoveClick(a, comp);
                                          }}
                                          title={
                                            canRemove
                                              ? (assignLocked ? "Remove assignment (admin override required)" : "Remove assignment")
                                              : "Cannot remove: pricing exists for this traveler-product combination. Remove or adjust pricing lines first."
                                          }
                                          style={{
                                            display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "7px 10px",
                                            border: "none", background: "none", borderRadius: 6, fontSize: 12.5, fontWeight: 600,
                                            cursor: canRemove ? "pointer" : "not-allowed", color: canRemove ? "#dc2626" : "#9ca3af",
                                          }}
                                        >
                                          <Trash2 size={12} /> Remove Assignment
                                        </button>
                                        {!canRemove && (
                                          <div style={{ fontSize: 10.5, color: "#9ca3af", padding: "2px 10px 6px" }}>
                                            Pricing exists — remove pricing first
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <button
                                    disabled={busyAdd}
                                    onClick={async () => { await handleAddClick(trav, comp); }}
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 16px",
                                      fontSize: 12, fontWeight: 700, color: "#fff", background: "#2563eb",
                                      border: "1px solid #2563eb", borderRadius: 8, cursor: "pointer",
                                    }}
                                  >
                                    {busyAdd ? <Loader2 size={12} className={styles.spin} /> : <Plus size={12} />} Assign
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
                )}
              </div>

              {/* Footer info row */}
              <div style={{ display: "flex", flexWrap: "wrap", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 4px" }}>
                {footerBlocks.map((b, bi) => (
                  <div key={bi} style={{ flex: "1 1 0", minWidth: 200, display: "flex", gap: 12, padding: "4px 16px", borderLeft: bi > 0 ? "1px solid #f0f0f0" : "none" }}>
                    <span style={{ width: 38, height: 38, borderRadius: 10, background: ps.bg, color: ps.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {b.icon}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>{b.title}</div>
                      {b.rows.filter((r) => r && (r.v || r.l)).map((r, ri) => (
                        <div key={ri} style={{ display: "flex", gap: 8, fontSize: 12.5, marginBottom: 5, lineHeight: 1.45 }}>
                          {r.l ? <span style={{ color: "#6b7280", fontWeight: 600, flexShrink: 0 }}>{r.l}:</span> : null}
                          <span style={{ fontWeight: 700, color: "#111827", overflowWrap: "anywhere" }}>{r.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {assigned.some((x) => x.hasPricing) && (
                <div className={styles.assignRuleNotice}>
                  <Shield size={13} />
                  <span>
                    If a traveler is assigned to a product and pricing has already been entered for that
                    traveler-product combination, the assignment must not be deleted directly. The system
                    must either block removal or require the related pricing lines to be removed/adjusted
                    first. Once pricing exists, the assignment becomes financially relevant and must be
                    preserved for audit, reporting, invoice, debit note, credit note and VAT purposes.
                  </span>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const MOD_TYPE_LABELS = {
  ROUTE_CHANGE: "Route Change", DATE_CHANGE: "Date Change", SCHEDULE_CHANGE: "Schedule Change",
  ROOM_CHANGE: "Room Change", UPGRADE: "Upgrade", DOWNGRADE: "Downgrade",
  VEHICLE_CHANGE: "Vehicle Change", CANCELLATION_FEE: "Cancellation Fee", OTHER: "Other",
};

// Traveler-level post-booking action types (separate table from product modifications).
const ACTION_TYPE_LABELS = {
  NAME_CORRECTION: "Name Correction", NAME_CHANGE: "Name Change", CANCEL_TRAVELER: "Traveler Cancellation",
};

const PricingTab = ({
  booking: b, components, assignments: rawAssignments, adjustments, subtotal, adjustmentsTotal,
  totalPrice, outstandingAmount, customerCreditAmount, clientPaid, isLocked, bookingId,
  onAddAdjustment, onEditAdjustment, onRemoveAdjustment, onSaveAdjustments, savingAdjustments, adjustmentsDirty,
  onCalculateVAT, setFlightPricingApi, setHotelPricingApi, setTransferPricingApi, setGenericPricingApi, setSSRPricingApi,
  refetch, showToast, ssrs,
}) => {
  const [pricingData, setPricingData] = useState({});
  const [saving, setSaving] = useState(false);
  const [modifications, setModifications] = useState([]);
  const [cancellationCases, setCancellationCases] = useState([]);
  const { execute: fetchModifications } = useGetProductModifications();
  const { execute: fetchCancellationCases } = useGetCancellationCases();

  // Super admin may reverse a post-booking modification from this tab.
  const currentUser = useAppSelector(selectUser);
  const isSuperAdmin = currentUser?.isAdmin === true || currentUser?.currentRoleName === "super_admin";
  const { execute: deleteProductMod, loading: deletingProductMod } = useDeleteProductModification();
  const { execute: deleteTravelerMod, loading: deletingTravelerMod } = useDeleteTravelerModification();
  const [modToRemove, setModToRemove] = useState(null);
  const deletingMod = deletingProductMod || deletingTravelerMod;

  const handleRemoveModification = async () => {
    if (!modToRemove) return;
    const res = modToRemove.kind === "traveler"
      ? await deleteTravelerMod(bookingId, modToRemove.id)
      : await deleteProductMod(bookingId, modToRemove.id);
    if (res.success) {
      showToast(res.message || "Post-booking modification removed", "success");
      setModToRemove(null);
      // Product modifications are fetched locally (keyed on bookingId, which does not
      // change), so refresh that list directly; refetch reloads booking-level data
      // (totals + traveler modifications).
      const modRes = await fetchModifications(bookingId, null);
      if (modRes.success && Array.isArray(modRes.data)) setModifications(modRes.data);
      refetch?.();
    } else {
      showToast(res.message || "Failed to remove modification", "error");
    }
  };

  useEffect(() => {
    if (!bookingId) return;
    fetchModifications(bookingId, null).then((res) => {
      if (res.success && Array.isArray(res.data)) setModifications(res.data);
    });
    fetchCancellationCases(bookingId).then((res) => {
      if (res.success && Array.isArray(res.data)) setCancellationCases(res.data);
    });
  }, [bookingId, fetchModifications, fetchCancellationCases]);

  // Unified post-booking modifications list: product modifications (fetched above)
  // PLUS traveler-level post-booking actions that carry a financial impact. Both
  // share the same financial fields and the same totalModificationAmount formula
  // (supplierCost + agencyFee − refundAmount), so they list and total together.
  const allModifications = useMemo(() => {
    const num = (v) => parseFloat(v || 0);
    const productMods = (modifications || []).map((m) => {
      const parentType =
        (components || []).find((c) => String(c.id) === String(m.productId))?.type || "";
      return {
        key: `prod-${m.id}`,
        id: m.id,
        kind: "product",
        createdAt: m.createdAt,
        typeLabel: MOD_TYPE_LABELS[m.modificationType] || m.modificationType || "Modification",
        title: m.modificationTitle || "—",
        supplierCost: num(m.supplierCost),
        agencyFee: num(m.agencyFee),
        refundAmount: num(m.refundAmount),
        vat: num(m.vatAmount),
        impact: num(m.totalModificationAmount),
        // Hotel/Transfer product mods are already folded into the product subtotal &
        // cost by the backend, so they must NOT be re-added to the headline totals
        // (their selling/cost is already in the breakdown). Flight + traveler mods are not.
        baked: parentType === "Hotel" || parentType === "Transfer",
      };
    });
    const travelerMods = (b.travelerModifications || [])
      .filter((m) =>
        num(m.supplierCost) !== 0 ||
        num(m.agencyFee) !== 0 ||
        num(m.refundAmount) !== 0 ||
        num(m.totalModificationAmount) !== 0
      )
      .map((m) => ({
        key: `trav-${m.id}`,
        id: m.id,
        kind: "traveler",
        modType: m.actionType,
        createdAt: m.createdAt,
        typeLabel: ACTION_TYPE_LABELS[m.actionType] || m.actionType || "Traveler Modification",
        title: m.modificationTitle || ACTION_TYPE_LABELS[m.actionType] || "—",
        supplierCost: num(m.supplierCost),
        agencyFee: num(m.agencyFee),
        refundAmount: num(m.refundAmount),
        vat: num(m.vatAmount),
        impact: num(m.totalModificationAmount),
        baked: false, // traveler mods are never folded into product pricing
      }));
    return [...productMods, ...travelerMods].sort(
      (x, y) => (x.createdAt ? new Date(x.createdAt).getTime() : 0) - (y.createdAt ? new Date(y.createdAt).getTime() : 0)
    );
  }, [modifications, b.travelerModifications, components]);

  const stepProducts = useMemo(() => {
    return components.map((c) => {
      const form = c._raw ? mapApiProductToForm(c._raw) : null;
      if (!form) return { id: `PRD-${c.id}`, type: c.type, supplier: c.supplier, _serverId: c._raw?.id || c.id };
      return { ...form, id: `PRD-${c.id}`, _serverId: c._raw?.id || c.id };
    });
  }, [components]);

  const stepTravelers = useMemo(() => {
    return (b.travelers || []).map((t) => ({
      id: t.id,
      firstName: t.firstName || t.name?.split(" ")[0] || "",
      lastName: t.lastName || t.name?.split(" ").slice(1).join(" ") || "",
      dob: t.rawDob || t.dob || "",
      gender: t.gender || "",
      status: t.status || "Active",
    }));
  }, [b.travelers]);

  const stepAssignments = useMemo(() => {
    return (rawAssignments || []).map((a) => {
      const travIdx = (b.travelers || []).findIndex((t) => t.id === a.travelerId);
      return { productId: `PRD-${a.productId}`, travelerIndex: travIdx >= 0 ? travIdx : 0 };
    });
  }, [rawAssignments, b.travelers]);

  // Map booking SSRs to the format expected by StepPricing's SSRPricingSection
  const stepSSRItems = useMemo(() => {
    // Chain a list of flight legs into an ordered airport list (BRU → AYT → BRU),
    // inserting a leg's departure only when it breaks from the previous arrival so
    // both contiguous round-trips and open-jaw multi-city routes read correctly.
    const buildRouteAirports = (legs) => {
      const out = [];
      (legs || []).forEach((leg) => {
        const dep = (leg.departure || leg.departureAirport || "").toUpperCase();
        const arr = (leg.arrival || leg.arrivalAirport || "").toUpperCase();
        if (dep && out[out.length - 1] !== dep) out.push(dep);
        if (arr && out[out.length - 1] !== arr) out.push(arr);
      });
      return out;
    };

    return (ssrs || []).map((s) => {
      const traveler = (b.travelers || []).find((t) => t.id === s.travelerId);

      // Resolve the flight (and specific segment) this SSR is attached to so the
      // pricing card can show its route. segmentId points at a leg.id; when it is
      // null the SSR applies to the whole flight.
      let flightRoute = [];
      let routeScope = null;   // "segment" | "flight"
      let flightLabel = "";    // e.g. "TK1940" for a single-segment SSR
      const flightComp = (components || []).find(
        (c) => c.type === "Flight" &&
          (c.id === s.productId || String(c._raw?.id) === String(s.productId))
      );
      if (flightComp) {
        const legs = flightComp._raw?.productDetails?.legs || [];
        if (s.segmentId != null) {
          let leg = legs.find((l) => String(l.id) === String(s.segmentId));
          if (!leg && /^\d+$/.test(String(s.segmentId))) leg = legs[Number(s.segmentId)];
          if (leg) {
            flightRoute = buildRouteAirports([leg]);
            routeScope = "segment";
            flightLabel = [leg.airlineCode, leg.flightNumber].filter(Boolean).join("");
          }
        }
        if (flightRoute.length === 0 && legs.length > 0) {
          flightRoute = buildRouteAirports(legs);
          routeScope = "flight";
        }
      }

      return {
        itemId: s.id,
        _serverId: s.id,
        travelerId: s.travelerId,
        qty: s.quantity || 1,
        label: s.ssrName || s.ssrDescription || s.ssrCode || "SSR",
        code: s.ssrCode || "—",
        flightRoute,
        routeScope,
        flightLabel,
        // Owning flight (pricing-card key) — lets the Pricing tab render this
        // SSR inside the flight card it belongs to.
        flightProductId: flightComp ? `PRD-${flightComp.id}` : null,
        travelerLabel: traveler ? `${traveler.firstName || ""} ${traveler.lastName || ""}`.trim() : "—",
        travelerInformation: traveler ? {
          firstName: traveler.firstName || traveler.name?.split(" ")[0] || "",
          lastName: traveler.lastName || traveler.name?.split(" ").slice(1).join(" ") || "",
          dob: traveler.rawDob || traveler.dob || "",
          gender: traveler.gender || "",
        } : null,
      };
    });
  }, [ssrs, b.travelers, components]);

  useEffect(() => {
    const p = {};
    components.forEach((c) => {
      const raw = c._raw;
      if (!raw) return;
      const pricing = raw.productDetails?.pricing || {};
      const prdKey = `PRD-${c.id}`;
      if (c.type === "Flight") {
        (pricing.travelerPricing || []).forEach((tp) => {
          const tIdx = (b.travelers || []).findIndex((t) => t.id === tp.travelerId);
          if (tIdx < 0) return;
          const bound = tp.legType || tp.bound || null;
          const key = bound ? `${tIdx}-${prdKey}-${bound.charAt(0).toUpperCase() + bound.slice(1)}` : `${tIdx}-${prdKey}`;
          p[key] = {
            netPrice: parseFloat(tp.netPrice || 0), tax: parseFloat(tp.tax || 0),
            vatTax: parseFloat(tp.vatTax || 0), commission: parseFloat(tp.commission || 0),
            segFee: parseFloat(tp.segmentFee || tp.segFee || 0), clientPrice: parseFloat(tp.sellingPrice || tp.clientPrice || 0),
            sellingPrice: parseFloat(tp.sellingPrice || tp.clientPrice || 0),
            ticketNumber: tp.ticketNumber || "",
          };
        });
      } else if (c.type === "Hotel") {
        const roomDefs = raw.productDetails?.rooms || [];
        const roomPricingList = pricing.roomPricing || [];
        // Use room definitions as the source of room IDs, match to pricing by roomId
        const roomsToUse = roomDefs.length > 0 ? roomDefs : roomPricingList.map((rp) => ({ id: rp.roomId }));
        roomsToUse.forEach((room, idx) => {
          const roomId = room.id || room.roomId;
          const rp = roomPricingList.find((x) => x.roomId === roomId) || {};
          const key = `hotelroom-${prdKey}-${roomId || `legacy-${prdKey}-${idx}`}`;
          p[key] = {
            netPrice: parseFloat(rp.netPrice || 0), markup: parseFloat(rp.markup || 0),
            vatTax: parseFloat(rp.vatTax || 0), sellingPrice: parseFloat(rp.sellingPrice || 0),
          };
        });
      } else if (c.type === "Transfer") {
        const key = `transfer-${prdKey}`;
        p[key] = {
          costPrice: parseFloat(pricing.costPrice || 0), markup: parseFloat(pricing.markup || 0),
          vatTax: parseFloat(pricing.vatTax || 0), sellingPrice: parseFloat(pricing.sellingPrice || 0),
        };
      } else {
        const tps = pricing.travelerPricing || [];
        if (tps.length > 0) {
          (b.travelers || []).forEach((t, tIdx) => {
            const hasAssign = (rawAssignments || []).some((a) => a.travelerId === t.id && a.productId === c.id);
            if (!hasAssign) return;
            const tp = tps.find((x) => x.travelerId === t.id) || {};
            const key = `${tIdx}-${prdKey}`;
            p[key] = {
              costPrice: parseFloat(tp.costPrice || 0), markup: parseFloat(tp.markup || 0),
              vatTax: parseFloat(tp.vatTax || 0), sellingPrice: parseFloat(tp.sellingPrice || 0),
            };
          });
        } else if (pricing.costPrice !== undefined || pricing.sellingPrice !== undefined) {
          // Single product-level pricing (e.g. Car Rental). Stored sellingPrice is
          // VAT-exclusive; recompute the editable display value as cost + markup + vat.
          const cst = parseFloat(pricing.costPrice || 0);
          const mk = parseFloat(pricing.markup || 0);
          const vt = parseFloat(pricing.vatTax || pricing.vat || 0);
          p[`single-${prdKey}`] = { costPrice: cst, markup: mk, vatTax: vt, sellingPrice: cst + mk + vt };
        }
      }
    });
    // Load SSR pricing data
    (ssrs || []).forEach((s) => {
      const key = `ssr-${s.id}-${s.travelerId ?? 0}`;
      p[key] = {
        costPrice: parseFloat(s.costPrice || 0),
        markup: parseFloat(s.markup || 0),
        vatTax: parseFloat(s.vatTax || 0),
        sellingPrice: parseFloat(s.sellingPrice || 0),
      };
    });
    setPricingData(p);
  }, [components, b.travelers, rawAssignments, ssrs]);

  // ── Cancellation-aware pricing model ───────────────────────────────────────
  // Builds, per cancelled / partially-cancelled product, the headline figures the
  // redesigned pricing cards show (original vs active vs cancelled selling+purchase,
  // supplier + agency fees, customer refund, credit note) PLUS a booking-level
  // "Price Breakdown (After Cancellation)". Mirrors the backend's canonical
  // recalculateBookingTotals so the numbers agree with the booking grand total:
  //   • fully cancelled product → top-level cancellation {cost,fee,vat}
  //   • still-active partial flight → directionFees[] only (never top-level)
  // The Sunsky fee is VAT-INCLUSIVE (VAT lives inside it), so total cancellation
  // = supplier cost + fee, and customer refund = cancelled value − total fees.
  const cancellationData = useMemo(() => {
    const num = (v) => parseFloat(v || 0);
    const cases = Array.isArray(cancellationCases) ? cancellationCases : [];
    const fmtDate = (d) => {
      if (!d) return "";
      try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }); }
      catch { return ""; }
    };
    // Latest confirmed case carrying a document = the credit/debit note to surface.
    const docCase = [...cases]
      .filter((c) => c.documentNumber)
      .sort((a, b2) => new Date(b2.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;
    const creditNote = docCase ? { number: docCase.documentNumber, status: "Issued", issuedOn: fmtDate(docCase.createdAt) } : null;

    const travName = (id) => (b.travelers || []).find((t) => String(t.id) === String(id))?.name || "—";
    const flightRowLines = (tp) => {
      const net = num(tp.netPrice), tax = num(tp.tax), vat = num(tp.vatTax);
      const com = num(tp.commission), seg = num(tp.segmentFee != null ? tp.segmentFee : tp.segFee);
      const sell = tp.sellingPrice != null ? num(tp.sellingPrice) : (tp.clientPrice != null ? num(tp.clientPrice) : net + tax + vat + com + seg);
      return { "Net Price": net, "Markup": Math.max(sell - (net + tax + vat + com + seg), 0), "Taxes": tax, "VAT": vat, "Commission": com, "Service Fee": seg };
    };
    const genericLine = (o) => ({ "Net Price": num(o.netPrice != null ? o.netPrice : o.costPrice), "Markup": num(o.markup), "Taxes": 0, "VAT": num(o.vatTax != null ? o.vatTax : o.vat), "Commission": 0, "Service Fee": 0 });
    const ROW_KEYS = ["Net Price", "Markup", "Taxes", "VAT", "Commission", "Service Fee"];
    const empty = () => Object.fromEntries(ROW_KEYS.map((k) => [k, 0]));
    const add = (acc, line) => { ROW_KEYS.forEach((k) => { acc[k] += line[k] || 0; }); return acc; };

    const byProduct = {};
    const gActive = empty(), gCancelled = empty();
    let gSupplierFee = 0, gAgencyFee = 0, gFeeVat = 0;
    let hasAnyCancellation = false;

    (components || []).forEach((c) => {
      const raw = c._raw;
      const cn = raw?.productDetails?.cancellation || {};
      const pr = raw?.productDetails?.pricing || {};
      const isCancelled = c.status === "Cancelled";
      const cancelledDirections = cn.cancelledDirections || [];
      const directionFees = cn.directionFees || [];
      const hasPartial = directionFees.length > 0 && !isCancelled;

      // Per-product active/cancelled financial lines (always accumulate into the
      // booking breakdown so active products feed the Active/Original totals).
      const pActive = empty(), pCancelled = empty();
      if (c.type === "Flight") {
        const curTp = pr.travelerPricing || [];
        if (isCancelled) { curTp.forEach((tp) => add(pCancelled, flightRowLines(tp))); }
        else {
          curTp.forEach((tp) => add(pActive, flightRowLines(tp)));
          if (cn.fullModeOriginal) {
            const orig = empty(); cn.fullModeOriginal.forEach((tp) => add(orig, flightRowLines(tp)));
            ROW_KEYS.forEach((k) => { pCancelled[k] = Math.max(orig[k] - pActive[k], 0); });
          } else {
            (cn.removedPricing || []).forEach((tp) => add(pCancelled, flightRowLines(tp)));
          }
        }
      } else if (c.type === "Hotel") {
        (pr.roomPricing || []).forEach((r) => add(isCancelled ? pCancelled : pActive, genericLine(r)));
      } else if (c.type === "Transfer") {
        add(isCancelled ? pCancelled : pActive, genericLine(pr));
      } else {
        const tps = pr.travelerPricing || [];
        if (tps.length) tps.forEach((tp) => add(isCancelled ? pCancelled : pActive, genericLine(tp)));
        else add(isCancelled ? pCancelled : pActive, genericLine(pr));
      }
      add(gActive, pActive); add(gCancelled, pCancelled);

      if (!isCancelled && !hasPartial) return; // active, untouched → no card
      hasAnyCancellation = true;

      // Fees (backend-canonical: cancelled→top-level, active-partial→directionFees).
      let supplierFee, agencyFee, feeVat;
      if (isCancelled) { supplierFee = num(cn.cost); agencyFee = num(cn.fee); feeVat = num(cn.vat); }
      else {
        supplierFee = directionFees.reduce((s, d) => s + num(d.cost), 0);
        agencyFee = directionFees.reduce((s, d) => s + num(d.fee), 0);
        feeVat = directionFees.reduce((s, d) => s + num(d.vat), 0);
      }
      const totalFees = supplierFee + agencyFee;
      gSupplierFee += supplierFee; gAgencyFee += agencyFee; gFeeVat += feeVat;

      const sumLines = (o) => ROW_KEYS.reduce((s, k) => s + o[k], 0);
      const activeSelling = isCancelled ? 0 : sumLines(pActive);
      const cancelledValue = sumLines(pCancelled);
      const originalSelling = activeSelling + cancelledValue;
      const activePurchase = isCancelled ? 0 : pActive["Net Price"];
      const cancelledPurchase = pCancelled["Net Price"];
      const originalPurchase = activePurchase + cancelledPurchase;
      const customerRefund = Math.max(0, cancelledValue - totalFees);

      // Traveler counts (flights only, matching the screenshots' strip).
      const assignedIds = [...new Set((rawAssignments || []).filter((a) => a.productId === c.id).map((a) => a.travelerId))];
      const totalTravelers = c.type === "Flight" ? assignedIds.length : 0;
      const cancelledTravelers = (b.travelers || []).filter((t) => t.status === "Cancelled" && assignedIds.includes(t.id)).length;

      // Cancelled flight directions → read-only removed-row blocks.
      const removedDirections = [];
      if (c.type === "Flight" && !isCancelled && cancelledDirections.length) {
        cancelledDirections.forEach((dir) => {
          const rows = (cn.removedPricing || [])
            .filter((tp) => String(tp.legType || "").toLowerCase() === String(dir).toLowerCase())
            .map((tp) => ({
              name: travName(tp.travelerId), net: num(tp.netPrice), tax: num(tp.tax), vat: num(tp.vatTax),
              commission: num(tp.commission), segFee: num(tp.segmentFee != null ? tp.segmentFee : tp.segFee),
              clientPrice: tp.sellingPrice != null ? num(tp.sellingPrice) : num(tp.clientPrice),
            }));
          const dirLegs = (raw?.productDetails?.legs || []).filter((l) => String(l.direction || "").toLowerCase() === String(dir).toLowerCase());
          const first = dirLegs[0], last = dirLegs[dirLegs.length - 1];
          const route = first ? `${first.departureAirport || first.departure || "—"} → ${(last || first).arrivalAirport || (last || first).arrival || "—"}` : "";
          removedDirections.push({ direction: dir, route, meta: first?.departureDate || "", rows });
        });
      }

      const bannerWhat = c.type === "Flight"
        ? (isCancelled ? "This flight has been cancelled" : `${cancelledDirections.join(" & ") || "A direction"} of this flight has been cancelled`)
        : `This ${c.type.toLowerCase()} has been cancelled`;
      const banner = `${bannerWhat}. ${creditNote ? "A credit note has been issued and a" : "A"} refund is due to the customer.`;

      byProduct[`PRD-${c.id}`] = {
        productType: c.type,
        status: isCancelled ? "Cancelled" : "Partial",
        cancelledAll: isCancelled,
        cancelledDirections,
        originalSelling, activeSelling, cancelledValue,
        originalPurchase, activePurchase,
        supplierFee, agencyFee, totalFees, feeVat,
        customerRefund, refundDue: customerRefund,
        totalTravelers, activeTravelers: totalTravelers - cancelledTravelers, cancelledTravelers,
        creditNote, removedDirections, banner,
      };
    });

    // Booking-level breakdown rows. Distribute the total retained cancellation fee across
    // the cancelled rows in order — each row absorbs at most its own cancelled value, and
    // any overflow carries to the next row. Previously the whole supplier fee sat on the
    // "Net Price" row and the per-row Math.max(cancelled − fee, 0) clamped away the
    // overflow: a €199,43 fee on a €168,61 Net Price line lost €30,82, so Taxes +
    // Commission were refunded in full and the customer was OVER-refunded (€181,39 instead
    // of €150,57). Cascading keeps the total Refund Due = cancelled − fees, matching the
    // per-product customerRefund and the credit note.
    let feeLeft = gSupplierFee + Math.max(gAgencyFee - gFeeVat, 0) + gFeeVat;
    const rows = ROW_KEYS.map((label) => {
      const active = gActive[label], cancelled = gCancelled[label];
      const fees = Math.min(feeLeft, cancelled);
      feeLeft -= fees;
      return { label, original: active + cancelled, cancelled, fees, active, refundDue: Math.max(cancelled - fees, 0) };
    });
    const sumCol = (k) => rows.reduce((s, r) => s + r[k], 0);
    const totals = { original: sumCol("original"), cancelled: sumCol("cancelled"), fees: sumCol("fees"), active: sumCol("active"), refundDue: sumCol("refundDue") };

    return { byProduct, breakdown: { rows, totals, creditNote }, hasAnyCancellation };
  }, [components, cancellationCases, b.travelers, rawAssignments]);

  const stepTravelerMods = useMemo(() => {
    return (b.travelerModifications || [])
      .filter((m) => m.actionType === "CANCEL_TRAVELER")
      .map((m) => ({
        travelerId: m.travelerId,
        supplierCost: parseFloat(m.supplierCost || 0),
        agencyFee: parseFloat(m.agencyFee || 0),
        refundAmount: parseFloat(m.refundAmount || 0),
        vatAmount: parseFloat(m.vatAmount || 0),
      }));
  }, [b.travelerModifications]);

  const stepData = useMemo(() => ({
    pricing: pricingData,
    products: stepProducts,
    travelers: stepTravelers,
    assignments: stepAssignments,
    ssrItems: stepSSRItems,
    adjustments: adjustments,
    saleType: b.saleType || "",
    bookingGrandTotal: parseFloat(b.totalPrice || 0),
    cancellation: cancellationData.byProduct,
    travelerMods: stepTravelerMods,
    hasClientPayments: (clientPaid || 0) > 0,
  }), [pricingData, stepProducts, stepTravelers, stepAssignments, stepSSRItems, adjustments, b.saleType, b.totalPrice, cancellationData, stepTravelerMods, clientPaid]);

  const handlePricingChange = (field, value) => {
    if (field === "pricing") setPricingData(value);
  };

  /**
   * Which direction keys a flight's pricing is held under.
   *
   * flightPricingMode is the intent, but the stored rows are the fact, and they
   * part company: removing one direction of a split flight leaves its rows in
   * place while the mode reverts to FULL. Whichever shape the data is in has to
   * be the shape we read and write, or the rows become unreachable from the UI.
   */
  const flightPricingDirections = (product, prdKey, priced) => {
    const split = ["Outbound", "Inbound"];
    if (product?.flightPricingMode === "SPLIT") return split;
    const hasDirectionRows = split.some((dir) =>
      Object.keys(priced || {}).some((k) => k.endsWith(`-${prdKey}-${dir}`))
    );
    return hasDirectionRows ? split : [null];
  };

  const handleSavePricing = async () => {
    if (!bookingId || isLocked) return;
    setSaving(true);
    let hasError = false;

    for (const c of components) {
      const prdKey = `PRD-${c.id}`;
      const serverId = c._raw?.id || c.id;
      if (!serverId) continue;

      if (c.type === "Flight") {
        const travelerPricing = [];
        const product = stepProducts.find((p) => p.id === prdKey);
        // Read the shape from the rows that are actually stored, not only from
        // the mode flag. The two can disagree: dropping a direction from a split
        // flight flips the flag to FULL while the Outbound/Inbound rows remain,
        // and trusting the flag then makes us look for keys that do not exist,
        // send nothing, and leave that money on the booking for good.
        const directions = flightPricingDirections(product, prdKey, pricingData);
        (b.travelers || []).forEach((t, tIdx) => {
          directions.forEach((dir) => {
            const key = dir ? `${tIdx}-${prdKey}-${dir}` : `${tIdx}-${prdKey}`;
            const p = pricingData[key];
            if (!p) return;
            travelerPricing.push({
              travelerId: t.id, legType: dir || null,
              netPrice: p.netPrice || 0, tax: p.tax || 0, vatTax: p.vatTax || 0,
              commission: p.commission || 0, segmentFee: p.segFee || 0,
              sellingPrice: p.clientPrice || p.sellingPrice || 0, ticketNumber: p.ticketNumber || "",
            });
          });
        });
        // Sent even when empty. An empty array is the instruction "this flight
        // has no prices", and skipping the call on empty is what made clearing
        // every price a no-op: the screen went blank, the stored rows stayed,
        // and the booking total never moved.
        const res = await setFlightPricingApi(bookingId, { productId: serverId, travelerPricing });
        if (!res.success) { hasError = true; showToast(`Flight pricing save failed: ${res.message}`, "error"); }
      } else if (c.type === "Hotel") {
        const product = stepProducts.find((p) => p.id === prdKey);
        const rooms = product?.rooms || [{ id: `legacy-${prdKey}-0` }];
        const roomPricing = rooms.map((room, idx) => {
          const key = `hotelroom-${prdKey}-${room.id || `legacy-${prdKey}-${idx}`}`;
          const p = pricingData[key] || {};
          return { roomId: room.id, netPrice: p.netPrice || 0, markup: p.markup || 0, vatTax: p.vatTax || 0, sellingPrice: p.sellingPrice || 0 };
        });
        const res = await setHotelPricingApi(bookingId, { productId: serverId, roomPricing });
        if (!res.success) { hasError = true; showToast(`Hotel pricing save failed: ${res.message}`, "error"); }
      } else if (c.type === "Transfer") {
        const key = `transfer-${prdKey}`;
        const p = pricingData[key] || {};
        const res = await setTransferPricingApi(bookingId, { productId: serverId, costPrice: p.costPrice || 0, markup: p.markup || 0, vatTax: p.vatTax || 0, sellingPrice: p.sellingPrice || 0 });
        if (!res.success) { hasError = true; showToast(`Transfer pricing save failed: ${res.message}`, "error"); }
      } else if (["Insurance", "Package", "CarRental", "Visa", "Cruise", "Incoming"].includes(c.type)) {
        // Generic products — per-traveler pricing (keyed `${travelerIndex}-PRD-${id}`)
        const travelerPricing = [];
        (b.travelers || []).forEach((t, tIdx) => {
          const hasAssign = (rawAssignments || []).some((a) => a.travelerId === t.id && a.productId === c.id);
          if (!hasAssign) return;
          const p = pricingData[`${tIdx}-${prdKey}`];
          if (!p) return;
          const gCost = parseFloat(p.costPrice || 0);
          const gMarkup = parseFloat(p.markup || 0);
          const gVat = parseFloat(p.vatTax || 0);
          travelerPricing.push({
            travelerId: t.id,
            costPrice: gCost,
            markup: gMarkup,
            vatTax: gVat,
            // Persist the VAT-inclusive selling price explicitly (consistent with
            // Transfer/SSR); the backend also recomputes it, so this is just robustness.
            sellingPrice: p.sellingPrice != null ? parseFloat(p.sellingPrice) : parseFloat((gCost + gMarkup + gVat).toFixed(2)),
          });
        });
        if (travelerPricing.length > 0 && setGenericPricingApi) {
          const res = await setGenericPricingApi(bookingId, { productId: serverId, travelerPricing });
          if (!res.success) { hasError = true; showToast(`${c.type} pricing save failed: ${res.message}`, "error"); }
        } else if (setGenericPricingApi) {
          // No traveler assignments → save a single product-level price (e.g. Car Rental).
          const sp = pricingData[`single-${prdKey}`];
          if (sp && (parseFloat(sp.costPrice || 0) > 0 || parseFloat(sp.markup || 0) > 0 || parseFloat(sp.vatTax || 0) > 0)) {
            const res = await setGenericPricingApi(bookingId, {
              productId: serverId,
              costPrice: parseFloat(sp.costPrice || 0),
              markup: parseFloat(sp.markup || 0),
              vat: parseFloat(sp.vatTax || 0),
            });
            if (!res.success) { hasError = true; showToast(`${c.type} pricing save failed: ${res.message}`, "error"); }
          }
        }
      }
    }

    // Save SSR pricing
    if (setSSRPricingApi && stepSSRItems.length > 0) {
      for (const ssr of stepSSRItems) {
        const key = `ssr-${ssr.itemId}-${ssr.travelerId ?? 0}`;
        const p = pricingData[key];
        if (!p) continue;
        const res = await setSSRPricingApi(bookingId, {
          ssrId: ssr._serverId,
          costPrice: p.costPrice || 0,
          markup: p.markup || 0,
          vatTax: p.vatTax || 0,
          sellingPrice: p.sellingPrice || 0,
        });
        if (!res.success) { hasError = true; showToast(`SSR pricing save failed: ${res.message}`, "error"); }
      }
    }

    if (!hasError) {
      showToast("Pricing saved successfully", "success");
      refetch?.();
    }
    setSaving(false);
  };

  const pricing = b.pricing;
  const hasAdjustments = adjustments.length > 0;

  // ── Post-booking modifications folded into the displayed totals ──────────────
  // Count each modification exactly once: Hotel/Transfer product mods are already
  // inside the product subtotal/cost (baked), so we add only the NOT-yet-counted
  // ones (flight + traveler mods). VAT liability never includes any modification
  // VAT, so we add ALL modification VAT.
  const modsNetForTotal = allModifications
    .filter((m) => !m.baked && m.modType !== "CANCEL_TRAVELER")
    .reduce((s, m) => s + m.impact, 0);
  const modsCostForTotal = allModifications
    .filter((m) => !m.baked && m.modType !== "CANCEL_TRAVELER")
    .reduce((s, m) => s + Math.max(m.supplierCost - m.refundAmount, 0), 0);
  const modsVatTotal = allModifications.reduce((s, m) => s + m.vat, 0);

  const displayTotal = (totalPrice || 0) + modsNetForTotal; // GROSS Total Selling Price
  const displayCost = (pricing.costPrice || 0) + modsCostForTotal;
  // Total Selling Price is always GROSS. Cancellation refund is not deducted here — it
  // surfaces as customer credit / negative outstanding when payments exceed the total.
  const displayNetPayable = displayTotal;
  const displayMargin = displayNetPayable - displayCost;
  const displayVat = (pricing.tomsVat || 0) + modsVatTotal;
  // Actual net paid = applied (capped) clientPaid + the overpaid excess (customer
  // credit). Signed outstanding: + = client still owes; − = we owe the client a refund
  // (e.g. a paid-then-cancelled booking) → returns to 0 once the refund is paid out.
  const actualNetPaid = (clientPaid || 0) + (customerCreditAmount || 0);
  const displaySignedOutstanding = displayNetPayable - actualNetPaid;
  const displayOutstanding = Math.max(0, displaySignedOutstanding);
  const displayCustomerCredit = Math.max(0, -displaySignedOutstanding);

  // VAT Summary rows: existing per-component VAT (if any) + a consolidated
  // post-booking modifications VAT line so the VAT Liability is fully explained.
  const vatRows = [
    ...(Array.isArray(pricing.vat) ? pricing.vat : []),
    ...(modsVatTotal !== 0
      ? [{
          component: "Post-Booking Modifications",
          regime: "NORMAL VAT",
          rate: "21%",
          amount: fmt(modsVatTotal),
        }]
      : []),
  ];

  return (
    <div>
      {/* Pricing Overview summary cards hidden for now — flip `false` to `true` to restore.
          (Per-product "Save Pricing" footers in BookingDetailPricing still handle saving.) */}
      {false && (
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}><Euro size={16} /> Pricing Overview</div>
          {!isLocked && (
            <LoadingButton className={styles.btnPrimary} loading={saving} loadingText="Saving…" icon={<Save size={13} />} onClick={handleSavePricing}>Save Pricing</LoadingButton>
          )}
        </div>
        <div className={styles.kpiRow3}>
          <div className={`${styles.kpiCard} ${styles.kpiBlue}`}>
            <div className={styles.kpiContent}>
              <div className={styles.kpiLabel}>Total Selling Price <Info size={12} className={styles.kpiInfoIcon} /></div>
              <div className={styles.kpiAmount} style={{ color: "#1d4ed8" }}>{fmt(displayNetPayable)}</div>
              <div className={styles.kpiSub}>Invoiced to customer{modsNetForTotal !== 0 ? " (incl. post-booking modifications)" : ""}</div>
            </div>
            <div className={styles.kpiIcon}><FileText size={28} /></div>
          </div>
          <div className={`${styles.kpiCard} ${displayMargin < 0 ? styles.kpiRed : styles.kpiGreen}`}>
            <div className={styles.kpiContent}>
              <div className={styles.kpiLabel}>Gross Margin <Info size={12} className={styles.kpiInfoIcon} /></div>
              <div className={styles.kpiAmount} style={{ color: displayMargin < 0 ? "#dc2626" : "#16a34a" }}>{fmt(displayMargin)}</div>
              <div className={styles.kpiSub}>{displayTotal > 0 ? ((displayMargin / displayTotal) * 100).toFixed(1) : "0.0"}% margin</div>
            </div>
            <div className={styles.kpiIcon}><TrendingUp size={28} /></div>
          </div>
          <div className={`${styles.kpiCard} ${displayOutstanding > 0 ? styles.kpiRed : displayCustomerCredit > 0.005 ? styles.kpiRed : styles.kpiNeutralPaid}`}>
            <div className={styles.kpiContent}>
              <div className={styles.kpiLabel}>Outstanding Balance <Info size={12} className={styles.kpiInfoIcon} /></div>
              <div className={styles.kpiAmount} style={{ color: Math.abs(displaySignedOutstanding) > 0.005 ? (displaySignedOutstanding > 0 ? "#dc2626" : "#ea580c") : "#16a34a" }}>{fmt(displaySignedOutstanding)}</div>
              <div className={styles.kpiSub}>
                {displayOutstanding > 0
                  ? `To be received from customer`
                  : displayCustomerCredit > 0.005
                    ? <span style={{ color: "#ea580c", fontWeight: 700 }}>Refund due to customer</span>
                    : <><Check size={12} style={{ color: "#16a34a", verticalAlign: "middle", marginRight: 3 }} />Fully Paid</>}
              </div>
            </div>
            <div className={styles.kpiIcon}><AlertCircle size={28} /></div>
          </div>
        </div>
        <div className={styles.kpiRow2}>
          <div className={`${styles.kpiCard} ${styles.kpiNeutral}`}>
            <div className={styles.kpiContent}>
              <div className={styles.kpiLabel}>Purchase Price <Info size={12} className={styles.kpiInfoIcon} /></div>
              <div className={styles.kpiAmount} style={{ color: "#334155" }}>{fmt(displayCost)}</div>
              <div className={styles.kpiSub}>Paid to suppliers</div>
            </div>
            <div className={styles.kpiIcon}><ShoppingCart size={28} /></div>
          </div>
          <div className={`${styles.kpiCard} ${styles.kpiYellow}`}>
            <div className={styles.kpiContent}>
              <div className={styles.kpiLabel}>VAT Liability <Info size={12} className={styles.kpiInfoIcon} /></div>
              <div className={styles.kpiAmount} style={{ color: "#b45309" }}>{fmt(displayVat)}</div>
              <div className={styles.kpiSub}>TOMS Margin{modsVatTotal !== 0 ? " + post-booking modifications" : ""}</div>
            </div>
            <div className={styles.kpiIcon}><Landmark size={28} /></div>
          </div>
        </div>
      </div>
      )}

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}><Euro size={16} /> Editable Product Pricing</div>
          {isLocked && <span className={styles.readOnlyBadge}>Locked</span>}
        </div>
        <BookingDetailPricing
          data={stepData}
          onChange={handlePricingChange}
          onCalculateVAT={onCalculateVAT}
          hideSummary
          onSavePricing={isLocked ? undefined : handleSavePricing}
          savingPricing={saving}
        />
      </div>

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}><Tag size={16} /> Adjustments (Discounts & Surcharges)</div>
          {!isLocked && (hasAdjustments || adjustmentsDirty) && (
            <div style={{ display: "flex", gap: 6 }}>
              <button className={styles.btnAddDiscount} onClick={onAddAdjustment}><Plus size={13} /> Add Adjustment</button>
              <LoadingButton className={styles.btnPrimary} loading={savingAdjustments} loadingText="Saving…" icon={<Save size={13} />} onClick={onSaveAdjustments}>Save</LoadingButton>
            </div>
          )}
        </div>

        {hasAdjustments ? (
          <div className={styles.adjustmentsListSection}>
            {adjustments.map((adj, idx) => {
              const isDiscount = adj.type === "Discount";
              const calcVal = adj.mode === "Fixed" ? adj.value : (subtotal * adj.value) / 100;
              return (
                <div key={adj.id || idx} className={`${styles.adjustmentCard} ${isDiscount ? styles.adjustmentCardDiscount : styles.adjustmentCardSurcharge}`}>
                  <div className={styles.adjustmentCardHeader}>
                    <div className={styles.adjustmentCardTitle}>
                      {isDiscount ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                      {adj.title || adj.type}
                      <span className={styles.adjustmentCardMode}>{adj.mode === "Fixed" ? `€ ${adj.value}` : `${adj.value}%`}</span>
                      {(adj.isFee || adj.type === "fee") && (
                        <span style={{ marginLeft: 6, fontSize: "10px", fontWeight: 600, color: "#92600a", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 4, padding: "1px 6px" }}>
                          Payment fee
                        </span>
                      )}
                    </div>
                    {!isLocked && (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className={styles.btnEditOutline} onClick={() => onEditAdjustment(idx)}><Pencil size={11} /> Edit</button>
                        <button className={styles.btnRemoveDiscount} onClick={() => onRemoveAdjustment(idx)}><X size={12} /></button>
                      </div>
                    )}
                  </div>
                  <div className={styles.adjustmentCardGrid}>
                    <div className={styles.adjustmentCardItem}>
                      <span className={styles.adjustmentCardLabel}>Amount</span>
                      <span className={styles.adjustmentCardValue} style={{ color: isDiscount ? "#c42b2b" : "#0d7a4f" }}>
                        {isDiscount ? "−" : "+"} {fmt(calcVal)}
                      </span>
                    </div>
                    {adj.description && (
                      <div className={styles.adjustmentCardItem}>
                        <span className={styles.adjustmentCardLabel}>Description</span>
                        <span className={styles.adjustmentCardValue}>{adj.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !isLocked && (
            <div className={styles.discountPrompt}>
              <button className={styles.btnAddDiscount} onClick={onAddAdjustment}><Plus size={13} /> Add Adjustment</button>
              <span className={styles.discountPromptText}>Apply discounts or surcharges to this booking</span>
            </div>
          )
        )}
      </div>

      {allModifications.length > 0 && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}><Euro size={16} /> Post-Booking Modifications ({allModifications.length})</div>
            {isSuperAdmin && (
              <span className={styles.readOnlyBadge} style={{ background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" }}>
                Super Admin — can remove
              </span>
            )}
          </div>
          <div className={styles.priceBreakdown}>
            <div className={`${styles.priceRow} ${styles.priceRowHead}`}>
              <span style={{ width: 90 }}>Date</span>
              <span style={{ width: 130 }}>Type</span>
              <span style={{ flex: 1 }}>Title</span>
              <span style={{ width: 100, textAlign: "right" }}>Supplier Cost</span>
              <span style={{ width: 100, textAlign: "right" }}>Agency Fee</span>
              <span style={{ width: 100, textAlign: "right" }}>Refund</span>
              <span style={{ width: 90, textAlign: "right" }}>VAT</span>
              <span style={{ width: 110, textAlign: "right" }}>Net Impact</span>
              {isSuperAdmin && <span style={{ width: 44, textAlign: "right" }} />}
            </div>
            {allModifications.map((mod) => {
              const isPositive = mod.impact >= 0;
              return (
                <div key={mod.key} className={styles.priceRow}>
                  <span style={{ width: 90, color: "#6b7280", fontSize: "12px" }}>
                    {mod.createdAt ? new Date(mod.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}
                  </span>
                  <span style={{ width: 130, color: "#4b5563", fontSize: "12px" }}>
                    {mod.typeLabel}
                  </span>
                  <span style={{ flex: 1, color: "#4b5563" }}>{mod.title || "—"}</span>
                  <span style={{ width: 100, textAlign: "right", color: "#6b7280" }}>{mod.supplierCost !== 0 ? fmt(mod.supplierCost) : "—"}</span>
                  <span style={{ width: 100, textAlign: "right", color: "#6b7280" }}>{mod.agencyFee !== 0 ? fmt(mod.agencyFee) : "—"}</span>
                  <span style={{ width: 100, textAlign: "right", color: "#6b7280" }}>{mod.refundAmount !== 0 ? fmt(mod.refundAmount) : "—"}</span>
                  <span style={{ width: 90, textAlign: "right", color: "#6b7280" }}>{mod.vat !== 0 ? fmt(mod.vat) : "—"}</span>
                  <span style={{ width: 110, textAlign: "right", fontWeight: 700, color: isPositive ? "#c42b2b" : "#0d7a4f" }}>
                    {isPositive ? "+" : "−"} {fmt(Math.abs(mod.impact))}
                  </span>
                  {isSuperAdmin && (
                    <span style={{ width: 44, textAlign: "right" }}>
                      <button
                        type="button"
                        className={styles.iconBtnDanger}
                        title="Remove this modification (super admin)"
                        aria-label="Remove modification"
                        onClick={() => setModToRemove(mod)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  )}
                </div>
              );
            })}
            <div className={`${styles.priceRow} ${styles.priceRowTotal}`}>
              <span style={{ flex: 1, fontWeight: 700 }}>Total Modifications</span>
              <span style={{ width: 100 }} /><span style={{ width: 100 }} /><span style={{ width: 100 }} />
              {(() => {
                const vatTotal = allModifications.reduce((s, m) => s + m.vat, 0);
                return (
                  <span style={{ width: 90, textAlign: "right", fontWeight: 700, color: "#6b7280" }}>
                    {vatTotal !== 0 ? fmt(vatTotal) : "—"}
                  </span>
                );
              })()}
              {(() => {
                const total = allModifications.reduce((s, m) => s + m.impact, 0);
                const pos = total >= 0;
                return (
                  <span style={{ width: 110, textAlign: "right", fontWeight: 800, color: pos ? "#c42b2b" : "#0d7a4f" }}>
                    {pos ? "+" : "−"} {fmt(Math.abs(total))}
                  </span>
                );
              })()}
              {isSuperAdmin && <span style={{ width: 44 }} />}
            </div>
          </div>
        </div>
      )}

      {cancellationData.hasAnyCancellation && (
        <div style={{ marginBottom: 16 }}>
          <PriceBreakdownAfterCancellation
            breakdown={cancellationData.breakdown}
            onViewFull={() => document.getElementById("pricing-breakdown-detail")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          />
        </div>
      )}

      <div className={styles.sectionCard} id="pricing-breakdown-detail">
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}><Euro size={16} /> Price Breakdown</div>
          {((components || []).some((c) => c.status === "Cancelled") || (b.travelers || []).some((t) => t.status === "Cancelled") || (ssrs || []).some((s) => s.status === "Cancelled")) && (
            <span className={styles.sectionSubtitle}>Live items only — cancelled items excluded from totals (see Cancellation Effects)</span>
          )}
        </div>
        <div className={styles.priceBreakdown}>
          <div className={`${styles.priceRow} ${styles.priceRowHead}`}>
            <span style={{ flex: 1 }}>Component</span>
            <span style={{ width: 120, textAlign: "right" }}>Cost</span>
            <span style={{ width: 120, textAlign: "right" }}>Sell</span>
            <span style={{ width: 100, textAlign: "right" }}>Margin</span>
          </div>
          {pricing.breakdown.map((r) => (
            <div key={r.label} className={styles.priceRow}>
              <span style={{ flex: 1, color: "#4b5563" }}>{r.label}</span>
              <span style={{ width: 120, textAlign: "right", color: "#6b7280" }}>{fmt(r.cost)}</span>
              <span style={{ width: 120, textAlign: "right", fontWeight: 600 }}>{fmt(r.sell)}</span>
              <span style={{ width: 100, textAlign: "right", color: "#0d7a4f", fontWeight: 700 }}>{fmt(r.sell - r.cost)}</span>
            </div>
          ))}
          <div className={`${styles.priceRow} ${styles.priceRowTotal}`}>
            <span style={{ flex: 1, fontWeight: 700 }}>Subtotal</span>
            <span style={{ width: 120, textAlign: "right", fontWeight: 700 }}>{fmt(pricing.costPrice)}</span>
            <span style={{ width: 120, textAlign: "right", fontWeight: 700 }}>{fmt(pricing.sellingPrice)}</span>
            <span style={{ width: 100, textAlign: "right", color: "var(--primary-color, #f5a51e)", fontWeight: 800 }}>{fmt(pricing.margin)}</span>
          </div>
          {adjustments.map((adj, idx) => {
            const isDiscount = adj.type === "Discount";
            const calcVal = adj.mode === "Fixed" ? adj.value : (subtotal * adj.value) / 100;
            return (
              <div key={adj.id || idx} className={styles.priceRow}>
                <span style={{ flex: 1, color: isDiscount ? "#c42b2b" : "#0d7a4f", fontWeight: 600 }}>
                  {adj.title || adj.type} ({adj.mode === "Fixed" ? "Fixed" : `${adj.value}%`})
                </span>
                <span style={{ width: 120 }} />
                <span style={{ width: 120, textAlign: "right", color: isDiscount ? "#c42b2b" : "#0d7a4f", fontWeight: 700 }}>
                  {isDiscount ? "−" : "+"} {fmt(calcVal)}
                </span>
                <span style={{ width: 100 }} />
              </div>
            );
          })}
          {hasAdjustments && (
            <div className={`${styles.priceRow} ${styles.priceRowTotal}`}>
              <span style={{ flex: 1, fontWeight: 700 }}>Final Total</span>
              <span style={{ width: 120 }} />
              <span style={{ width: 120, textAlign: "right", fontWeight: 800, fontSize: "14px" }}>{fmt(totalPrice)}</span>
              <span style={{ width: 100 }} />
            </div>
          )}
        </div>
      </div>

      {/* ── Cancellation Effects (product- and traveler-wise) ── */}
      {(() => {
        const cancelledTravelers = (b.travelers || []).filter((t) => t.status === "Cancelled");
        const travMods = (b.travelerModifications || []).filter((m) => m.actionType === "CANCEL_TRAVELER");

        // Source of truth: productDetails.cancellation stored by cancelProduct /
        // cancelFlightLegs. Whole-product cancels carry a top-level {cost,fee,vat};
        // partial flight cancels accumulate directionFees[]. The fee is VAT-INCLUSIVE
        // (VAT is inside it, not added on top).
        const readCanc = (raw) => {
          const c = raw?.productDetails?.cancellation || {};
          let cost = parseFloat(c.cost || 0), fee = parseFloat(c.fee || 0), vat = parseFloat(c.vat || 0);
          for (const df of c.directionFees || []) {
            cost += parseFloat(df.cost || 0); fee += parseFloat(df.fee || 0); vat += parseFloat(df.vat || 0);
          }
          return { cost, fee, vat };
        };

        const productRows = [];
        (components || []).forEach((c) => {
          const raw = c._raw;
          const cn = raw?.productDetails?.cancellation;
          const isCancelled = c.status === "Cancelled";
          const hasPartial = (cn?.directionFees || []).length > 0;
          if (!isCancelled && !hasPartial) return; // active, untouched product
          const { cost, fee, vat } = readCanc(raw);
          // Customer refund = original sell − retained charge (supplier cost + Sunsky
          // fee, which already includes its VAT). Supplier refund due = the original
          // supplier cost we no longer owe minus the supplier's cancellation charge.
          const original = isCancelled ? c.sellingPrice : null;
          const customerRefund = original != null ? Math.max(0, original - (cost + fee)) : null;
          const supplierRefundDue = isCancelled ? Math.max(0, (c.costPrice || 0) - cost) : null;
          productRows.push({
            key: `p-${raw?.id || c.id}`,
            label: c.type,
            statusLabel: isCancelled ? "Cancelled" : "Partially cancelled",
            original, cost, fee, vat, customerRefund, supplierRefundDue,
          });
        });

        if (productRows.length === 0 && cancelledTravelers.length === 0) return null;

        const totCost = productRows.reduce((s, r) => s + (r.cost || 0), 0);
        const totFee = productRows.reduce((s, r) => s + (r.fee || 0), 0);
        const totVat = productRows.reduce((s, r) => s + (r.vat || 0), 0);
        const totCustomerRefund = productRows.reduce((s, r) => s + (r.customerRefund || 0), 0);
        const totSupplierRefundDue = productRows.reduce((s, r) => s + (r.supplierRefundDue || 0), 0);

        return (
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}><Ban size={16} /> Cancellation Effects</div>
              <span className={styles.sectionSubtitle}>Retained fees folded into totals · settlement shown below</span>
            </div>

            {productRows.length > 0 && (
              <div className={styles.priceBreakdown}>
                <div className={`${styles.priceRow} ${styles.priceRowHead}`}>
                  <span style={{ flex: 1 }}>Product</span>
                  <span style={{ width: 110, textAlign: "right" }}>Original Sell</span>
                  <span style={{ width: 100, textAlign: "right" }}>Canc. Cost</span>
                  <span style={{ width: 100, textAlign: "right" }}>Sunsky Fee</span>
                  <span style={{ width: 80, textAlign: "right" }}>VAT</span>
                  <span style={{ width: 120, textAlign: "right" }}>Cust. Refund</span>
                </div>
                {productRows.map((r) => (
                  <div key={r.key} className={styles.priceRow}>
                    <span style={{ flex: 1, color: "#4b5563" }}>
                      {r.label}
                      <span className={`${styles.badge} ${styles.badgeCancelled}`} style={{ marginLeft: 8 }}><span className={styles.dot} />{r.statusLabel}</span>
                    </span>
                    <span style={{ width: 110, textAlign: "right", color: "#6b7280", textDecoration: r.original != null ? "line-through" : "none" }}>{r.original != null ? fmt(r.original) : "—"}</span>
                    <span style={{ width: 100, textAlign: "right", color: "#c42b2b", fontWeight: 600 }}>{r.cost ? fmt(r.cost) : "—"}</span>
                    <span style={{ width: 100, textAlign: "right", color: "#334155", fontWeight: 600 }}>{r.fee ? fmt(r.fee) : "—"}</span>
                    <span style={{ width: 80, textAlign: "right", color: "#b45309" }}>{r.vat ? fmt(r.vat) : "—"}</span>
                    <span style={{ width: 120, textAlign: "right", color: "#0d7a4f", fontWeight: 700 }}>{r.customerRefund != null ? fmt(r.customerRefund) : "—"}</span>
                  </div>
                ))}
                <div className={`${styles.priceRow} ${styles.priceRowTotal}`}>
                  <span style={{ flex: 1, fontWeight: 700 }}>Totals</span>
                  <span style={{ width: 110 }} />
                  <span style={{ width: 100, textAlign: "right", fontWeight: 700, color: "#c42b2b" }}>{totCost ? fmt(totCost) : "—"}</span>
                  <span style={{ width: 100, textAlign: "right", fontWeight: 700, color: "#334155" }}>{totFee ? fmt(totFee) : "—"}</span>
                  <span style={{ width: 80, textAlign: "right", fontWeight: 700, color: "#b45309" }}>{totVat ? fmt(totVat) : "—"}</span>
                  <span style={{ width: 120, textAlign: "right", fontWeight: 800, color: "#0d7a4f" }}>{fmt(totCustomerRefund)}</span>
                </div>
              </div>
            )}

            {/* Settlement statement — tracks who owes what after the cancellation */}
            {productRows.length > 0 && (
              <div className={styles.priceBreakdown} style={{ marginTop: 14, background: "#f8fafc", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ fontWeight: 700, color: "#334155", marginBottom: 6, fontSize: 13 }}>Settlement</div>
                <div className={styles.priceRow}>
                  <span style={{ flex: 1, color: "#4b5563" }}>Supplier refund due <span style={{ color: "#9ca3af" }}>(original cost − cancellation cost)</span></span>
                  <span style={{ width: 140, textAlign: "right", color: totSupplierRefundDue > 0 ? "#0d7a4f" : "#9ca3af", fontWeight: 700 }}>{fmt(totSupplierRefundDue)}</span>
                </div>
                <div className={styles.priceRow}>
                  <span style={{ flex: 1, color: "#4b5563" }}>Refund due to customer <span style={{ color: "#9ca3af" }}>(current outstanding balance)</span></span>
                  <span style={{ width: 140, textAlign: "right", color: outstandingAmount < 0 ? "#0d7a4f" : "#9ca3af", fontWeight: 700 }}>{outstandingAmount < 0 ? fmt(Math.abs(outstandingAmount)) : fmt(0)}</span>
                </div>
                {cancellationCases.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed #e2e8f0" }}>
                    <div style={{ fontWeight: 700, color: "#334155", marginBottom: 4, fontSize: 12 }}>Recorded cases</div>
                    {cancellationCases.map((cc) => (
                      <div key={cc.id} className={styles.priceRow} style={{ fontSize: 12 }}>
                        <span style={{ flex: 1, color: "#4b5563" }}>
                          <strong>{cc.caseNumber}</strong>
                          <span style={{ color: "#9ca3af", marginLeft: 8 }}>refund: {cc.refundMethod || "—"}</span>
                        </span>
                        <span style={{ width: 180, textAlign: "right", color: cc.documentNumber ? "#1d4ed8" : "#9ca3af", fontWeight: 600 }}>
                          {cc.documentNumber || "No document"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {cancelledTravelers.length > 0 && (
              <div className={styles.priceBreakdown} style={{ marginTop: productRows.length > 0 ? 14 : 0 }}>
                <div className={`${styles.priceRow} ${styles.priceRowHead}`}>
                  <span style={{ flex: 1 }}>Cancelled Traveler</span>
                  <span style={{ flex: 1 }}>Reason</span>
                  <span style={{ width: 170 }}>Products</span>
                  <span style={{ width: 120, textAlign: "right" }}>Refund</span>
                </div>
                {cancelledTravelers.map((t) => {
                  const tm = travMods.filter((m) => String(m.travelerId) === String(t.id)).slice(-1)[0];
                  const prods = (rawAssignments || []).filter((a) => String(a.travelerId) === String(t.id)).map((a) => (components || []).find((c) => c.id === a.productId)?.type).filter(Boolean);
                  const refund = tm && tm.refundAmount != null ? parseFloat(tm.refundAmount) : 0;
                  return (
                    <div key={`t-${t.id}`} className={styles.priceRow}>
                      <span style={{ flex: 1, fontWeight: 600, color: "#374151" }}>{t.name}</span>
                      <span style={{ flex: 1, color: "#6b7280" }}>{tm?.modificationTitle || "—"}</span>
                      <span style={{ width: 170, color: "#6b7280" }}>{prods.length ? prods.join(", ") : "—"}</span>
                      <span style={{ width: 120, textAlign: "right", color: refund > 0 ? "#0d7a4f" : "#9ca3af", fontWeight: 700 }}>{refund > 0 ? fmt(refund) : "—"}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Remove Post-Booking Modification (super admin only) ── */}
      {modToRemove && (
        <BaseModal
          open={!!modToRemove}
          onClose={() => setModToRemove(null)}
          title="Remove Post-Booking Modification"
          width="460px"
          footer={
            <>
              <button className={styles.btnSecondary} onClick={() => setModToRemove(null)}>Cancel</button>
              <LoadingButton className={styles.btnDanger} loading={deletingMod} loadingText="Removing…" icon={<Trash2 size={13} />} onClick={handleRemoveModification}>Remove Modification</LoadingButton>
            </>
          }
        >
          <div className={styles.deleteConfirmText}>
            Remove <strong>{modToRemove.typeLabel}</strong> — “{modToRemove.title || "—"}”?
            <div style={{ marginTop: 10, color: "#6b7280", fontSize: "13px" }}>
              Its net impact of <strong>{modToRemove.impact >= 0 ? "+" : "−"} {fmt(Math.abs(modToRemove.impact))}</strong> will be reversed from the booking totals
              {modToRemove.kind === "traveler" ? ", and any traveler name change or cancellation it applied will be undone" : ""}.
              This is a privileged super-admin action and is recorded in the audit log.
            </div>
          </div>
        </BaseModal>
      )}

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}><FileText size={16} /> VAT Summary</div>
            <div className={styles.sectionSubtitle}>VAT Regime: <strong>{pricing.vatRegime}</strong> · Hotel Place of Supply: <strong>{pricing.vatCountry}</strong></div>
          </div>
          <span className={styles.readOnlyBadge}>Read-only</span>
        </div>
        {vatRows.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>Component</th><th>VAT Regime</th><th>Rate / Rule</th><th>VAT Amount</th></tr></thead>
            <tbody>
              {vatRows.map((v, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{v.component}</td>
                  <td>{v.regime}</td>
                  <td>{v.rate}</td>
                  <td style={{ fontWeight: 700 }}>{v.amount}</td>
                </tr>
              ))}
              <tr>
                <td style={{ fontWeight: 800 }} colSpan={3}>Total VAT Liability</td>
                <td style={{ fontWeight: 800, color: "#b45309" }}>{fmt(displayVat)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div className={styles.vatInfo}>VAT summary will be populated once pricing is calculated for all components.</div>
        )}
        {pricing.vatCountry && pricing.vatCountry !== "—" && (
          <div className={styles.vatInfo}>Hotel VAT is derived from hotel country ({pricing.vatCountry}) only. VAT override is not allowed for TOMS bookings.</div>
        )}
      </div>

      {!isLocked && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0 4px" }}>
          <LoadingButton className={styles.btnPrimary} loading={saving} loadingText="Saving…" icon={<Save size={13} />} onClick={handleSavePricing}>Save Pricing</LoadingButton>
        </div>
      )}
    </div>
  );
};

const ConfirmationsTab = ({ confirmations }) => {
  const { showToast } = useToast();
  const copyRef = (value, label) => {
    if (!value || value === "—") return;
    navigator.clipboard.writeText(value)
      .then(() => showToast(`${label} reference copied`, "success"))
      .catch(() => showToast(`Failed to copy ${label} reference`, "error"));
  };
  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <div>
          <div className={styles.sectionTitle}><CheckCircle size={16} /> Confirmations</div>
          <div className={styles.sectionSubtitle}>Supplier confirmation references — read-only</div>
        </div>
        <span className={styles.readOnlyBadge}>Read-only</span>
      </div>
      <div className={styles.confirmGrid}>
        {confirmations.map((c) => {
          const CIcon = ICON_MAP[c.iconKey] || FileText;
          const hasRef = c.ref && c.ref !== "—";
          return (
            <div key={c.id} className={styles.confirmRow}>
              <div className={styles.confirmRowLeft}>
                <CIcon size={15} />{c.component}
                <span className={styles.componentSupplier}>via {c.supplier}</span>
                {hasRef && (
                  <button
                    type="button"
                    onClick={() => copyRef(c.ref, c.component)}
                    title="Copy reference"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, color: "#6b7280", borderRadius: 3 }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = "#2563eb"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = "#6b7280"; }}
                  >
                    <span className={styles.confirmRef}>{c.ref}</span>
                    <Copy size={12} />
                  </button>
                )}
              </div>
              <div className={styles.confirmRowRight}>
                <span>{c.confirmedAt}</span>
                <span className={styles.confirmBy}>{c.confirmedBy}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* PaymentsTab is now imported from ./PaymentsTab.jsx */

/* DocumentsTab is now imported from ./DocumentsTab.jsx */

const AUDIT_ACTION_ICONS = {
  "Booking Created": FilePlus, "Product Added": Plus, "Product Updated": Pencil,
  "Product Deleted": Trash2, "Product Cancelled": Ban, "Product Copied": FilePlus,
  "Pricing Set": Euro, "Product Modification": Pencil, "Schedule Change": AlertTriangle,
  "Flight Confirmed": Plane, "Hotel Confirmed": Hotel, "Transfer Confirmed": Bus,
  "Insurance Confirmed": Shield, "Traveler Added": User, "Traveler Removed": UserX,
  "Traveler Edited": User, "Assignment Created": Link2, "Assignment Deleted": Link2,
  "Payment Received": CreditCard, "Payment Added": CreditCard, "Documents Generated": FileText,
  "Booking Confirmed": CheckCircle, "Booking Cancelled": Ban, "Booking Submitted": CheckCircle,
  "Booking Locked": Lock, "Booking Updated": Pencil, "Pricing Updated": Euro,
  "Customer Updated": UserCircle,
};

const getAuditIcon = (action) => {
  if (AUDIT_ACTION_ICONS[action]) return AUDIT_ACTION_ICONS[action];
  const lower = (action || "").toLowerCase();
  if (lower.includes("creat") || lower.includes("added")) return Plus;
  if (lower.includes("delet") || lower.includes("remov") || lower.includes("cancel")) return Trash2;
  if (lower.includes("updat") || lower.includes("edit") || lower.includes("modif")) return Pencil;
  if (lower.includes("confirm")) return CheckCircle;
  if (lower.includes("payment") || lower.includes("paid")) return CreditCard;
  if (lower.includes("assign")) return Link2;
  if (lower.includes("pricing") || lower.includes("price")) return Euro;
  return ClipboardList;
};

const getAuditCategory = (action) => {
  const lower = (action || "").toLowerCase();
  if (lower.includes("delet") || lower.includes("remov") || lower.includes("cancel")) return "destructive";
  if (lower.includes("creat") || lower.includes("added") || lower.includes("confirm")) return "success";
  if (lower.includes("updat") || lower.includes("edit") || lower.includes("modif") || lower.includes("schedule") || lower.includes("change")) return "warning";
  if (lower.includes("payment") || lower.includes("pricing") || lower.includes("price")) return "finance";
  return "neutral";
};

const formatAuditTimestamp = (ts) => {
  if (!ts) return { date: "—", time: "", full: "—" };
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return { date: ts, time: "", full: ts };
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const full = `${date} at ${time}`;
    return { date, time, full };
  } catch { return { date: ts, time: "", full: ts }; }
};

const FIELD_LABELS = {
  // Product / booking
  productStatus: "Product Status", serviceStatus: "Service Status",
  supplierName: "Supplier", supplier: "Supplier", supplierReference: "Supplier Ref",
  supplierRef: "Supplier Ref", sellingPrice: "Selling Price", costPrice: "Cost Price",
  internalNotes: "Notes", productType: "Product Type", travelerPricing: "Traveler Pricing",
  roomPricing: "Room Pricing", markup: "Markup", netPrice: "Net Price", vatTax: "VAT",
  copiedFromProductId: "Copied From Product", departureTime: "Departure", arrivalTime: "Arrival",
  departureDate: "Departure Date", checkIn: "Check-in", checkOut: "Check-out",
  route: "Route", airline: "Airline", hotel: "Hotel", rooms: "Rooms", type: "Type",
  // Customer – Private
  firstName: "First Name", lastName: "Last Name", dateOfBirth: "Date of Birth",
  gender: "Gender", nationality: "Nationality", language: "Preferred Language",
  email: "Email", phone: "Phone",
  // Customer – Company
  companyName: "Company Name", legalName: "Legal Name", vatNumber: "VAT Number",
  industry: "Industry", website: "Website",
  primaryFirstName: "Primary Contact – First Name", primaryLastName: "Primary Contact – Last Name",
  primaryEmail: "Primary Contact – Email", primaryPhone: "Primary Contact – Phone",
  primaryRole: "Primary Contact – Role",
  invoiceEmail: "Invoice Email", paymentTerms: "Payment Terms",
  // Address (flat-prefixed keys)
  address_street: "Street", address_houseNumber: "House Number", address_boxNumber: "Box Number",
  address_city: "City", address_zip: "Postal Code", address_country: "Country",
  // Booking-level snapshot
  customerName: "Customer Name", customerEmail: "Customer Email", customerPhone: "Customer Phone",
};

const humanLabel = (key) => {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  // "traveler_7"  → "Traveler #7"
  const travelerMatch = key.match(/^traveler_(\d+)$/);
  if (travelerMatch) return `Traveler #${travelerMatch[1]}`;
  // "assignment_3" → "Assignment #3"
  const assignmentMatch = key.match(/^assignment_(\d+)$/);
  if (assignmentMatch) return `Assignment #${assignmentMatch[1]}`;
  // camelCase → "Camel Case"
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
};

const formatFieldVal = (val) => {
  if (val === null || val === undefined) return "—";
  if (Array.isArray(val)) return `${val.length} item${val.length !== 1 ? "s" : ""}`;
  if (typeof val === "object") return Object.entries(val).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => `${humanLabel(k)}: ${v}`).join(", ") || "—";
  return String(val);
};

const PRODUCT_FILTERS = [
  { key: "Flight", Icon: Plane },
  { key: "Hotel", Icon: Hotel },
  { key: "Transfer", Icon: Bus },
];

/* ══════════════════════════════════════════════
   PRODUCT-CONTEXTUAL AUDIT DETAIL
   Renders a "before → after" view tailored to each product type so an operator
   can see exactly what a change did. Flights are grouped leg-by-leg (one-way /
   round-trip / multi-city); hotels split into Stay and Room & board; transfers
   into Service / Arrival / Departure; everything else falls back to a clean
   field comparison. Pure presentation — reuses the existing diff styling tokens.
   ══════════════════════════════════════════════ */

const auditDash = (v) => (v === null || v === undefined || v === "") ? "—" : String(v);

const fmtAuditDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtAuditMoney = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return "EUR 0,00";
  return `EUR ${num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const isNonEmptyObj = (o) => o && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;

// Build before→after rows for a list of field defs; only changed fields are kept.
// Each def: { key, label, get?(obj), fmt?(val) }.
const buildCtxRows = (oldObj, newObj, fields) => {
  const rows = [];
  fields.forEach((f) => {
    const rawOld = f.get ? f.get(oldObj || {}) : oldObj?.[f.key];
    const rawNew = f.get ? f.get(newObj || {}) : newObj?.[f.key];
    const o = f.fmt ? f.fmt(rawOld) : rawOld;
    const n = f.fmt ? f.fmt(rawNew) : rawNew;
    if (JSON.stringify(o ?? null) !== JSON.stringify(n ?? null)) {
      rows.push({ key: f.key, label: f.label, old: auditDash(o), new: auditDash(n) });
    }
  });
  return rows;
};

const CtxRows = ({ rows }) => (
  <table className={styles.alCtxTable}>
    <tbody>
      {rows.map((r) => (
        <tr key={r.key}>
          <td className={styles.alDiffField}>{r.label}</td>
          <td className={styles.alDiffOld}>{r.old}</td>
          <td className={styles.alDiffArrow}><ArrowRight size={13} /></td>
          <td className={styles.alDiffNew}>{r.new}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CtxGroup = ({ icon: Icon, title, changedCount, emptyText, children }) => (
  <div className={styles.alCtxGroup}>
    <div className={styles.alCtxGroupHead}>
      <span className={styles.alCtxGroupTitle}>{Icon && <Icon size={13} />}{title}</span>
      {typeof changedCount === "number" && (
        changedCount > 0
          ? <span className={styles.alCtxBadgeChanged}>{changedCount} {changedCount === 1 ? "change" : "changes"}</span>
          : <span className={styles.alCtxBadgeSame}>No changes</span>
      )}
    </div>
    {changedCount === 0
      ? <div className={styles.alCtxNoChange}>{emptyText || "No changes in this section."}</div>
      : children}
  </div>
);

/* ── Flight ── */
const normLegs = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (Array.isArray(v.legs)) return v.legs;
  return [];
};
const legRoute = (l) => `${l?.departureAirport || l?.departure || "—"} → ${l?.arrivalAirport || l?.arrival || "—"}`;
const legLabel = (l, i, flightType, total) => {
  if (l?.direction) return l.direction;
  if (flightType === "RT") return i === 0 ? "Outbound" : "Inbound";
  if (flightType === "OW" || total === 1) return "Outbound";
  return `Segment ${i + 1}`;
};
const legFlightNo = (l) => {
  const num = l?.flightNumber || l?.flightCode;
  if (!num) return "—";
  return l?.airlineCode ? `${l.airlineCode} ${num}` : String(num);
};
// Getters use `departureDate || date` / `flightNumber || flightCode` aliases on
// purpose: post-booking-modification snapshots are normalised (departureDate /
// flightNumber), but older "Schedule Change" entries store raw leg arrays that may
// use the `date` / `flightCode` aliases. The same getter is applied to both old and
// new, so equal values never show up as a false change.
const LEG_FIELDS = [
  { key: "route", label: "Route", get: legRoute },
  { key: "departureDate", label: "Departure date", get: (l) => fmtAuditDate(l?.departureDate || l?.date) },
  { key: "departureTime", label: "Departure time", get: (l) => auditDash(l?.departureTime) },
  { key: "arrivalTime", label: "Arrival time", get: (l) => auditDash(l?.arrivalTime) },
  { key: "flight", label: "Flight number", get: legFlightNo },
  { key: "cabinClass", label: "Cabin", get: (l) => auditDash(l?.cabinClass) },
  { key: "flightStatus", label: "Status", get: (l) => auditDash(l?.flightStatus) },
];

const FlightScheduleCard = ({ legs, flightType, label, variant }) => {
  if (!legs.length) return null;
  const total = legs.length;
  return (
    <div className={`${styles.alSchedCard} ${styles[`alSchedCard_${variant}`]}`}>
      <div className={styles.alSchedCardHeader}>
        <span className={styles.alSchedCardLabel}>{label}</span>
        <span className={styles.alSchedCardType}>{flightType === "RT" ? "Round Trip" : flightType === "OW" ? "One Way" : "Multi-City"}</span>
      </div>
      {legs.map((leg, i) => (
        <div key={i} className={styles.alSchedLeg}>
          <div className={styles.alSchedLegHead}>
            <Plane size={12} />
            <span className={styles.alSchedLegTitle}>{legLabel(leg, i, flightType, total)}</span>
            <span className={styles.alSchedLegRoute}>{legRoute(leg)}</span>
          </div>
          <div className={styles.alSchedLegFields}>
            {LEG_FIELDS.map((f) => {
              const val = f.get ? f.get(leg) : leg?.[f.key];
              if (!val || val === "—") return null;
              return (
                <div key={f.key} className={styles.alSchedField}>
                  <span className={styles.alSchedFieldLabel}>{f.label}</span>
                  <span className={styles.alSchedFieldValue}>{val}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const FlightChangeView = ({ oldValue, newValue }) => {
  const oldLegs = normLegs(oldValue);
  const newLegs = normLegs(newValue);
  const total = Math.max(oldLegs.length, newLegs.length);
  const inferType = () => {
    if (total === 1) return "OW";
    const refLegs = newLegs.length ? newLegs : oldLegs;
    const first = refLegs[0];
    const last = refLegs[refLegs.length - 1];
    const firstDep = first?.departureAirport || first?.departure;
    const lastArr = last?.arrivalAirport || last?.arrival;
    if (total === 2 && firstDep && lastArr && firstDep === lastArr) return "RT";
    return "MULTI";
  };
  const flightType = oldValue?.flightType || newValue?.flightType || inferType();

  const groups = [];
  for (let i = 0; i < total; i++) {
    const ol = oldLegs[i];
    const nl = newLegs[i];
    if (ol && nl) {
      const rows = buildCtxRows(ol, nl, LEG_FIELDS);
      groups.push(
        <CtxGroup key={i} icon={Plane} title={`${legLabel(nl, i, flightType, total)} · ${legRoute(nl)}`} changedCount={rows.length} emptyText="This flight segment is unchanged.">
          <CtxRows rows={rows} />
        </CtxGroup>
      );
    } else if (nl) {
      const rows = LEG_FIELDS.map((f) => ({ key: f.key, label: f.label, old: "—", new: auditDash(f.get(nl)) })).filter((r) => r.new !== "—");
      groups.push(<CtxGroup key={i} icon={Plane} title={`Added segment · ${legRoute(nl)}`} changedCount={rows.length}><CtxRows rows={rows} /></CtxGroup>);
    } else if (ol) {
      const rows = LEG_FIELDS.map((f) => ({ key: f.key, label: f.label, old: auditDash(f.get(ol)), new: "—" })).filter((r) => r.old !== "—");
      groups.push(<CtxGroup key={i} icon={Plane} title={`Removed segment · ${legRoute(ol)}`} changedCount={rows.length}><CtxRows rows={rows} /></CtxGroup>);
    }
  }
  return (
    <div className={styles.alCtx}>
      {(oldLegs.length > 0 || newLegs.length > 0) && (
        <div className={styles.alSchedCompare}>
          <FlightScheduleCard legs={oldLegs} flightType={flightType} label="Old Flight Schedule" variant="old" />
          <div className={styles.alSchedArrow}><ArrowRight size={18} /></div>
          <FlightScheduleCard legs={newLegs} flightType={flightType} label="New Flight Schedule" variant="new" />
        </div>
      )}
      {groups}
    </div>
  );
};

/* ── Hotel ── */
const HOTEL_STAY_FIELDS = [
  { key: "checkIn", label: "Check-in", fmt: fmtAuditDate },
  { key: "checkOut", label: "Check-out", fmt: fmtAuditDate },
  { key: "nights", label: "Nights" },
];
const HOTEL_ROOM_FIELDS = [
  { key: "roomName", label: "Room" },
  { key: "roomType", label: "Room type" },
  { key: "roomCharacteristic", label: "View / feature" },
  { key: "board", label: "Board", get: (d) => d?.boardName || d?.boardCode },
  { key: "numberOfRooms", label: "Number of rooms" },
  { key: "roomStatus", label: "Room status" },
  { key: "roomRate", label: "Rate" },
];
const HotelChangeView = ({ oldValue, newValue }) => {
  const stay = buildCtxRows(oldValue, newValue, HOTEL_STAY_FIELDS);
  const room = buildCtxRows(oldValue, newValue, HOTEL_ROOM_FIELDS);
  return (
    <div className={styles.alCtx}>
      <CtxGroup icon={Calendar} title="Stay" changedCount={stay.length} emptyText="No date changes."><CtxRows rows={stay} /></CtxGroup>
      <CtxGroup icon={Tag} title="Room & board" changedCount={room.length} emptyText="No room or board changes."><CtxRows rows={room} /></CtxGroup>
    </div>
  );
};

/* ── Transfer ── */
const TRANSFER_SVC_FIELDS = [
  { key: "transferMoment", label: "Transfer moment" },
  { key: "transferType", label: "Transfer type" },
  { key: "vehicleType", label: "Vehicle" },
  { key: "travelersCount", label: "Travelers" },
];
const transferLegFields = (prefix) => [
  { key: `${prefix}_pickup`, label: "Pickup", get: (d) => d?.[prefix]?.pickupLocation },
  { key: `${prefix}_dropoff`, label: "Drop-off", get: (d) => d?.[prefix]?.dropoffLocation },
  { key: `${prefix}_date`, label: "Pickup date", get: (d) => fmtAuditDate(d?.[prefix]?.pickupDate) },
  { key: `${prefix}_time`, label: "Pickup time", get: (d) => auditDash(d?.[prefix]?.pickupTime) },
];
const TransferChangeView = ({ oldValue, newValue }) => {
  const svc = buildCtxRows(oldValue, newValue, TRANSFER_SVC_FIELDS);
  const groups = [
    <CtxGroup key="svc" icon={Bus} title="Service" changedCount={svc.length} emptyText="No service changes."><CtxRows rows={svc} /></CtxGroup>,
  ];
  [["arrival", "Arrival"], ["departure", "Departure"], ["single", "Transfer"]].forEach(([p, lbl]) => {
    if (oldValue?.[p] || newValue?.[p]) {
      const rows = buildCtxRows(oldValue, newValue, transferLegFields(p));
      groups.push(
        <CtxGroup key={p} icon={Globe} title={lbl} changedCount={rows.length} emptyText={`No ${lbl.toLowerCase()} changes.`}><CtxRows rows={rows} /></CtxGroup>
      );
    }
  });
  return <div className={styles.alCtx}>{groups}</div>;
};

/* ── Generic (Insurance / Car Rental / Visa / Package) ── */
const GenericContextView = ({ oldValue, newValue }) => {
  const keys = new Set([...Object.keys(oldValue || {}), ...Object.keys(newValue || {})]);
  const rows = [];
  keys.forEach((k) => {
    const o = oldValue?.[k];
    const n = newValue?.[k];
    if (JSON.stringify(o ?? null) !== JSON.stringify(n ?? null)) {
      rows.push({ key: k, label: humanLabel(k), old: auditDash(formatFieldVal(o)), new: auditDash(formatFieldVal(n)) });
    }
  });
  return (
    <div className={styles.alCtx}>
      <CtxGroup icon={FileText} title="Details" changedCount={rows.length} emptyText="No field changes."><CtxRows rows={rows} /></CtxGroup>
    </div>
  );
};

const HOTEL_CTX_KEYS = ["checkIn", "checkOut", "nights", "roomName", "roomType", "roomCharacteristic", "boardCode", "boardName", "numberOfRooms", "roomStatus", "roomRate"];
const TRANSFER_CTX_KEYS = ["transferMoment", "transferType", "vehicleType", "travelersCount", "arrival", "departure", "single"];
const objHasAny = (o, keys) => o && typeof o === "object" && keys.some((k) => o[k] !== undefined && o[k] !== null);

// Decide which contextual view (if any) can render this entry's before/after.
const getAuditContextKind = (entry) => {
  const { productType: pt, oldValue: ov, newValue: nv } = entry;
  if (!ov && !nv) return null;
  if (pt === "Flight" && (normLegs(ov).length || normLegs(nv).length)) return "flight";
  if ((pt === "Hotel" || pt === "Accommodation") && (objHasAny(ov, HOTEL_CTX_KEYS) || objHasAny(nv, HOTEL_CTX_KEYS))) return "hotel";
  if (pt === "Transfer" && (objHasAny(ov, TRANSFER_CTX_KEYS) || objHasAny(nv, TRANSFER_CTX_KEYS))) return "transfer";
  if (["Insurance", "CarRental", "Visa", "Package"].includes(pt) && (isNonEmptyObj(ov) || isNonEmptyObj(nv))) return "generic";
  return null;
};

const AuditContextView = ({ kind, entry }) => {
  if (kind === "flight") return <FlightChangeView oldValue={entry.oldValue} newValue={entry.newValue} />;
  if (kind === "hotel") return <HotelChangeView oldValue={entry.oldValue} newValue={entry.newValue} />;
  if (kind === "transfer") return <TransferChangeView oldValue={entry.oldValue} newValue={entry.newValue} />;
  if (kind === "generic") return <GenericContextView oldValue={entry.oldValue} newValue={entry.newValue} />;
  return null;
};

// Financial breakdown + operational flags for modification entries (entry.meta).
const AuditMetaPanel = ({ meta, reason }) => {
  if (!meta) return null;
  const fin = meta.financial || {};
  const dirLabel = fin.direction === "INCREASE" ? "Price increase"
    : fin.direction === "DECREASE" ? "Price decrease" : "No financial change";
  const docLabel = fin.document === "DEBIT_NOTE" ? "Debit note"
    : fin.document === "CREDIT_NOTE" ? "Credit note" : null;
  const items = [
    { label: "Supplier cost", value: fmtAuditMoney(fin.supplierCost) },
    { label: "Agency fee (incl. VAT)", value: fmtAuditMoney(fin.agencyFee) },
    { label: "Refund", value: fmtAuditMoney(fin.refundAmount) },
    { label: "VAT (embedded)", value: fmtAuditMoney(fin.vatAmount) },
    { label: "Net change", value: fmtAuditMoney(fin.total) },
    { label: "Direction", value: dirLabel },
  ];
  if (docLabel) items.push({ label: "Document required", value: docLabel });
  const showNotes = reason && reason !== meta.modificationTitle;
  return (
    <div className={styles.alDetailSection}>
      <div className={styles.alDetailSectionTitle}>Financial Impact</div>
      <div className={styles.alDetailGrid}>
        {items.map((it) => (
          <div key={it.label} className={styles.alDetailGridItem}>
            <span className={styles.alDetailGridLabel}>{it.label}</span>
            <span className={styles.alDetailGridValue}>{it.value}</span>
          </div>
        ))}
        {showNotes && (
          <div className={`${styles.alDetailGridItem} ${styles.alDetailGridFull}`}>
            <span className={styles.alDetailGridLabel}>Internal Notes</span>
            <span className={styles.alDetailGridValue}>{reason}</span>
          </div>
        )}
      </div>
      {meta.flags && (
        <div className={styles.alCtxFlags}>
          <span className={`${styles.alCtxFlag} ${meta.flags.regenerateDocuments ? styles.alCtxFlagOn : styles.alCtxFlagOff}`}>
            {meta.flags.regenerateDocuments ? <Check size={11} /> : <X size={11} />} Documents regenerated
          </span>
          <span className={`${styles.alCtxFlag} ${meta.flags.notifyCustomer ? styles.alCtxFlagOn : styles.alCtxFlagOff}`}>
            {meta.flags.notifyCustomer ? <Check size={11} /> : <X size={11} />} Customer notified
          </span>
        </div>
      )}
    </div>
  );
};

const AuditEntry = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const ActionIcon = getAuditIcon(entry.action);
  const category = getAuditCategory(entry.action);
  const { time, full } = formatAuditTimestamp(entry.timestamp);
  const PtIcon = entry.productType ? PRODUCT_ICON_MAP[entry.productType] : null;

  // Diff rows — only shown when both old + new exist
  const diffRows = useMemo(() => {
    if (!entry.oldValue || !entry.newValue) return [];
    if (typeof entry.oldValue !== "object" || typeof entry.newValue !== "object")
      return [{ key: "value", old: entry.oldValue, new: entry.newValue }];
    const allKeys = new Set([...Object.keys(entry.oldValue), ...Object.keys(entry.newValue)]);
    const rows = [];
    allKeys.forEach((k) => {
      const o = entry.oldValue[k]; const n = entry.newValue[k];
      if (JSON.stringify(o) !== JSON.stringify(n)) rows.push({ key: k, old: o, new: n });
    });
    return rows;
  }, [entry.oldValue, entry.newValue]);

  // "Details" rows — only when newValue exists without oldValue (e.g. "added" actions)
  const detailRows = useMemo(() => {
    if (!entry.newValue || entry.oldValue) return [];
    if (typeof entry.newValue !== "object" || Array.isArray(entry.newValue)) return [];
    return Object.entries(entry.newValue).filter(([, v]) => v !== null && v !== undefined);
  }, [entry.newValue, entry.oldValue]);

  // Product-contextual before→after view (flight legs / hotel stay / transfer / generic).
  // When present it replaces the raw diff/detail tables for a far clearer story.
  const contextKind = useMemo(() => getAuditContextKind(entry), [entry]);

  // Expandable when there is ANY content to show (including the note itself for old entries)
  const hasExpandable = !!entry.note || diffRows.length > 0 || detailRows.length > 0 || !!entry.financialImpact || !!entry.reason || !!contextKind || !!entry.meta;

  return (
    <div className={`${styles.alCard} ${expanded ? styles.alCardExpanded : ""} ${styles[`alCard_${category}`]}`}>

      {/* ── Overview Row ── */}
      <div
        className={styles.alOverview}
        onClick={() => hasExpandable && setExpanded(!expanded)}
        role={hasExpandable ? "button" : undefined}
        style={{ cursor: hasExpandable ? "pointer" : "default" }}
      >
        <div className={`${styles.alIconWrap} ${styles[`alIcon_${category}`]}`}>
          <ActionIcon size={14} />
        </div>

        <div className={styles.alMain}>
          <div className={styles.alActionRow}>
            <span className={styles.alAction}>{entry.action}</span>
            {entry.productType && (
              <span className={styles.alProductBadge}>
                {PtIcon && <PtIcon size={10} />}
                {entry.productType}
              </span>
            )}
            {entry.financialImpact && (
              <span className={styles.alFinBadge}><Euro size={9} /> Financial</span>
            )}
            {!contextKind && diffRows.length > 0 && (
              <span className={styles.alChangeBadge}>
                {diffRows.length} {diffRows.length === 1 ? "change" : "changes"}
              </span>
            )}
            {contextKind && (
              <span className={styles.alChangeBadge}>Before → After</span>
            )}
          </div>
          {/* Human-readable summary — the most important line */}
          {entry.note && (
            <div className={styles.alDescRow}>
              <span className={styles.alDesc}>{entry.note}</span>
            </div>
          )}
        </div>

        <div className={styles.alMeta}>
          <div className={styles.alMetaUser}>
            <span className={`${styles.alUserIcon} ${entry.isSystem ? styles.alUserSys : ""}`}>
              {entry.isSystem ? "S" : (entry.user || "U").charAt(0).toUpperCase()}
            </span>
            <span className={styles.alUserName}>{entry.isSystem ? "System" : entry.user}</span>
          </div>
          <span className={styles.alTime}>{time}</span>
        </div>

        {hasExpandable && (
          <span className={styles.alChevron}>
            {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </span>
        )}
      </div>

      {/* ── Expanded Detail Panel ── */}
      {expanded && (
        <div className={styles.alDetail}>

          {/* ── Product-contextual "before → after" (flights / hotels / transfers / generic) ── */}
          {contextKind && (
            <div className={styles.alDetailSection}>
              <div className={styles.alDetailSectionTitle}>What Changed</div>
              <AuditContextView kind={contextKind} entry={entry} />
            </div>
          )}

          {/* ── Fallback: show the note for old entries that have no structured data ── */}
          {!contextKind && entry.note && detailRows.length === 0 && diffRows.length === 0 && !entry.financialImpact && !entry.reason && !entry.meta && (
            <div className={styles.alDetailSection}>
              <div className={styles.alDetailSectionTitle}>Description</div>
              <div className={styles.alDetailDesc}>{entry.note}</div>
            </div>
          )}

          {/* ── Details: "added" actions (newValue only, no old) ── */}
          {!contextKind && detailRows.length > 0 && (
            <div className={styles.alDetailSection}>
              <div className={styles.alDetailSectionTitle}>Details</div>
              <div className={styles.alDiffTableWrap}>
                <table className={styles.alDiffTable}>
                  <tbody>
                    {detailRows.map(([k, v]) => (
                      <tr key={k}>
                        <td className={styles.alDiffField} style={{ width: "38%" }}>
                          {humanLabel(k)}
                        </td>
                        <td className={styles.alDiffNew}>{formatFieldVal(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Changes: diff table (old → new) ── */}
          {!contextKind && diffRows.length > 0 && (
            <div className={styles.alDetailSection}>
              <div className={styles.alDetailSectionTitle}>
                Changes ({diffRows.length})
              </div>
              <div className={styles.alDiffTableWrap}>
                <table className={styles.alDiffTable}>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Before</th>
                      <th></th>
                      <th>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffRows.map(({ key, old: o, new: n }) => (
                      <tr key={key}>
                        <td className={styles.alDiffField}>{humanLabel(key)}</td>
                        <td className={styles.alDiffOld}>{formatFieldVal(o)}</td>
                        <td className={styles.alDiffArrow}>→</td>
                        <td className={styles.alDiffNew}>{formatFieldVal(n)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Financial impact + operational flags (modification entries) ── */}
          {entry.meta && <AuditMetaPanel meta={entry.meta} reason={entry.reason} />}

          {/* ── Context: reason / financial impact (legacy entries without meta) ── */}
          {!entry.meta && (entry.financialImpact || entry.reason) && (
            <div className={styles.alDetailSection}>
              <div className={styles.alDetailSectionTitle}>Context</div>
              <div className={styles.alDetailGrid}>
                {entry.financialImpact && (
                  <div className={styles.alDetailGridItem}>
                    <span className={styles.alDetailGridLabel}>Financial Impact</span>
                    <span className={styles.alDetailGridValue}>{entry.financialImpact}</span>
                  </div>
                )}
                {entry.reason && (
                  <div className={styles.alDetailGridItem}>
                    <span className={styles.alDetailGridLabel}>Reason</span>
                    <span className={styles.alDetailGridValue}>{entry.reason}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Compact metadata footer ── */}
          <div className={styles.alMetaFooter}>
            <span>Entry #{entry.id}</span>
            <span className={styles.alMetaFooterDot}>·</span>
            <span>{full}</span>
            <span className={styles.alMetaFooterDot}>·</span>
            <span>{entry.isSystem ? "System / API" : entry.user}</span>
            {entry.productType && (
              <>
                <span className={styles.alMetaFooterDot}>·</span>
                <span>{entry.productType}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AuditTab = ({ log }) => {
  const [sourceFilter, setSourceFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");

  const filtered = useMemo(() => log.filter((e) => {
    const srcOk = sourceFilter === "all" || (sourceFilter === "system" ? e.isSystem : !e.isSystem);
    const ptOk = productFilter === "all" || e.productType === productFilter;
    return srcOk && ptOk;
  }), [log, sourceFilter, productFilter]);

  const grouped = useMemo(() => {
    const groups = [];
    let lastDate = null;
    filtered.forEach((entry) => {
      const { date } = formatAuditTimestamp(entry.timestamp);
      if (date !== lastDate) { groups.push({ date, entries: [entry] }); lastDate = date; }
      else groups[groups.length - 1].entries.push(entry);
    });
    return groups;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { Flight: 0, Hotel: 0, Transfer: 0 };
    log.forEach((e) => { if (e.productType && c[e.productType] !== undefined) c[e.productType]++; });
    return c;
  }, [log]);

  const stats = useMemo(() => {
    const s = { total: log.length, user: 0, system: 0 };
    log.forEach((e) => { if (e.isSystem) s.system++; else s.user++; });
    return s;
  }, [log]);

  return (
    <div className={styles.sectionCard}>
      {/* ── Header ── */}
      <div className={styles.alHeader}>
        <div className={styles.alHeaderLeft}>
          <div className={styles.alHeaderIcon}><ClipboardList size={18} /></div>
          <div>
            <div className={styles.alHeaderTitle}>Activity Log</div>
            <div className={styles.alHeaderSub}>Complete audit trail for this booking</div>
          </div>
        </div>
        <div className={styles.alFiltersArea}>
          <div className={styles.alFilterGroup}>
            <span className={styles.alFilterLabel}>Source</span>
            <div className={styles.alFilters}>
              {[{ key: "all", label: "All" }, { key: "user", label: "User" }, { key: "system", label: "System" }].map((f) => (
                <button key={f.key} className={`${styles.alFilterBtn} ${sourceFilter === f.key ? styles.alFilterActive : ""}`} onClick={() => setSourceFilter(f.key)}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.alFilterGroup}>
            <span className={styles.alFilterLabel}>Product</span>
            <div className={styles.alFilters}>
              <button className={`${styles.alFilterBtn} ${productFilter === "all" ? styles.alFilterActive : ""}`} onClick={() => setProductFilter("all")}>All</button>
              {PRODUCT_FILTERS.map(({ key, Icon }) => {
                const isActive = productFilter === key;
                const cnt = counts[key];
                return (
                  <button key={key} className={`${styles.alFilterBtn} ${isActive ? styles.alFilterActive : ""}`} style={cnt === 0 ? { opacity: 0.4 } : {}} onClick={() => setProductFilter(key)}>
                    <Icon size={11} /> {key}
                    {cnt > 0 && <span className={styles.alFilterCount}>{cnt}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className={styles.alStatsBar}>
        <div className={styles.alStat}>
          <span className={styles.alStatNum}>{stats.total}</span>
          <span className={styles.alStatLabel}>Total</span>
        </div>
        <div className={styles.alStatDivider} />
        <div className={styles.alStat}>
          <span className={styles.alStatNum}>{stats.user}</span>
          <span className={styles.alStatLabel}>User Actions</span>
        </div>
        <div className={styles.alStatDivider} />
        <div className={styles.alStat}>
          <span className={styles.alStatNum}>{stats.system}</span>
          <span className={styles.alStatLabel}>System Events</span>
        </div>
        <div className={styles.alStatDivider} />
        <div className={styles.alStat}>
          <span className={styles.alStatNum}>{grouped.length}</span>
          <span className={styles.alStatLabel}>Days</span>
        </div>
        <div className={styles.alStatDivider} />
        <div className={styles.alStat}>
          <span className={styles.alStatNum}>{filtered.length}</span>
          <span className={styles.alStatLabel}>Showing</span>
        </div>
      </div>

      {/* ── Timeline ── */}
      {filtered.length === 0 ? (
        <div className={styles.auditEmptyState}>
          <ClipboardList size={28} />
          <div>No activity logs found{productFilter !== "all" ? ` for ${productFilter}` : ""}.</div>
        </div>
      ) : (
        <div className={styles.alTimeline}>
          {grouped.map((group, gi) => (
            <div key={gi} className={styles.alGroup}>
              <div className={styles.alDateRow}>
                <div className={styles.alDateDot} />
                <div className={styles.alDateLabel}>{group.date}</div>
                <div className={styles.alDateLine} />
                <div className={styles.alDateCount}>{group.entries.length} {group.entries.length === 1 ? "event" : "events"}</div>
              </div>
              <div className={styles.alGroupEntries}>
                {group.entries.map((entry) => (
                  <AuditEntry key={entry.id} entry={entry} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookingDetail;